// ═══════════════════════════════════════════════════════════════════════════════
// COCKPIT ASSISTANT UNIFIÉ - Interface style Claude avec menu Cockpit complet
// Layout: Menu Cockpit (gauche) | Chat IA (centre) | Artifacts (droite)
// Fonctionne pour PRO et PARTICULIER avec le même composant
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  // Navigation & UI
  ArrowLeft, ArrowUp, Plus, MessageSquare, FolderOpen, Clock, Trash2,
  Send, Mic, MicOff, Volume2, VolumeX, Loader2, Settings,
  PanelRightOpen, PanelRightClose, PanelLeftOpen, PanelLeftClose, MoreHorizontal, Edit3,
  ChevronDown, ChevronRight, ChevronLeft, Sparkles, X, Search, Home, Pencil, Check,
  Copy, Download, Mail, MessageCircle, Menu, LogOut, Video, Save, Lock, AlertTriangle,
  Paperclip, ImageIcon, FileUp, Film, File, RefreshCw, LayoutGrid,
  // Pro icons
  Users, Target, Layers, HardHat, Megaphone, Globe, Network, Briefcase, Store,
  FileText, Receipt, Bell, UserPlus, TrendingUp, Calendar, Newspaper, Folder,
  Package, Wrench, Euro, BarChart2, Percent, Zap, Image, BookOpen, Handshake,
  Eye, Building, Camera, Shield, Clock as ClockIcon, CreditCard, User, ShoppingCart, Layout,
  // Particulier icons
  Heart, ShoppingBag, Star, Bookmark, PenTool, Hash, Lightbulb,
  // Common
  BadgeCheck, Building2, Compass, Minimize2, Maximize2,
  // Paramètres - Abonnement & Facturation
  Crown, Coins, Cloud, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import ArtifactDevisInteractif from '../../components/artifacts/ArtifactDevisInteractif';
import ArtifactTasksInteractif from '../../components/artifacts/ArtifactTasksInteractif';
import ArtifactProjetInteractif, { DEMO_PROJECT } from '../../components/artifacts/ArtifactProjetInteractif';
import ArtifactSearchArtisans from '../../components/artifacts/ArtifactSearchArtisans';
import ArtifactSearchProduits from '../../components/artifacts/ArtifactSearchProduits';
import ArtifactSearchPosts from '../../components/artifacts/ArtifactSearchPosts';
// Import des composants de données pour le panneau droit
import {
  ArtifactDevisList,
  ArtifactFacturesList,
  ArtifactClientsList,
  ArtifactProspectsList,
  ArtifactContactsList,
  ArtifactTasksList,
  ArtifactTeamList,
  ArtifactEventsList,
  ArtifactMessagesList,
  ArtifactChantiersList,
  ArtifactProductsList,
  ArtifactNotesList,
  ArtifactDashboard,
  EmptyState
} from '../../components/artifacts/DataArtifacts';
import VoiceNotesSuperpouvoir from '../../components/ai/VoiceNotesSuperpouvoir';
import { FormulaBadge } from '../../components/credits';
import { ThemeToggle, ThemeToggleMini, useTheme } from '../../context/ThemeContext';
import RegisterPromptModal from '../../components/RegisterPromptModal';
import { 
  getAssistantContext, 
  resolveContextKey, 
  defaultAssistantContext 
} from '../../config/assistantContextConfig';
import { useBuyer } from '../../contexts/BuyerContext';

// ═══════════════════════════════════════════════════════════════════════════════
// IMPORT DES PAGES EXISTANTES - À afficher dans le panneau droit
// ═══════════════════════════════════════════════════════════════════════════════
// ────────────────────────────────────────────────────────────────────────────────
// COCKPIT PRO - Collaboration
// ────────────────────────────────────────────────────────────────────────────────
import EquipeProPage from '../cockpit-pro/collaboration/EquipeProPage';
import FeedProPage from '../cockpit-pro/collaboration/FeedProPage';
import TchatProPage from '../cockpit-pro/collaboration/TchatProPage';
import TachesCollabProPage from '../cockpit-pro/collaboration/TachesProPage';
import CollabPage from '../../components/collab/CollabPage';
// ────────────────────────────────────────────────────────────────────────────────
// COCKPIT PRO - Pointage RH
// ────────────────────────────────────────────────────────────────────────────────
import TimeClockPage from '../cockpit/TimeClockPage';
import TimeClockManagerPage from '../cockpit/TimeClockManagerPage';
// ────────────────────────────────────────────────────────────────────────────────
// COCKPIT PRO - Pages principales
// ────────────────────────────────────────────────────────────────────────────────
import TachesProPage from '../cockpit-pro/TachesProPage';
import AgendaProPage from '../cockpit-pro/AgendaProPage';
import MessagerieProPage from '../cockpit-pro/MessagerieProPage';
import DemandesDevisPage from '../cockpit-pro/DemandesDevisPage';
import DocumentsProPage from '../cockpit-pro/DocumentsProPage';
import CockpitProDashboard from '../cockpit-pro/CockpitProDashboard';
import MailsProPage from '../cockpit-pro/MailsProPage';
import ReseauProPage from '../cockpit-pro/ReseauProPage';
import ReseauSocialProPage from '../cockpit-pro/ReseauSocialProPage';
import EmploiProPage from '../cockpit-pro/EmploiProPage';
import VisioProPage from '../cockpit-pro/VisioProPage';
import MesOptionsPage from '../cockpit-pro/MesOptionsPage';
// ────────────────────────────────────────────────────────────────────────────────
// COCKPIT PRO - Plateforme (Superadmin uniquement)
// ────────────────────────────────────────────────────────────────────────────────
import PlateformeDashboardPage from '../cockpit-pro/plateforme/PlateformeDashboardPage';
import PlateformeAbonnementsPage from '../cockpit-pro/plateforme/PlateformeAbonnementsPage';
import CercleConfiancePage from '../cockpit-pro/plateforme/CercleConfiancePage';
import MesContactsPage from '../cockpit-particulier/MesContactsPage';
import TicketsProPage from '../cockpit-pro/marketplace/TicketsProPage';
// ────────────────────────────────────────────────────────────────────────────────
// COCKPIT PRO - Catalogue
// ────────────────────────────────────────────────────────────────────────────────
import ImportCSVPage from '../cockpit-pro/catalogue/ImportCSVPage';
import PrestationsPage from '../client/crm-batiment/PrestationsPage';
import ProduitsPage from '../client/crm-batiment/ProduitsPage';
import HourlyRatesPage from '../client/HourlyRatesPage';
import BoutiqueProPage from '../client/crm-batiment/BoutiqueProPage';
// ────────────────────────────────────────────────────────────────────────────────
// COCKPIT PARTICULIER - Pages principales
// ────────────────────────────────────────────────────────────────────────────────
import MesNotesPage from '../cockpit-particulier/MesNotesPage';
import DashboardParticulierPage from '../cockpit-particulier/DashboardPage';
import DocumentsParticulierPage from '../cockpit-particulier/DocumentsPage';
import AgendaParticulierPage from '../cockpit-particulier/AgendaPage';
import MessagerieParticulierPage from '../cockpit-particulier/MessagerieParticulierPage';
import MailsParticulierPage from '../cockpit-particulier/MailsPage';
import TachesKanbanPage from '../cockpit-particulier/TachesKanbanPage';
import BoiteAIdeesPage from '../cockpit-particulier/BoiteAIdeesPage';
import VisioconferencePage from '../cockpit-particulier/VisioconferencePage';
import FavorisPage from '../cockpit-particulier/FavorisPage';
import AchatsPage from '../cockpit-particulier/AchatsPage';
import VentesPage from '../cockpit-particulier/VentesPage';
import MesAnnoncesPage from '../cockpit-particulier/MesAnnoncesPage';
import VendeursSuivisPage from '../cockpit-particulier/VendeursSuivisPage';
import CerclesPage from '../cockpit-particulier/CerclesPage';
import GroupesPage from '../cockpit-particulier/GroupesPage';
// ────────────────────────────────────────────────────────────────────────────────
// COCKPIT PARTICULIER - Réseau Social
// ────────────────────────────────────────────────────────────────────────────────
import FeedReseau from '../cockpit-particulier/reseau/FeedReseau';
import NotificationsPage from '../cockpit-particulier/reseau/NotificationsPage';
import MesPublications from '../cockpit-particulier/reseau/MesPublications';
import BlogPage from '../cockpit-particulier/reseau/BlogPage';
import MaGalerie from '../cockpit-particulier/reseau/MaGalerie';
import EvenementsPage from '../cockpit-particulier/reseau/EvenementsPage';
import SalonsPage from '../cockpit-particulier/reseau/SalonsPage';
import MesAbonnes from '../cockpit-particulier/reseau/MesAbonnes';
import MesAbonnements from '../cockpit-particulier/reseau/MesAbonnements';
import MessagesReseau from '../cockpit-particulier/reseau/MessagesReseau';
// ────────────────────────────────────────────────────────────────────────────────
// COCKPIT PARTICULIER - Marketplace
// ────────────────────────────────────────────────────────────────────────────────
import DashboardMarketplace from '../cockpit-particulier/marketplace/DashboardMarketplace';
import CommandesPage from '../cockpit-particulier/marketplace/CommandesPage';
import AvisMarketplacePage from '../cockpit-particulier/marketplace/AvisMarketplacePage';
import FavorisMarketplacePage from '../cockpit-particulier/marketplace/FavorisMarketplacePage';
import DeposerAnnoncePage from '../cockpit-particulier/DeposerAnnoncePage';
// ────────────────────────────────────────────────────────────────────────────────
// COCKPIT PARTICULIER - Artisans/Prestataires
// ────────────────────────────────────────────────────────────────────────────────
import DashboardArtisans from '../cockpit-particulier/artisans/DashboardArtisans';
import RechercheArtisans from '../cockpit-particulier/artisans/RechercheArtisans';
import DevisParticulierPage from '../cockpit-particulier/artisans/DevisPage';
import ChantiersParticulierPage from '../cockpit-particulier/artisans/ChantiersPage';
import ArtisansFavoris from '../cockpit-particulier/artisans/ArtisansFavoris';
import AvisArtisans from '../cockpit-particulier/artisans/AvisArtisans';
// ────────────────────────────────────────────────────────────────────────────────
// COCKPIT PARTICULIER - Emploi
// ────────────────────────────────────────────────────────────────────────────────
import DashboardEmploi from '../cockpit-particulier/emploi/DashboardEmploi';
import OffresEmploiPage from '../cockpit-particulier/emploi/OffresPage';
import CandidaturesPage from '../cockpit-particulier/emploi/CandidaturesPage';
import CVPage from '../cockpit-particulier/emploi/CVPage';
import AlertesEmploiPage from '../cockpit-particulier/emploi/AlertesPage';
import RechercheEmploiPage from '../cockpit-particulier/emploi/RechercheEmploiPage';
import OffresBTPPage from '../cockpit-particulier/emploi/OffresBTPPage';
import EntreprisesRecrutentPage from '../cockpit-particulier/emploi/EntreprisesRecrutentPage';
import DeposerCVPage from '../cockpit-particulier/emploi/DeposerCVPage';
import EmploiUnifiedPage from '../cockpit-particulier/emploi/EmploiUnifiedPage';
import CockpitMessagesPage from '../cockpit-particulier/MessagesPage';
// ────────────────────────────────────────────────────────────────────────────────
// COMPOSANTS MOBILE
// ────────────────────────────────────────────────────────────────────────────────
import MobileTasksListPage from '../../components/mobile/MobileTasksListPage';

// UI Components
import FloatingHelper from '../../components/ui/FloatingHelper';
import CockpitMainNav from '../../components/layout/CockpitMainNav';
import CockpitProTopNav from '../../components/layout/CockpitProTopNav';

// ────────────────────────────────────────────────────────────────────────────────
// MODALES NAVIGATION (Rayons, Filtres, Catégories)
// ────────────────────────────────────────────────────────────────────────────────
import MarketplaceCatalogueMenu from '../../components/marketplace/MarketplaceCatalogueMenu';
import ParticuliersCatalogueMenu from '../../components/particuliers/ParticuliersCatalogueMenu';
import UnifiedFilterSystem from '../../components/filters/UnifiedFilterSystem';
import MobileBottomNav from '../../components/layout/MobileBottomNav';

// ────────────────────────────────────────────────────────────────────────────────
// PAGES EXTERNES (Navigation horizontale)
// ────────────────────────────────────────────────────────────────────────────────
import UniversPage from '../UniversPage';
import AbracadaWorldHome from '../AbracadaWorldHome';
import HomePage from '../HomePage';
import AccueilBatiPremium from '../../components/AccueilBatiPremium';
import ArtisansPage from '../ecosystem/marketplace/ArtisansPage';
import MobileArtisansPage from '../mobile/MobileArtisansPage';
import ShopDetailPage from '../ecosystem/marketplace/ShopDetailPage';
import Artisans5EtoilesPage from '../ecosystem/marketplace/Artisans5EtoilesPage';
import ArtisansMapView from '../../components/maps/ArtisansMapView';
import MarketplaceProduitsPage from '../univers/MarketplaceProduitsPage';
import EntreParticuliersPage from '../univers/EntreParticuliersPage';
import ActuPage from '../ActuPage';
import DesktopGaleriePage from '../desktop/DesktopGaleriePage';
import DesktopSalonPage from '../desktop/DesktopSalonPage';
import DesktopEvenementsPage from '../desktop/DesktopEvenementsPage';
import DesktopCinemaPage from '../desktop/DesktopCinemaPage';
import DecrireProjetPage from '../DecrireProjetPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION DES MENUS
// ═══════════════════════════════════════════════════════════════════════════════

// Menu PRO complet
const MENU_PRO = {
  mon_espace_pro: {
    id: 'mon_espace_pro',
    label: 'Mon Espace',
    icon: User,
    color: 'blue',
    context: 'hub_espace_pro', // Hub direct
    subItems: [
      { id: 'pro-taches', label: 'Liste des Tâches', icon: Check, context: 'taches_pro_perso', description: 'Organise ton quotidien' },
      { id: 'pro-contacts', label: 'Mes Contacts', icon: Users, context: 'contacts_pro', description: 'Gère tes contacts' },
      { id: 'pro-emails', label: 'Mes Emails', icon: Mail, context: 'emails_pro', description: 'Gère ta boîte mail' },
      { id: 'pro-agenda', label: 'Mon Agenda', icon: Calendar, context: 'agenda_pro_perso', description: 'Planifie tes rendez-vous' },
      { id: 'pro-visio', label: 'Visioconférence', icon: Video, context: 'visio_pro', description: 'Appels vidéo' },
      { id: 'pro-notes', label: 'Mes Notes', icon: FileText, context: 'notes_pro', description: 'Prends des notes' },
      { id: 'pro-idees', label: 'Boîte à idées', icon: Lightbulb, context: 'boite_idees_pro', description: 'Capture tes inspirations' },
      { id: 'pro-documents', label: 'Mes Documents', icon: FolderOpen, context: 'documents_pro', description: 'Stocke tes fichiers' },
    ]
  },
  collaboration: {
    id: 'collaboration',
    label: 'Collaboration',
    icon: Users,
    color: 'blue',
    subItems: [
      { id: 'col-dashboard', label: 'Tableau de bord', icon: Home, context: 'collaboration_dashboard' },
      { id: 'col-equipe', label: 'Mon Équipe', icon: Users, context: 'equipe' },
      { id: 'col-collabs', label: 'Collabs (Externes)', icon: UserPlus, context: 'collabs' },
      { id: 'col-pointage', label: 'Pointage RH', icon: Clock, context: 'pointage_rh' },
      { id: 'col-pointage-manager', label: 'Pointage Manager', icon: Eye, context: 'pointage_manager' },
      { id: 'col-taches', label: 'Tâches partagées', icon: Check, context: 'taches' },
      { id: 'col-agenda', label: 'Agenda partagé', icon: Calendar, context: 'agenda' },
      { id: 'col-tchat', label: 'Tchat équipe', icon: MessageCircle, context: 'tchat_equipe' },
      { id: 'col-feed', label: 'Feed équipe', icon: Newspaper, context: 'feed_equipe' },
      { id: 'col-documents', label: 'Documents partagés', icon: Folder, context: 'documents' },
    ]
  },
  commercial: {
    id: 'commercial',
    label: 'Commercial',
    icon: Target,
    color: 'orange',
    subItems: [
      { id: 'com-dashboard', label: 'Tableau de bord', icon: Home, context: 'commercial_dashboard' },
      { id: 'com-demandes', label: 'Demandes de devis', icon: FileText, context: 'demandes_devis' },
      { id: 'com-prospects', label: 'Prospects', icon: UserPlus, context: 'prospects' },
      { id: 'com-pipeline', label: 'Pipeline', icon: TrendingUp, context: 'pipeline' },
      { id: 'com-clients', label: 'Clients', icon: Users, context: 'clients' },
      { id: 'com-devis', label: 'Mes Devis', icon: Receipt, context: 'devis' },
      { id: 'com-factures', label: 'Factures', icon: Receipt, context: 'factures' },
      { id: 'com-relances', label: 'Relances', icon: Bell, context: 'relances' },
    ]
  },
  catalogue: {
    id: 'catalogue',
    label: 'Catalogue',
    icon: Layers,
    color: 'blue',
    subItems: [
      { id: 'cat-dashboard', label: 'Vue d\'ensemble', icon: Home, context: 'page_pro_catalogue' },
      { id: 'cat-produits', label: 'Mes Produits', icon: Package, context: 'page_pro_produits' },
      { id: 'cat-prestations', label: 'Mes Prestations', icon: Wrench, context: 'page_pro_prestations' },
      { id: 'cat-tarifs', label: 'Grille tarifaire', icon: Euro, context: 'page_pro_tarifs' },
      { id: 'cat-import', label: 'Import CSV', icon: FileText, context: 'page_pro_import' },
    ]
  },
  chantiers: {
    id: 'chantiers',
    label: 'Chantiers',
    icon: HardHat,
    color: 'slate',
    subItems: [
      { id: 'ch-dashboard', label: 'Tableau de bord', icon: Home, context: 'chantiers_dashboard' },
      { id: 'ch-encours', label: 'Chantiers en cours', icon: Wrench, context: 'chantiers_en_cours' },
      { id: 'ch-avenir', label: 'À venir', icon: Calendar, context: 'chantiers_a_venir' },
      { id: 'ch-termines', label: 'Terminés', icon: Check, context: 'chantiers_termines' },
      { id: 'ch-planning', label: 'Planning', icon: Calendar, context: 'planning' },
      { id: 'ch-pointage', label: 'Pointage GPS', icon: Target, context: 'pointage' },
    ]
  },
  marketing: {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    color: 'orange',
    subItems: [
      { id: 'mk-dashboard', label: 'Tableau de bord', icon: Home, context: 'marketing_dashboard' },
      { id: 'mk-stats', label: 'Statistiques', icon: BarChart2, context: 'stats' },
      { id: 'mk-campagnes', label: 'Campagnes email', icon: Mail, context: 'campagnes_email' },
      { id: 'mk-promos', label: 'Promotions', icon: Percent, context: 'promotions' },
      { id: 'mk-ventes-flash', label: 'Ventes Flash', icon: Zap, context: 'ventes_flash' },
      { id: 'mk-landing', label: 'Landing Pages', icon: Layout, context: 'landing_pages', isComingSoon: true },
    ]
  },
  reseauSocial: {
    id: 'reseauSocial',
    label: 'Réseau Social',
    icon: Globe,
    color: 'blue',
    subItems: [
      { id: 'rs-feed', label: 'Mon Feed', icon: Newspaper, context: 'feed_social' },
      { id: 'rs-posts', label: 'Mes Publications', icon: FileText, context: 'publications' },
      { id: 'rs-galerie', label: 'Ma Galerie', icon: Image, context: 'galerie' },
      { id: 'rs-abonnes', label: 'Mes Abonnés', icon: Users, context: 'abonnes' },
      { id: 'rs-abonnements', label: 'Mes Abonnements', icon: UserPlus, context: 'abonnements' },
      { id: 'rs-messages', label: 'Messages', icon: MessageCircle, context: 'messages_social' },
    ]
  },
  reseauPro: {
    id: 'reseauPro',
    label: 'Réseau Pro',
    icon: Network,
    color: 'slate',
    isComingSoon: true,
    comingSoonDescription: 'Un réseau dédié uniquement aux professionnels du bâtiment. Connecte-toi avec d\'autres artisans, partage tes réalisations, trouve des partenaires.',
    subItems: []
  },
  centraleAchat: {
    id: 'centraleAchat',
    label: 'Centrale d\'achat',
    icon: ShoppingCart,
    color: 'slate',
    isComingSoon: true,
    comingSoonDescription: 'Centrale d\'achats et regroupements d\'achats pour les professionnels du bâtiment. Prix négociés sur les matériaux et équipements.',
    subItems: []
  },
  emploi: {
    id: 'emploi',
    label: 'Emploi',
    icon: Briefcase,
    color: 'orange',
    subItems: [
      { id: 'em-recherche', label: 'Rechercher', icon: Search, context: 'recherche_emploi' },
      { id: 'em-offres', label: 'Mes Offres', icon: FileText, context: 'offres_emploi' },
      { id: 'em-candidatures', label: 'Candidatures', icon: Users, context: 'candidatures_recues' },
    ]
  },
  boutique: {
    id: 'boutique',
    label: 'Ma Boutique',
    icon: Store,
    color: 'orange',
    subItems: [
      { id: 'bt-apercu', label: 'Créer / Modifier', icon: Eye, context: 'page_pro_boutique' },
      { id: 'bt-infos', label: 'Informations', icon: Building, context: 'page_pro_boutique' },
      { id: 'bt-visuels', label: 'Logo & Couverture', icon: Camera, context: 'page_pro_boutique' },
      { id: 'bt-apropos', label: 'À propos', icon: FileText, context: 'boutique_apropos' },
      { id: 'bt-mentions', label: 'Mentions Devis', icon: Receipt, context: 'mentions_devis' },
      { id: 'bt-cgv', label: 'CGV', icon: Shield, context: 'cgv' },
      { id: 'bt-horaires', label: 'Horaires', icon: ClockIcon, context: 'horaires' },
      { id: 'bt-blog', label: 'Blog & Articles', icon: BookOpen, context: 'blog' },
    ]
  },
  // Administration supprimée - Tout est accessible via le bouton "Paramètres" en bas du menu
};

// Menu PLATEFORME - Superadmin uniquement
const MENU_PLATEFORME = {
  plateforme: {
    id: 'plateforme',
    label: '👑 Plateforme',
    icon: Shield,
    color: 'orange',
    superadminOnly: true,
    subItems: [
      { id: 'pf-dashboard', label: 'Vue d\'ensemble', icon: BarChart2, context: 'plateforme_dashboard' },
      { id: 'pf-cercle-confiance', label: 'Cercle de Confiance', icon: BadgeCheck, context: 'plateforme_cercle_confiance' },
      { id: 'pf-abonnements', label: 'Abonnements', icon: CreditCard, context: 'plateforme_abonnements' },
      { id: 'pf-vendeurs', label: 'Vendeurs / Boutiques', icon: Store, context: 'plateforme_vendeurs' },
      { id: 'pf-utilisateurs', label: 'Utilisateurs', icon: Users, context: 'plateforme_utilisateurs' },
      { id: 'pf-abracacoins', label: 'AbracaCoins', icon: Coins, context: 'plateforme_coins' },
      { id: 'pf-promos', label: 'Remises & Promos', icon: Percent, context: 'plateforme_promos' },
      { id: 'pf-analytics', label: 'Analytics', icon: TrendingUp, context: 'plateforme_analytics' },
      { id: 'pf-moderation', label: 'Modération', icon: Shield, context: 'plateforme_moderation' },
      { id: 'pf-tickets', label: 'Tickets Support', icon: MessageCircle, context: 'plateforme_tickets' },
      { id: 'pf-factures', label: 'Facturation Globale', icon: Receipt, context: 'plateforme_factures' },
      { id: 'pf-parametres', label: 'Paramètres', icon: Settings, context: 'plateforme_parametres' },
    ]
  },
};

// Menu PARTICULIER complet
const MENU_PARTICULIER = {
  espace: {
    id: 'espace',
    label: 'Mon Espace',
    icon: Settings,
    color: 'blue',
    context: 'hub_espace', // Hub direct au lieu de sous-menu
    // Gardé pour référence interne (utilisé par le hub)
    hubItems: [
      { id: 'o-taches', label: 'Liste des Tâches', icon: Check, context: 'taches_perso', description: 'Organise tes tâches quotidiennes' },
      { id: 'o-contacts', label: 'Mes Contacts', icon: Users, context: 'contacts', description: 'Gère tes contacts' },
      { id: 'o-emails', label: 'Mes Emails', icon: Mail, context: 'emails', description: 'Gère ta boîte mail' },
      { id: 'o-agenda', label: 'Mon Agenda', icon: Calendar, context: 'agenda_perso', description: 'Planifie tes rendez-vous' },
      { id: 'o-visio', label: 'Visioconférence', icon: Video, context: 'visio', description: 'Appels vidéo' },
      { id: 'o-notes', label: 'Mes Notes', icon: FileText, context: 'notes', description: 'Prends des notes' },
      { id: 'o-idees', label: 'Boîte à idées', icon: Lightbulb, context: 'boite_idees', description: 'Capture tes inspirations' },
      { id: 'o-documents', label: 'Mes Documents', icon: FolderOpen, context: 'documents', description: 'Stocke tes fichiers' },
    ]
  },
  reseau: {
    id: 'reseau',
    label: 'Réseau Social',
    icon: Network,
    color: 'orange',
    context: 'hub_reseau', // Hub direct
    hubItems: [
      { id: 'r-cercles', label: 'Mes groupes privés', icon: Users, context: 'cercles', description: 'Communautés privées' },
      { id: 'r-cercles-feed', label: 'Actu des groupes', icon: Newspaper, context: 'cercles_feed', description: 'Feed des groupes privés' },
      { id: 'r-posts', label: 'Mes Publications', icon: FileText, context: 'publications', description: 'Posts et articles' },
      { id: 'r-blog', label: 'Mon Blog', icon: PenTool, context: 'blog', description: 'Articles longs' },
      { id: 'r-galerie', label: 'Ma Galerie', icon: Image, context: 'galerie', description: 'Photos et médias' },
      { id: 'r-evenements', label: 'Événements', icon: Calendar, context: 'evenements', description: 'Agenda communautaire' },
      { id: 'r-salons', label: 'Groupes publics', icon: Hash, context: 'salons', description: 'Discussions ouvertes' },
      { id: 'r-abonnes', label: 'Mes Abonnés', icon: Users, context: 'abonnes', description: 'Qui te suit' },
      { id: 'r-abonnements', label: 'Mes Abonnements', icon: UserPlus, context: 'abonnements', description: 'Qui tu suis' },
      { id: 'r-messages', label: 'Messages privés', icon: MessageCircle, context: 'messages_prives', description: 'Conversations' },
    ]
  },
  marketplace: {
    id: 'marketplace',
    label: 'Marketplace',
    icon: Store,
    color: 'blue',
    context: 'hub_marketplace', // Hub direct
    hubItems: [
      { id: 'm-dashboard', label: 'Tableau de bord', icon: Home, context: 'marketplace_dashboard', description: 'Vue d\'ensemble' },
      { id: 'm-annonces', label: 'Mes Annonces', icon: Package, context: 'annonces', description: 'Produits en vente' },
      { id: 'm-commandes', label: 'Mes Commandes', icon: ShoppingBag, context: 'commandes', description: 'Suivi des commandes' },
      { id: 'm-ventes', label: 'Mes Ventes', icon: Euro, context: 'ventes', description: 'Historique des ventes' },
      { id: 'm-achats', label: 'Mes Achats', icon: ShoppingBag, context: 'achats', description: 'Historique des achats' },
      { id: 'm-messages', label: 'Messages', icon: MessageCircle, context: 'messages_marketplace', description: 'Échanges acheteurs' },
      { id: 'm-favoris', label: 'Favoris', icon: Heart, context: 'favoris', description: 'Produits sauvegardés' },
      { id: 'm-avis', label: 'Mes Avis', icon: Star, context: 'avis', description: 'Évaluations données' },
      { id: 'm-vendeurs', label: 'Vendeurs suivis', icon: Users, context: 'vendeurs_suivis', description: 'Boutiques favorites' },
    ]
  },
  artisans: {
    id: 'artisans',
    label: 'Prestataires',
    icon: Handshake,
    color: 'slate',
    context: 'hub_prestataires', // Hub direct
    hubItems: [
      { id: 'a-dashboard', label: 'Tableau de bord', icon: Home, context: 'artisans_dashboard', description: 'Vue d\'ensemble' },
      { id: 'a-recherche', label: 'Trouver un artisan', icon: Search, context: 'recherche_artisan', description: 'Recherche locale' },
      { id: 'a-devis', label: 'Mes Demandes', icon: FileText, context: 'demandes_devis_part', description: 'Demandes de devis' },
      { id: 'a-chantiers', label: 'Mes Chantiers', icon: Wrench, context: 'mes_chantiers', description: 'Projets en cours' },
      { id: 'a-favoris', label: 'Artisans favoris', icon: Bookmark, context: 'artisans_favoris', description: 'Pros de confiance' },
      { id: 'a-avis', label: 'Mes Avis', icon: Star, context: 'avis_artisans', description: 'Évaluations données' },
    ]
  },
  emploi: {
    id: 'emploi',
    label: 'Emploi',
    icon: Briefcase,
    color: 'orange',
    context: 'hub_emploi', // Hub direct
    hubItems: [
      { id: 'e-dashboard', label: 'Tableau de bord', icon: Home, context: 'emploi_dashboard', description: 'Vue d\'ensemble' },
      { id: 'e-offres', label: 'Offres sauvegardées', icon: Bookmark, context: 'offres_sauvegardees', description: 'Postes intéressants' },
      { id: 'e-candidatures', label: 'Mes Candidatures', icon: FileText, context: 'candidatures', description: 'Suivi des réponses' },
      { id: 'e-cv', label: 'Mon CV', icon: User, context: 'mon_cv', description: 'Profil professionnel' },
      { id: 'e-alertes', label: 'Alertes emploi', icon: Bell, context: 'alertes_emploi', description: 'Notifications jobs' },
    ]
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM - Charte graphique AbracadaBati
// Bleu Navy #1E3A5F | Orange #F97316 | Design premium épuré style Claude
// ═══════════════════════════════════════════════════════════════════════════════
const BRAND = {
  navy: '#1E3A5F',
  navyLight: '#2d5a8a',
  navyDark: '#152a45',
  orange: '#F97316',
  orangeLight: '#FB923C',
  orangeDark: '#EA580C',
};

// Style unifié pour tous les espaces - Design premium
const UNIFIED_STYLE = {
  // Élément normal
  default: {
    bg: 'bg-transparent',
    text: 'text-white/70',
    iconBg: 'bg-white/10',
  },
  // Élément survolé
  hover: {
    bg: 'hover:bg-white/10',
    text: 'hover:text-white',
  },
  // Élément actif
  active: {
    bg: 'bg-white/15',
    text: 'text-white',
    iconBg: 'bg-[#F97316]',
    border: 'border-l-2 border-[#F97316]',
  },
  // Sous-item normal
  subDefault: {
    text: 'text-white/60',
  },
  // Sous-item actif
  subActive: {
    bg: 'bg-[#F97316]/20',
    text: 'text-[#F97316]',
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════
const getUserId = () => {
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  const buyerUser = localStorage.getItem('buyer_user');
  const clientUser = localStorage.getItem('client_user');
  
  if (ecosystemUser) {
    try { return JSON.parse(ecosystemUser).id; } catch(e) {}
  }
  if (buyerUser) {
    try { return JSON.parse(buyerUser).id; } catch(e) {}
  }
  if (clientUser) {
    try { return JSON.parse(clientUser).id; } catch(e) {}
  }
  return localStorage.getItem('userId') || 'demo_user';
};

const getUserName = () => {
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  if (ecosystemUser) {
    try { 
      const parsed = JSON.parse(ecosystemUser);
      return parsed.first_name || parsed.name?.split(' ')[0] || 'toi';
    } catch(e) {}
  }
  return 'toi';
};

const getAssistantName = () => localStorage.getItem('assistant_custom_name') || 'Mon Assistant';
const setAssistantNameStorage = (name) => localStorage.setItem('assistant_custom_name', name);

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT MENU ITEM - Design Premium Unifié
// ═══════════════════════════════════════════════════════════════════════════════
function MenuEspaceItem({ espace, isExpanded, onToggle, activeContext, onSelectContext, onShowComingSoon, isMobileView = false, pendingInvitationsCount = 0 }) {
  const Icon = espace.icon;
  
  // Mapping des espaces vers leurs hubs (pour mobile)
  const hubMapping = {
    'mon_espace': 'hub_espace',
    'reseau_social': 'hub_reseau',
    'marketplace': 'hub_marketplace',
    'prestataires': 'hub_prestataires',
    'emploi': 'hub_emploi',
    // Cockpit Pro
    'mon_espace_pro': 'hub_espace_pro',
    'collaboration': 'hub_collaboration',
    'commercial': 'hub_commercial',
    'catalogue': 'hub_catalogue',
    'chantiers': 'hub_chantiers_pro',
    'marketing': 'hub_marketing',
    'reseauSocial': 'hub_reseau_pro',
  };
  
  // Si c'est un espace "à venir", afficher un style grisé avec le popup
  if (espace.isComingSoon) {
    return (
      <div className="mb-2" data-testid={`menu-section-${espace.id}`}>
        <button
          onClick={() => onShowComingSoon && onShowComingSoon(espace)}
          data-testid={`menu-btn-${espace.id}`}
          className={`w-full flex items-center gap-3 px-4 py-4 rounded-2xl text-sm font-medium transition-all duration-200 ${
            isMobileView 
              ? 'bg-white/60 text-[#9C8B7A]' 
              : 'text-white/40 hover:bg-white/5'
          }`}
          style={isMobileView ? { boxShadow: '0 2px 10px rgba(31, 59, 110, 0.03)' } : {}}
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isMobileView ? 'bg-[#F5F3EF]' : 'bg-white/5'}`}>
            <Icon size={18} className={isMobileView ? 'text-[#9C8B7A]' : 'text-white/40'} />
          </div>
          <span className="flex-1 text-left font-semibold">{espace.label}</span>
          <span className="px-2 py-1 bg-amber-100/80 text-amber-700 text-[10px] font-bold rounded-full uppercase">
            À venir
          </span>
        </button>
      </div>
    );
  }
  
  // Si l'élément a un context direct (sans subItems), c'est un lien direct
  if (espace.context && (!espace.subItems || espace.subItems.length === 0)) {
    const isActive = activeContext === espace.context;
    return (
      <div className="mb-2" data-testid={`menu-section-${espace.id}`}>
        <button
          onClick={() => onSelectContext(espace.context, espace.label, espace.label)}
          data-testid={`menu-btn-${espace.id}`}
          className={`
            w-full flex items-center gap-3 px-4 py-4 rounded-2xl text-[15px] font-semibold transition-all duration-200
            ${isMobileView
              ? (isActive ? 'bg-[#E8955C] text-white' : 'bg-white/90 text-[#1F3B6E]')
              : (isActive 
                  ? 'bg-[#F97316] text-white shadow-lg shadow-orange-500/20' 
                  : 'text-white/70 hover:bg-white/5 hover:text-white')
            }
          `}
          style={isMobileView ? { boxShadow: '0 2px 15px rgba(31, 59, 110, 0.05), 0 1px 3px rgba(0,0,0,0.03)' } : {}}
        >
          <div className={`
            w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200
            ${isMobileView
              ? (isActive ? 'bg-white/20' : 'bg-[#F0F5FA]')
              : (isActive ? 'bg-white/20' : 'bg-white/10')
            }
          `}>
            <Icon size={18} className={isMobileView ? (isActive ? 'text-white' : 'text-[#1F3B6E]') : 'text-white'} />
          </div>
          <span className="flex-1 text-left">{espace.label}</span>
        </button>
      </div>
    );
  }
  
  const hasActiveChild = espace.subItems?.some(item => activeContext === item.context) || false;
  
  // Sur MOBILE : Ouvrir le Hub au lieu de l'accordéon
  const handleClick = () => {
    if (isMobileView && hubMapping[espace.id]) {
      // Ouvrir le Hub correspondant
      onSelectContext(hubMapping[espace.id], espace.label, espace.label);
    } else {
      // Desktop : ouvrir l'accordéon
      onToggle();
    }
  };

  return (
    <div className="mb-2" data-testid={`menu-section-${espace.id}`}>
      <button
        onClick={handleClick}
        data-testid={`menu-btn-${espace.id}`}
        className={`
          w-full flex items-center gap-3 px-4 py-4 rounded-2xl text-[15px] font-semibold transition-all duration-200
          ${isMobileView
            ? 'bg-white/90 text-[#1F3B6E]'
            : (isExpanded || hasActiveChild 
                ? 'bg-white/10 text-white' 
                : 'text-white/70 hover:bg-white/5 hover:text-white')
          }
        `}
        style={isMobileView ? { boxShadow: '0 2px 15px rgba(31, 59, 110, 0.05), 0 1px 3px rgba(0,0,0,0.03)' } : {}}
      >
        <div className={`
          w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200
          ${isMobileView
            ? (hasActiveChild ? 'bg-[#E8955C]' : 'bg-[#F0F5FA]')
            : (isExpanded || hasActiveChild ? 'bg-[#F97316]' : 'bg-white/10')
          }
        `}>
          <Icon size={18} className={isMobileView ? (hasActiveChild ? 'text-white' : 'text-[#1F3B6E]') : 'text-white'} />
        </div>
        <span className="flex-1 text-left">{espace.label}</span>
        {/* Flèche seulement sur desktop (accordéon) */}
        {!isMobileView && (
          <ChevronRight 
            size={18} 
            strokeWidth={1.5}
            className={`transition-transform duration-300 text-white/50 ${isExpanded ? 'rotate-90' : ''}`} 
          />
        )}
        {/* Sur mobile : flèche vers la droite (indique navigation) */}
        {isMobileView && (
          <ChevronRight 
            size={18} 
            strokeWidth={1.5}
            className="text-[#C9A15D]/70"
          />
        )}
      </button>

      {/* Accordéon - Desktop seulement */}
      {!isMobileView && (
        <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden"
            data-testid={`menu-submenu-${espace.id}`}
          >
            <div className={`mt-2 space-y-1 ${isMobileView ? 'bg-white/80 rounded-2xl p-3' : 'ml-5 pl-4 border-l border-white/20'}`}
              style={isMobileView ? { boxShadow: '0 2px 12px rgba(31, 59, 110, 0.04)' } : {}}
            >
              {espace.subItems.map((item) => {
                const ItemIcon = item.icon;
                const isActive = activeContext === item.context;
                
                // Si l'item a un path, c'est une navigation externe
                if (item.path) {
                  return (
                    <a
                      key={item.id}
                      href={item.path}
                      data-testid={`menu-item-${item.id}`}
                      className={`
                        w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all duration-200
                        ${isMobileView
                          ? 'text-[#1F3B6E]/70 hover:bg-[#F0F5FA]'
                          : 'text-white/60 hover:bg-white/5 hover:text-white'
                        }
                      `}
                    >
                      <ItemIcon size={16} className={isMobileView ? 'text-[#1F3B6E]/50' : 'text-white/50'} />
                      <span className="flex-1 text-left truncate">{item.label}</span>
                      <ChevronRight size={14} className={isMobileView ? 'text-[#C9A15D]/50' : 'text-white/30'} />
                    </a>
                  );
                }
                
                return (
                  <button
                    key={item.id}
                    data-testid={`menu-item-${item.context}`}
                    onClick={() => onSelectContext(item.context, item.label, espace.label)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-all duration-200
                      ${isMobileView
                        ? (isActive 
                            ? 'bg-[#E8955C] text-white font-semibold' 
                            : 'text-[#1F3B6E]/70 hover:bg-[#F0F5FA]')
                        : (isActive 
                            ? 'bg-[#F97316] text-white font-medium shadow-lg shadow-orange-500/20' 
                            : 'text-white/60 hover:bg-white/5 hover:text-white')
                      }
                    `}
                    style={isActive && isMobileView ? { boxShadow: '0 4px 12px rgba(232, 149, 92, 0.25)' } : {}}
                  >
                    <ItemIcon size={16} className={isActive ? 'text-white' : (isMobileView ? 'text-[#1F3B6E]/50' : 'text-white/50')} />
                    <span className="flex-1 text-left truncate">{item.label}</span>
                    {/* Badge invitations pour "Mes Messages" */}
                    {item.context === 'messagerie' && pendingInvitationsCount > 0 && (
                      <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-orange-500 text-white text-[10px] font-bold rounded-full animate-pulse">
                        {pendingInvitationsCount}
                      </span>
                    )}
                    {item.isNew && (
                      <span className="px-2 py-0.5 bg-[#1F3B6E] text-white text-[10px] font-bold rounded-full">NEW</span>
                    )}
                    {item.isComingSoon && (
                      <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${isMobileView ? 'bg-[#EEF4FA] text-[#8B7355]' : 'bg-white/20 text-white/70'}`}>Bientôt</span>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PARAMÈTRES PANEL - Intégré dans le panneau droit
// ═══════════════════════════════════════════════════════════════════════════════
function ParametresPanel({ isPro = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profil'); // profil, notifications, securite
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    ville: '',
    codePostal: '',
    // Champs Pro
    entreprise: '',
    siret: '',
  });
  
  // Notifications complètes
  const [notifications, setNotifications] = useState({
    // Général
    email_notifications: true,
    push_notifications: true,
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    // Abracadatchat
    chat_messages: true,
    chat_mentions: true,
    chat_reactions: true,
    // Social
    new_follower: true,
    follow_request: true,
    likes_on_posts: true,
    comments_on_posts: true,
    mentions: true,
    // Commercial (Pro)
    new_quote_request: true,
    quote_accepted: true,
    quote_refused: true,
    payment_received: true,
    new_prospect: true,
    // Particulier
    new_offer: true,
    price_drop: true,
    vendor_contact: true,
    friend_activity: true,
    // Événements
    event_reminder: true,
    event_invitation: true,
    // Système
    security_alerts: true,
    newsletter: false,
  });
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        // Charger le profil
        const response = await fetch(`${API_URL}/api/ecosystem/settings/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const userData = data.user || {};
          setProfile({
            name: userData.name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || '',
            email: userData.email || '',
            phone: userData.phone || '',
            ville: userData.city || '',
            codePostal: userData.postal_code || '',
            entreprise: userData.company_name || '',
            siret: userData.siret || '',
          });
          const settings = data.settings || {};
          setNotifications(prev => ({
            ...prev,
            email_notifications: settings.notifications_email !== false,
            push_notifications: settings.push_notifications !== false,
            quiet_hours_enabled: settings.quiet_hours_enabled || false,
          }));
        }
        
        // Charger les préférences de notifications
        try {
          const notifResponse = await fetch(`${API_URL}/api/notifications/preferences`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (notifResponse.ok) {
            const notifData = await notifResponse.json();
            setNotifications(prev => ({ ...prev, ...notifData }));
          }
        } catch (e) {
          console.log('Préférences notifications non disponibles');
        }
      } catch (error) {
        console.error('Erreur chargement paramètres:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleSaveProfile = async () => {
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
    if (!token) {
      toast.error('Tu dois être connecté');
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/ecosystem/settings/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: profile.name,
          phone: profile.phone,
          ville: profile.ville,
          code_postal: profile.codePostal,
          entreprise: profile.entreprise,
          siret: profile.siret,
        })
      });
      if (response.ok) {
        toast.success('Profil enregistré');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const updateNotification = async (key, value) => {
    setNotifications(prev => ({ ...prev, [key]: value }));
    
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
    try {
      await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });
    } catch (error) {
      // Revert si erreur
      setNotifications(prev => ({ ...prev, [key]: !value }));
    }
  };

  const handleDeleteAccount = () => {
    if (confirm('Es-tu sûr de vouloir supprimer ton compte ? Cette action est irréversible.')) {
      toast.error('Contacte le support pour supprimer ton compte');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ecosystem_token');
    localStorage.removeItem('client_token');
    localStorage.removeItem('buyer_token');
    localStorage.removeItem('user_data');
    toast.success('Déconnexion réussie');
    navigate('/');
  };

  // Toggle Switch component
  const ToggleSwitch = ({ enabled, onChange }) => (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-[#F97316]' : 'bg-gray-300'}`}
    >
      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
    </button>
  );

  // Notification Item component
  const NotifItem = ({ icon: Icon, label, desc, notifKey, color = 'orange' }) => (
    <label className="flex items-center justify-between py-3 cursor-pointer">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          color === 'orange' ? 'bg-[#FFF8F4] text-[#F97316]' : 
          color === 'blue' ? 'bg-[#F0F4F8] text-[#1E3A5F]' :
          color === 'green' ? 'bg-green-50 text-green-600' :
          color === 'purple' ? 'bg-purple-50 text-purple-600' :
          'bg-gray-100 text-gray-600'
        }`}>
          <Icon size={18} />
        </div>
        <div>
          <p className="font-medium text-gray-900 text-sm">{label}</p>
          <p className="text-xs text-gray-500">{desc}</p>
        </div>
      </div>
      <ToggleSwitch 
        enabled={notifications[notifKey]} 
        onChange={(v) => updateNotification(notifKey, v)} 
      />
    </label>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-hide">
      {/* Tabs - 5 onglets */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex overflow-x-auto scrollbar-hide">
          {[
            { id: 'profil', label: 'Profil', icon: User },
            { id: 'abonnement', label: 'Abonnement', icon: CreditCard },
            { id: 'facturation', label: 'Facturation', icon: Receipt },
            { id: 'notifications', label: 'Notifications', icon: Bell },
            { id: 'securite', label: 'Sécurité', icon: Shield },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-[100px] flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'text-[#F97316] border-b-2 border-[#F97316] bg-[#FFF8F4]' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* TAB PROFIL */}
        {activeTab === 'profil' && (
          <>
            <div>
              <h2 className="text-lg font-bold text-[#1E3A5F]">
                {isPro ? 'Mon profil professionnel' : 'Mon profil'}
              </h2>
              <p className="text-gray-500 text-sm">Gère tes informations personnelles</p>
            </div>

            {/* Champs Pro : Entreprise & SIRET */}
            {isPro && (
              <div className="bg-[#F0F4F8] rounded-xl p-5 space-y-4 border border-[#1E3A5F]/10">
                <h3 className="font-semibold text-[#1E3A5F] flex items-center gap-2">
                  <Building size={18} className="text-[#F97316]" />
                  Informations entreprise
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise</label>
                  <input
                    type="text"
                    value={profile.entreprise}
                    onChange={(e) => setProfile(p => ({ ...p, entreprise: e.target.value }))}
                    placeholder="Ma Société SAS"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316] bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Numéro SIRET</label>
                  <input
                    type="text"
                    value={profile.siret}
                    onChange={(e) => setProfile(p => ({ ...p, siret: e.target.value.replace(/\D/g, '').slice(0, 14) }))}
                    placeholder="123 456 789 00012"
                    maxLength={17}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316] bg-white font-mono"
                  />
                  <p className="text-xs text-gray-400 mt-1">14 chiffres sans espaces</p>
                </div>
              </div>
            )}

            {/* Informations personnelles */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
              <h3 className="font-semibold text-[#1E3A5F] flex items-center gap-2">
                <User size={18} className="text-[#F97316]" />
                Informations personnelles
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                />
                <p className="text-xs text-gray-400 mt-1">L'email ne peut pas être modifié</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                  placeholder="06 12 34 56 78"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                  <input
                    type="text"
                    value={profile.ville}
                    onChange={(e) => setProfile(p => ({ ...p, ville: e.target.value }))}
                    placeholder="Lyon"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                  <input
                    type="text"
                    value={profile.codePostal}
                    onChange={(e) => setProfile(p => ({ ...p, codePostal: e.target.value }))}
                    placeholder="69001"
                    maxLength={5}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
                  />
                </div>
              </div>
            </div>

            {/* Bouton Enregistrer */}
            <button
              onClick={handleSaveProfile}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Enregistrer le profil
                </>
              )}
            </button>
          </>
        )}

        {/* TAB ABONNEMENT */}
        {activeTab === 'abonnement' && (
          <>
            <div>
              <h2 className="text-lg font-bold text-[#1E3A5F]">Mon Abonnement</h2>
              <p className="text-gray-500 text-sm">Gère ton plan et tes options</p>
            </div>

            {/* Plan actuel */}
            <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] rounded-xl p-5 text-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white/70 text-sm">Plan actuel</p>
                  <h3 className="text-2xl font-bold">{isPro ? 'Professionnel' : 'Particulier'}</h3>
                </div>
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <Crown size={24} className="text-[#F97316]" />
                </div>
              </div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-3xl font-bold">{isPro ? '29,90' : '0'}</span>
                <span className="text-white/70">€/mois</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Check size={16} className="text-green-400" />
                  <span>Accès complet au Cockpit</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={16} className="text-green-400" />
                  <span>100 AbracaCoins/mois inclus</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check size={16} className="text-green-400" />
                  <span>Support prioritaire</span>
                </div>
              </div>
            </div>

            {/* AbracaCoins */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#1E3A5F] flex items-center gap-2">
                  <Coins size={18} className="text-[#F97316]" />
                  Mes AbracaCoins
                </h3>
                <span className="text-2xl font-bold text-[#F97316]">87</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3 mb-3">
                <div className="bg-gradient-to-r from-[#F97316] to-[#FB923C] h-3 rounded-full" style={{ width: '87%' }} />
              </div>
              <p className="text-xs text-gray-500 mb-4">87 coins restants sur 100 ce mois-ci</p>
              <button className="w-full py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2">
                <Plus size={16} />
                Acheter des AbracaCoins
              </button>
            </div>

            {/* Options d'abonnement */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-[#1E3A5F] text-sm">Options disponibles</h3>
              </div>
              <div className="p-4 space-y-3">
                {isPro ? (
                  <>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Users size={18} className="text-[#1E3A5F]" />
                        <div>
                          <p className="font-medium text-sm">Membres équipe</p>
                          <p className="text-xs text-gray-500">3 membres inclus</p>
                        </div>
                      </div>
                      <button className="text-sm text-[#F97316] font-medium hover:underline">+ Ajouter</button>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Cloud size={18} className="text-[#1E3A5F]" />
                        <div>
                          <p className="font-medium text-sm">Stockage cloud</p>
                          <p className="text-xs text-gray-500">5 Go utilisés sur 10 Go</p>
                        </div>
                      </div>
                      <button className="text-sm text-[#F97316] font-medium hover:underline">Augmenter</button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500 text-sm mb-3">Passe au plan Pro pour débloquer plus d'options</p>
                    <button className="px-4 py-2 bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-lg text-sm font-medium transition-colors">
                      Découvrir le plan Pro
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button className="flex-1 py-2.5 border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors">
                Changer de plan
              </button>
              <button className="flex-1 py-2.5 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors">
                Résilier
              </button>
            </div>
          </>
        )}

        {/* TAB FACTURATION */}
        {activeTab === 'facturation' && (
          <>
            <div>
              <h2 className="text-lg font-bold text-[#1E3A5F]">Facturation</h2>
              <p className="text-gray-500 text-sm">Historique et moyens de paiement</p>
            </div>

            {/* Moyen de paiement */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-[#1E3A5F] flex items-center gap-2 mb-4">
                <CreditCard size={18} className="text-[#F97316]" />
                Moyen de paiement
              </h3>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">VISA</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">•••• •••• •••• 4242</p>
                    <p className="text-xs text-gray-500">Expire 12/26</p>
                  </div>
                </div>
                <button className="text-sm text-[#F97316] font-medium hover:underline">Modifier</button>
              </div>
              <button className="mt-3 w-full py-2.5 border border-dashed border-gray-300 text-gray-500 hover:border-[#F97316] hover:text-[#F97316] rounded-lg text-sm transition-colors flex items-center justify-center gap-2">
                <Plus size={16} />
                Ajouter un moyen de paiement
              </button>
            </div>

            {/* Adresse de facturation */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-[#1E3A5F] flex items-center gap-2 mb-4">
                <MapPin size={18} className="text-[#F97316]" />
                Adresse de facturation
              </h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-sm">{isPro ? 'Ma Société SAS' : 'Jean Dupont'}</p>
                <p className="text-sm text-gray-600">123 Rue de la Paix</p>
                <p className="text-sm text-gray-600">75001 Paris, France</p>
              </div>
              <button className="mt-3 text-sm text-[#F97316] font-medium hover:underline">Modifier l'adresse</button>
            </div>

            {/* Historique des factures */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <h3 className="font-semibold text-[#1E3A5F] text-sm">Historique des factures</h3>
                <button className="text-xs text-[#F97316] font-medium hover:underline">Tout voir</button>
              </div>
              <div className="divide-y divide-gray-100">
                {[
                  { date: '01/04/2026', montant: '29,90 €', statut: 'Payée' },
                  { date: '01/03/2026', montant: '29,90 €', statut: 'Payée' },
                  { date: '01/02/2026', montant: '29,90 €', statut: 'Payée' },
                ].map((facture, idx) => (
                  <div key={idx} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-[#1E3A5F]/5 rounded-lg flex items-center justify-center">
                        <FileText size={16} className="text-[#1E3A5F]" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Facture {facture.date}</p>
                        <p className="text-xs text-gray-500">{facture.montant}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        {facture.statut}
                      </span>
                      <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                        <Download size={16} className="text-gray-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* TAB NOTIFICATIONS */}
        {activeTab === 'notifications' && (
          <>
            <div>
              <h2 className="text-lg font-bold text-[#1E3A5F]">Notifications</h2>
              <p className="text-gray-500 text-sm">Choisis quand et comment tu veux être notifié</p>
            </div>

            {/* Paramètres généraux */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-[#1E3A5F] text-sm">Paramètres généraux</h3>
              </div>
              <div className="p-4 divide-y divide-gray-100">
                <NotifItem icon={Mail} label="Notifications par email" desc="Recevoir les alertes importantes par email" notifKey="email_notifications" color="blue" />
                <NotifItem icon={Bell} label="Notifications in-app" desc="Voir les notifications dans Abracadabati" notifKey="push_notifications" color="orange" />
                <label className="flex items-center justify-between py-3 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-purple-50 text-purple-600">
                      <Clock size={18} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Mode silencieux</p>
                      <p className="text-xs text-gray-500">Pas de notifications la nuit</p>
                    </div>
                  </div>
                  <ToggleSwitch 
                    enabled={notifications.quiet_hours_enabled} 
                    onChange={(v) => updateNotification('quiet_hours_enabled', v)} 
                  />
                </label>
              </div>
            </div>

            {/* Abracadatchat */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-[#FFF8F4]">
                <h3 className="font-semibold text-[#F97316] text-sm flex items-center gap-2">
                  <MessageCircle size={16} />
                  Abracadatchat
                </h3>
              </div>
              <div className="p-4 divide-y divide-gray-100">
                <NotifItem icon={MessageCircle} label="Nouveaux messages" desc="Quand tu reçois un message" notifKey="chat_messages" color="orange" />
                <NotifItem icon={Users} label="Mentions" desc="Quand quelqu'un te mentionne" notifKey="chat_mentions" color="orange" />
                <NotifItem icon={Heart} label="Réactions" desc="Quand quelqu'un réagit à ton message" notifKey="chat_reactions" color="orange" />
              </div>
            </div>

            {/* Réseau Social */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-[#F0F4F8]">
                <h3 className="font-semibold text-[#1E3A5F] text-sm flex items-center gap-2">
                  <Globe size={16} />
                  Réseau Social
                </h3>
              </div>
              <div className="p-4 divide-y divide-gray-100">
                <NotifItem icon={UserPlus} label="Nouveaux abonnés" desc="Quand quelqu'un s'abonne à toi" notifKey="new_follower" color="blue" />
                <NotifItem icon={Users} label="Demandes d'abonnement" desc="Quand tu reçois une demande" notifKey="follow_request" color="blue" />
                <NotifItem icon={Heart} label="J'aime sur tes posts" desc="Quand quelqu'un aime ta publication" notifKey="likes_on_posts" color="blue" />
                <NotifItem icon={MessageCircle} label="Commentaires" desc="Quand quelqu'un commente" notifKey="comments_on_posts" color="blue" />
                <NotifItem icon={Users} label="Mentions" desc="Quand tu es mentionné" notifKey="mentions" color="blue" />
                <NotifItem icon={Users} label="Activité de tes amis" desc="Quand tes amis publient" notifKey="friend_activity" color="blue" />
              </div>
            </div>

            {/* Commercial (Pro uniquement) */}
            {isPro && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 bg-green-50">
                  <h3 className="font-semibold text-green-700 text-sm flex items-center gap-2">
                    <Target size={16} />
                    Commercial
                  </h3>
                </div>
                <div className="p-4 divide-y divide-gray-100">
                  <NotifItem icon={FileText} label="Demandes de devis" desc="Quand tu reçois une demande" notifKey="new_quote_request" color="green" />
                  <NotifItem icon={Check} label="Devis accepté" desc="Quand un client accepte ton devis" notifKey="quote_accepted" color="green" />
                  <NotifItem icon={X} label="Devis refusé" desc="Quand un client refuse" notifKey="quote_refused" color="green" />
                  <NotifItem icon={Euro} label="Paiement reçu" desc="Quand un paiement est effectué" notifKey="payment_received" color="green" />
                  <NotifItem icon={UserPlus} label="Nouveau prospect" desc="Quand tu as un nouveau prospect" notifKey="new_prospect" color="green" />
                </div>
              </div>
            )}

            {/* Offres & Achats (Particulier) */}
            {!isPro && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 bg-green-50">
                  <h3 className="font-semibold text-green-700 text-sm flex items-center gap-2">
                    <ShoppingBag size={16} />
                    Offres & Achats
                  </h3>
                </div>
                <div className="p-4 divide-y divide-gray-100">
                  <NotifItem icon={Receipt} label="Nouvelles offres" desc="Quand tu reçois une offre" notifKey="new_offer" color="green" />
                  <NotifItem icon={TrendingUp} label="Baisse de prix" desc="Quand un favori baisse de prix" notifKey="price_drop" color="green" />
                  <NotifItem icon={Store} label="Contact vendeur" desc="Quand un vendeur te contacte" notifKey="vendor_contact" color="green" />
                </div>
              </div>
            )}

            {/* Événements */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-purple-50">
                <h3 className="font-semibold text-purple-700 text-sm flex items-center gap-2">
                  <Calendar size={16} />
                  Événements
                </h3>
              </div>
              <div className="p-4 divide-y divide-gray-100">
                <NotifItem icon={Calendar} label="Rappels" desc="Rappels de tes événements" notifKey="event_reminder" color="purple" />
                <NotifItem icon={Calendar} label="Invitations" desc="Quand tu es invité à un événement" notifKey="event_invitation" color="purple" />
              </div>
            </div>

            {/* Système */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                  <Settings size={16} />
                  Système
                </h3>
              </div>
              <div className="p-4 divide-y divide-gray-100">
                <NotifItem icon={Shield} label="Alertes de sécurité" desc="Connexions suspectes, etc." notifKey="security_alerts" color="blue" />
                <NotifItem icon={Newspaper} label="Newsletter" desc="Actualités et conseils Abracadabati" notifKey="newsletter" color="blue" />
              </div>
            </div>
          </>
        )}

        {/* TAB SÉCURITÉ */}
        {activeTab === 'securite' && (
          <>
            <div>
              <h2 className="text-lg font-bold text-[#1E3A5F]">Sécurité</h2>
              <p className="text-gray-500 text-sm">Protège ton compte</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <button
                onClick={() => setShowPasswordModal(true)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F0F4F8] rounded-lg flex items-center justify-center">
                    <Lock size={20} className="text-[#1E3A5F]" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Changer le mot de passe</p>
                    <p className="text-sm text-gray-500">Modifie ton mot de passe actuel</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>

              <button
                onClick={() => toast.info('Authentification 2FA bientôt disponible')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F0F4F8] rounded-lg flex items-center justify-center">
                    <Shield size={20} className="text-[#1E3A5F]" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Authentification à deux facteurs</p>
                    <p className="text-sm text-gray-500">Non activée</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>

              <button
                onClick={() => toast.info('Sessions actives bientôt disponible')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F0F4F8] rounded-lg flex items-center justify-center">
                    <Eye size={20} className="text-[#1E3A5F]" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Sessions actives</p>
                    <p className="text-sm text-gray-500">Voir les appareils connectés</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>
            </div>

            {/* Déconnexion */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-between p-4 bg-red-50 hover:bg-red-100 rounded-xl transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 group-hover:bg-red-200 rounded-lg flex items-center justify-center transition-colors">
                    <LogOut size={20} className="text-red-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-red-700">Déconnexion</p>
                    <p className="text-sm text-red-500">Se déconnecter de ton compte</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-red-400" />
              </button>
            </div>

            {/* Zone dangereuse */}
            <div className="bg-red-50 rounded-xl border border-red-200 p-5">
              <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                <AlertTriangle size={18} />
                Zone dangereuse
              </h3>
              <p className="text-sm text-red-600 mb-4">
                Ces actions sont irréversibles. Procède avec prudence.
              </p>
              <button
                onClick={handleDeleteAccount}
                className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-colors"
              >
                <Trash2 size={16} />
                Supprimer mon compte
              </button>
            </div>
          </>
        )}
      </div>

      {/* Modal changement mot de passe */}
      {showPasswordModal && (
        <ChangePasswordModalInline onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
}

// Modal inline pour changer le mot de passe
function ChangePasswordModalInline({ onClose }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
      const response = await fetch(`${API_URL}/api/ecosystem/settings/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      if (!response.ok) {
        const data = await response.json();
        setError(data.detail || 'Erreur lors du changement');
        return;
      }
      toast.success('Mot de passe modifié avec succès');
      onClose();
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 md:left-[288px] z-50 flex items-center justify-center p-4 bg-black/60">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#1E3A5F]">Changer le mot de passe</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe actuel</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
              required
              minLength={8}
            />
            <p className="text-xs text-gray-500 mt-1">Minimum 8 caractères</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#F97316] focus:border-[#F97316]"
              required
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl">
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-xl disabled:opacity-50"
            >
              {loading ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB MON ESPACE - Page centrale avec accès aux outils personnels
// Design Premium épuré avec cartes élégantes
// Mobile optimisé pour Samsung S24/S25 et Pro Max
// ═══════════════════════════════════════════════════════════════════════════════
function HubMonEspace({ onSelectTool, pendingInvitationsCount = 0, isMobile = false, onOpenMenu }) {
  // Détection des grands écrans mobiles (Pro Max, Ultra)
  const [isLargeMobile, setIsLargeMobile] = useState(false);
  
  useEffect(() => {
    const checkScreenSize = () => {
      // Pro Max / Ultra : hauteur > 900px sur mobile
      const isLarge = window.innerWidth < 768 && window.innerHeight > 900;
      setIsLargeMobile(isLarge);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Définition des outils avec leurs métadonnées
  const tools = [
    { 
      id: 'taches', 
      label: 'Liste des Tâches', 
      context: 'taches_perso',
      icon: Check,
      description: 'Organise ton quotidien',
      gradient: 'from-blue-500 to-blue-600',
      bgLight: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    { 
      id: 'contacts', 
      label: 'Mes Contacts', 
      context: 'contacts',
      icon: Users,
      description: 'Gère tes contacts',
      gradient: 'from-green-500 to-green-600',
      bgLight: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    { 
      id: 'emails', 
      label: 'Mes Emails', 
      context: 'emails',
      icon: Mail,
      description: 'Gère ta boîte mail',
      gradient: 'from-violet-500 to-violet-600',
      bgLight: 'bg-violet-50',
      iconColor: 'text-violet-600'
    },
    { 
      id: 'agenda', 
      label: 'Mon Agenda', 
      context: 'agenda_perso',
      icon: Calendar,
      description: 'Planifie tes rendez-vous',
      gradient: 'from-emerald-500 to-emerald-600',
      bgLight: 'bg-emerald-50',
      iconColor: 'text-emerald-600'
    },
    { 
      id: 'visio', 
      label: 'Visioconférence', 
      context: 'visio',
      icon: Video,
      description: 'Appels vidéo',
      gradient: 'from-cyan-500 to-cyan-600',
      bgLight: 'bg-cyan-50',
      iconColor: 'text-cyan-600'
    },
    { 
      id: 'notes', 
      label: 'Mes Notes', 
      context: 'notes',
      icon: FileText,
      description: 'Prends des notes',
      gradient: 'from-amber-500 to-amber-600',
      bgLight: 'bg-amber-50',
      iconColor: 'text-amber-600'
    },
    { 
      id: 'idees', 
      label: 'Boîte à idées', 
      context: 'boite_idees',
      icon: Lightbulb,
      description: 'Capture tes inspirations',
      gradient: 'from-pink-500 to-pink-600',
      bgLight: 'bg-pink-50',
      iconColor: 'text-pink-600'
    },
    { 
      id: 'documents', 
      label: 'Mes Documents', 
      context: 'documents',
      icon: FolderOpen,
      description: 'Stocke tes fichiers',
      gradient: 'from-slate-500 to-slate-600',
      bgLight: 'bg-slate-100',
      iconColor: 'text-slate-600'
    },
  ];

  return (
    <div className={`h-full flex flex-col ${isLargeMobile ? 'justify-center' : ''}`} data-testid="hub-mon-espace">
      {/* Header - Même typographie que MesContactsPage */}
      <div className={isMobile ? (isLargeMobile ? 'mb-6' : 'mb-2') : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Menu Hamburger - Mobile uniquement */}
            {isMobile && onOpenMenu && (
              <button
                onClick={onOpenMenu}
                className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="hamburger-menu"
              >
                <Menu size={22} className="text-[#1E3A5F]" />
              </button>
            )}
            <div>
              <h1 className="text-xl font-bold text-[#1e3a5f]">Mon Espace</h1>
              {/* Sous-texte masqué sur mobile pour remonter les cartes */}
              {!isMobile && (
                <p className="text-sm text-gray-500">
                  Tous tes outils personnels au même endroit
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Grille de cartes - Optimisée mobile */}
      <div className={`${isMobile ? '' : 'flex-1 overflow-y-auto pb-4'}`}>
        <div className={`grid grid-cols-2 ${isMobile ? (isLargeMobile ? 'gap-3' : 'gap-2.5') : 'md:grid-cols-3 lg:grid-cols-4 gap-4'}`}>
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <motion.button
                key={tool.id}
                onClick={() => onSelectTool(tool.context, tool.label)}
                data-testid={`hub-card-${tool.id}`}
                className={`group relative flex flex-col items-center bg-white rounded-2xl border border-gray-100 hover:border-gray-200 transition-all duration-300 ${
                  isMobile 
                    ? 'p-3' 
                    : 'p-6 hover:shadow-xl hover:-translate-y-1'
                }`}
                whileHover={isMobile ? {} : { scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Badge si présent */}
                {tool.badge > 0 && (
                  <span className={`absolute flex items-center justify-center bg-orange-500 text-white font-bold rounded-full shadow-lg animate-pulse ${
                    isMobile 
                      ? '-top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px]' 
                      : '-top-2 -right-2 min-w-[24px] h-6 px-2 text-xs'
                  }`}>
                    {tool.badge}
                  </span>
                )}
                
                {/* Icône avec fond coloré */}
                <div className={`rounded-xl ${tool.bgLight} flex items-center justify-center ${
                  isMobile 
                    ? 'w-11 h-11 mb-2' 
                    : 'w-16 h-16 rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300'
                }`}>
                  <Icon size={isMobile ? 20 : 28} className={tool.iconColor} strokeWidth={1.5} />
                </div>
                
                {/* Label */}
                <h3 className={`font-semibold text-gray-900 text-center ${
                  isMobile ? 'text-xs leading-tight' : 'text-sm mb-1'
                }`}>
                  {tool.label}
                </h3>
                
                {/* Description - Cachée sur mobile pour gagner de l'espace */}
                {!isMobile && (
                  <p className="text-xs text-gray-500 text-center">
                    {tool.description}
                  </p>
                )}
                
                {/* Indicateur hover - Desktop seulement */}
                {!isMobile && (
                  <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-gradient-to-r ${tool.gradient} rounded-full transition-all duration-300 group-hover:w-1/2`} />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB GÉNÉRIQUE - Composant réutilisable pour tous les espaces
// ═══════════════════════════════════════════════════════════════════════════════
function HubGeneric({ title, tools, onSelectTool, isMobile = false, onOpenMenu }) {
  const [isLargeMobile, setIsLargeMobile] = useState(false);
  
  useEffect(() => {
    const checkScreenSize = () => {
      const isLarge = window.innerWidth < 768 && window.innerHeight > 900;
      setIsLargeMobile(isLarge);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return (
    <div className={`h-full flex flex-col ${isLargeMobile ? 'justify-center' : ''}`} data-testid={`hub-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={isMobile ? (isLargeMobile ? 'mb-6' : 'mb-2') : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Menu Hamburger - Mobile uniquement */}
            {isMobile && onOpenMenu && (
              <button
                onClick={onOpenMenu}
                className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="hamburger-menu"
              >
                <Menu size={22} className="text-[#1E3A5F]" />
              </button>
            )}
            <div>
              <h1 className="text-xl font-bold text-[#1e3a5f]">{title}</h1>
              {!isMobile && (
                <p className="text-sm text-gray-500">
                  Tous tes outils {title.toLowerCase()} au même endroit
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`${isMobile ? '' : 'flex-1 overflow-y-auto pb-4'}`}>
        <div className={`grid grid-cols-2 ${isMobile ? (isLargeMobile ? 'gap-3' : 'gap-2.5') : 'md:grid-cols-3 lg:grid-cols-4 gap-4'}`}>
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <motion.button
                key={tool.id}
                onClick={() => onSelectTool(tool.context, tool.label)}
                data-testid={`hub-card-${tool.id}`}
                className={`group relative flex flex-col items-center bg-white rounded-2xl border border-gray-100 hover:border-gray-200 transition-all duration-300 ${
                  isMobile ? 'p-3' : 'p-6 hover:shadow-xl hover:-translate-y-1'
                }`}
                whileHover={isMobile ? {} : { scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {tool.badge > 0 && (
                  <span className={`absolute flex items-center justify-center bg-orange-500 text-white font-bold rounded-full shadow-lg animate-pulse ${
                    isMobile ? '-top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px]' : '-top-2 -right-2 min-w-[24px] h-6 px-2 text-xs'
                  }`}>
                    {tool.badge}
                  </span>
                )}
                <div className={`rounded-xl ${tool.bgLight} flex items-center justify-center ${
                  isMobile ? 'w-11 h-11 mb-2' : 'w-16 h-16 rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300'
                }`}>
                  <Icon size={isMobile ? 20 : 28} className={tool.iconColor} strokeWidth={1.5} />
                </div>
                <h3 className={`font-semibold text-gray-900 text-center ${isMobile ? 'text-xs leading-tight' : 'text-sm mb-1'}`}>
                  {tool.label}
                </h3>
                {!isMobile && (
                  <p className="text-xs text-gray-500 text-center">{tool.description}</p>
                )}
                {!isMobile && (
                  <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-gradient-to-r ${tool.gradient} rounded-full transition-all duration-300 group-hover:w-1/2`} />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB RÉSEAU SOCIAL
// ═══════════════════════════════════════════════════════════════════════════════
function HubReseauSocial({ onSelectTool, isMobile = false, onOpenMenu }) {
  const tools = [
    { id: 'posts', label: 'Mes Publications', icon: FileText, context: 'publications', description: 'Posts', gradient: 'from-blue-500 to-blue-600', bgLight: 'bg-blue-50', iconColor: 'text-blue-600' },
    { id: 'galerie', label: 'Ma Galerie', icon: Image, context: 'galerie', description: 'Photos', gradient: 'from-purple-500 to-purple-600', bgLight: 'bg-purple-50', iconColor: 'text-purple-600' },
    { id: 'blog', label: 'Mon Blog', icon: PenTool, context: 'blog', description: 'Articles', gradient: 'from-emerald-500 to-emerald-600', bgLight: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { id: 'cercles', label: 'Groupes privés', icon: Users, context: 'cercles', description: 'Communautés', gradient: 'from-cyan-500 to-cyan-600', bgLight: 'bg-cyan-50', iconColor: 'text-cyan-600' },
    { id: 'abonnes', label: 'Mes Abonnés', icon: Users, context: 'abonnes', description: 'Followers', gradient: 'from-amber-500 to-amber-600', bgLight: 'bg-amber-50', iconColor: 'text-amber-600' },
    { id: 'abonnements', label: 'Abonnements', icon: UserPlus, context: 'abonnements', description: 'Following', gradient: 'from-pink-500 to-pink-600', bgLight: 'bg-pink-50', iconColor: 'text-pink-600' },
  ];
  return <HubGeneric title="Réseau Social" tools={tools} onSelectTool={onSelectTool} isMobile={isMobile} onOpenMenu={onOpenMenu} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB MARKETPLACE
// ═══════════════════════════════════════════════════════════════════════════════
function HubMarketplace({ onSelectTool, isMobile = false, onOpenMenu }) {
  const tools = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Home, context: 'marketplace_dashboard', description: 'Vue d\'ensemble', gradient: 'from-blue-500 to-blue-600', bgLight: 'bg-blue-50', iconColor: 'text-blue-600' },
    { id: 'annonces', label: 'Mes Annonces', icon: Package, context: 'annonces', description: 'Produits', gradient: 'from-orange-500 to-orange-600', bgLight: 'bg-orange-50', iconColor: 'text-orange-600' },
    { id: 'demandes', label: 'Mes Tickets', icon: MessageCircle, context: 'tickets_pro', description: 'Reçus', gradient: 'from-indigo-500 to-indigo-600', bgLight: 'bg-indigo-50', iconColor: 'text-indigo-600' },
    { id: 'commandes', label: 'Mes Commandes', icon: ShoppingBag, context: 'commandes', description: 'Suivi', gradient: 'from-emerald-500 to-emerald-600', bgLight: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { id: 'ventes', label: 'Mes Ventes', icon: Euro, context: 'ventes', description: 'Revenus', gradient: 'from-green-500 to-green-600', bgLight: 'bg-green-50', iconColor: 'text-green-600' },
    { id: 'achats', label: 'Mes Achats', icon: ShoppingBag, context: 'achats', description: 'Historique', gradient: 'from-violet-500 to-violet-600', bgLight: 'bg-violet-50', iconColor: 'text-violet-600' },
    { id: 'favoris', label: 'Favoris', icon: Heart, context: 'favoris', description: 'Sauvegardés', gradient: 'from-pink-500 to-pink-600', bgLight: 'bg-pink-50', iconColor: 'text-pink-600' },
    { id: 'avis', label: 'Mes Avis', icon: Star, context: 'avis', description: 'Évaluations', gradient: 'from-amber-500 to-amber-600', bgLight: 'bg-amber-50', iconColor: 'text-amber-600' },
    { id: 'vendeurs', label: 'Vendeurs suivis', icon: Users, context: 'vendeurs_suivis', description: 'Boutiques', gradient: 'from-slate-500 to-slate-600', bgLight: 'bg-slate-100', iconColor: 'text-slate-600' },
  ];
  return <HubGeneric title="Marketplace" tools={tools} onSelectTool={onSelectTool} isMobile={isMobile} onOpenMenu={onOpenMenu} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB PRESTATAIRES
// ═══════════════════════════════════════════════════════════════════════════════
function HubPrestataires({ onSelectTool, isMobile = false, onOpenMenu }) {
  const tools = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Home, context: 'artisans_dashboard', description: 'Vue d\'ensemble', gradient: 'from-slate-500 to-slate-600', bgLight: 'bg-slate-100', iconColor: 'text-slate-600' },
    { id: 'devis', label: 'Mes Demandes', icon: FileText, context: 'demandes_devis_part', description: 'Devis', gradient: 'from-orange-500 to-orange-600', bgLight: 'bg-orange-50', iconColor: 'text-orange-600' },
    { id: 'chantiers', label: 'Mes Chantiers', icon: Wrench, context: 'mes_chantiers', description: 'Projets', gradient: 'from-emerald-500 to-emerald-600', bgLight: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { id: 'favoris', label: 'Artisans favoris', icon: Bookmark, context: 'artisans_favoris', description: 'Confiance', gradient: 'from-pink-500 to-pink-600', bgLight: 'bg-pink-50', iconColor: 'text-pink-600' },
    { id: 'avis', label: 'Mes Avis', icon: Star, context: 'avis_artisans', description: 'Évaluations', gradient: 'from-amber-500 to-amber-600', bgLight: 'bg-amber-50', iconColor: 'text-amber-600' },
  ];
  return <HubGeneric title="Prestataires" tools={tools} onSelectTool={onSelectTool} isMobile={isMobile} onOpenMenu={onOpenMenu} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB EMPLOI
// ═══════════════════════════════════════════════════════════════════════════════
function HubEmploi({ onSelectTool, isMobile = false, onOpenMenu }) {
  const tools = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Home, context: 'emploi_dashboard', description: 'Vue d\'ensemble', gradient: 'from-orange-500 to-orange-600', bgLight: 'bg-orange-50', iconColor: 'text-orange-600' },
    { id: 'offres', label: 'Offres sauvées', icon: Bookmark, context: 'offres_sauvegardees', description: 'Postes', gradient: 'from-blue-500 to-blue-600', bgLight: 'bg-blue-50', iconColor: 'text-blue-600' },
    { id: 'candidatures', label: 'Candidatures', icon: FileText, context: 'candidatures', description: 'Suivi', gradient: 'from-emerald-500 to-emerald-600', bgLight: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { id: 'cv', label: 'Mon CV', icon: User, context: 'mon_cv', description: 'Profil pro', gradient: 'from-violet-500 to-violet-600', bgLight: 'bg-violet-50', iconColor: 'text-violet-600' },
    { id: 'alertes', label: 'Alertes emploi', icon: Bell, context: 'alertes_emploi', description: 'Notifications', gradient: 'from-red-500 to-red-600', bgLight: 'bg-red-50', iconColor: 'text-red-600' },
  ];
  return <HubGeneric title="Emploi" tools={tools} onSelectTool={onSelectTool} isMobile={isMobile} onOpenMenu={onOpenMenu} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB COLLABORATION (COCKPIT PRO)
// ═══════════════════════════════════════════════════════════════════════════════
function HubCollaboration({ onSelectTool, isMobile = false }) {
  const tools = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Home, context: 'collaboration_dashboard', description: 'Vue d\'ensemble', gradient: 'from-blue-500 to-blue-600', bgLight: 'bg-blue-50', iconColor: 'text-blue-600' },
    { id: 'equipe', label: 'Mon Équipe', icon: Users, context: 'equipe', description: 'Membres', gradient: 'from-emerald-500 to-emerald-600', bgLight: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { id: 'collabs', label: 'Collabs (Externes)', icon: UserPlus, context: 'collabs', description: 'Partenaires', gradient: 'from-violet-500 to-violet-600', bgLight: 'bg-violet-50', iconColor: 'text-violet-600' },
    { id: 'pointage-rh', label: 'Pointage RH', icon: Clock, context: 'pointage_rh', description: 'Heures', gradient: 'from-amber-500 to-amber-600', bgLight: 'bg-amber-50', iconColor: 'text-amber-600' },
    { id: 'pointage-manager', label: 'Pointage Manager', icon: Eye, context: 'pointage_manager', description: 'Validation', gradient: 'from-indigo-500 to-indigo-600', bgLight: 'bg-indigo-50', iconColor: 'text-indigo-600' },
    { id: 'taches', label: 'Tâches partagées', icon: Check, context: 'taches', description: 'Missions', gradient: 'from-orange-500 to-orange-600', bgLight: 'bg-orange-50', iconColor: 'text-orange-600' },
    { id: 'agenda', label: 'Agenda partagé', icon: Calendar, context: 'agenda', description: 'Planning', gradient: 'from-cyan-500 to-cyan-600', bgLight: 'bg-cyan-50', iconColor: 'text-cyan-600' },
    { id: 'tchat', label: 'Tchat équipe', icon: MessageCircle, context: 'tchat_equipe', description: 'Discussion', gradient: 'from-pink-500 to-pink-600', bgLight: 'bg-pink-50', iconColor: 'text-pink-600' },
    { id: 'feed', label: 'Feed équipe', icon: Newspaper, context: 'feed_equipe', description: 'Actualités', gradient: 'from-slate-500 to-slate-600', bgLight: 'bg-slate-100', iconColor: 'text-slate-600' },
    { id: 'documents', label: 'Documents', icon: Folder, context: 'documents', description: 'Fichiers', gradient: 'from-teal-500 to-teal-600', bgLight: 'bg-teal-50', iconColor: 'text-teal-600' },
  ];
  return <HubGeneric title="Collaboration" tools={tools} onSelectTool={onSelectTool} isMobile={isMobile} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB COMMERCIAL (COCKPIT PRO)
// ═══════════════════════════════════════════════════════════════════════════════
function HubCommercial({ onSelectTool, isMobile = false }) {
  const tools = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Home, context: 'commercial_dashboard', description: 'Vue d\'ensemble', gradient: 'from-orange-500 to-orange-600', bgLight: 'bg-orange-50', iconColor: 'text-orange-600' },
    { id: 'demandes', label: 'Demandes devis', icon: FileText, context: 'demandes_devis', description: 'Devis reçus', gradient: 'from-blue-500 to-blue-600', bgLight: 'bg-blue-50', iconColor: 'text-blue-600' },
    { id: 'prospects', label: 'Prospects', icon: UserPlus, context: 'prospects', description: 'Potentiels', gradient: 'from-emerald-500 to-emerald-600', bgLight: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { id: 'pipeline', label: 'Pipeline', icon: TrendingUp, context: 'pipeline', description: 'Suivi ventes', gradient: 'from-violet-500 to-violet-600', bgLight: 'bg-violet-50', iconColor: 'text-violet-600' },
    { id: 'clients', label: 'Clients', icon: Users, context: 'clients', description: 'Fichier', gradient: 'from-cyan-500 to-cyan-600', bgLight: 'bg-cyan-50', iconColor: 'text-cyan-600' },
    { id: 'devis', label: 'Mes Devis', icon: Receipt, context: 'devis', description: 'Envoyés', gradient: 'from-amber-500 to-amber-600', bgLight: 'bg-amber-50', iconColor: 'text-amber-600' },
    { id: 'factures', label: 'Factures', icon: Receipt, context: 'factures', description: 'Facturation', gradient: 'from-green-500 to-green-600', bgLight: 'bg-green-50', iconColor: 'text-green-600' },
    { id: 'relances', label: 'Relances', icon: Bell, context: 'relances', description: 'Rappels', gradient: 'from-red-500 to-red-600', bgLight: 'bg-red-50', iconColor: 'text-red-600' },
  ];
  return <HubGeneric title="Commercial" tools={tools} onSelectTool={onSelectTool} isMobile={isMobile} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB CATALOGUE (COCKPIT PRO)
// ═══════════════════════════════════════════════════════════════════════════════
function HubCatalogue({ onSelectTool, isMobile = false }) {
  const tools = [
    { id: 'dashboard', label: 'Vue d\'ensemble', icon: Home, context: 'page_pro_catalogue', description: 'Dashboard', gradient: 'from-blue-500 to-blue-600', bgLight: 'bg-blue-50', iconColor: 'text-blue-600' },
    { id: 'produits', label: 'Mes Produits', icon: Package, context: 'page_pro_produits', description: 'Articles', gradient: 'from-orange-500 to-orange-600', bgLight: 'bg-orange-50', iconColor: 'text-orange-600' },
    { id: 'prestations', label: 'Mes Prestations', icon: Wrench, context: 'page_pro_prestations', description: 'Services', gradient: 'from-emerald-500 to-emerald-600', bgLight: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { id: 'tarifs', label: 'Grille tarifaire', icon: Euro, context: 'page_pro_tarifs', description: 'Prix', gradient: 'from-amber-500 to-amber-600', bgLight: 'bg-amber-50', iconColor: 'text-amber-600' },
    { id: 'import', label: 'Import CSV', icon: FileText, context: 'page_pro_import', description: 'Importer', gradient: 'from-violet-500 to-violet-600', bgLight: 'bg-violet-50', iconColor: 'text-violet-600' },
  ];
  return <HubGeneric title="Catalogue" tools={tools} onSelectTool={onSelectTool} isMobile={isMobile} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB CHANTIERS (COCKPIT PRO)
// ═══════════════════════════════════════════════════════════════════════════════
function HubChantiersPro({ onSelectTool, isMobile = false }) {
  const tools = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Home, context: 'chantiers_dashboard', description: 'Vue d\'ensemble', gradient: 'from-slate-500 to-slate-600', bgLight: 'bg-slate-100', iconColor: 'text-slate-600' },
    { id: 'encours', label: 'Chantiers en cours', icon: Wrench, context: 'chantiers_en_cours', description: 'Actifs', gradient: 'from-orange-500 to-orange-600', bgLight: 'bg-orange-50', iconColor: 'text-orange-600' },
    { id: 'avenir', label: 'À venir', icon: Calendar, context: 'chantiers_a_venir', description: 'Planifiés', gradient: 'from-blue-500 to-blue-600', bgLight: 'bg-blue-50', iconColor: 'text-blue-600' },
    { id: 'termines', label: 'Terminés', icon: Check, context: 'chantiers_termines', description: 'Archivés', gradient: 'from-emerald-500 to-emerald-600', bgLight: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { id: 'planning', label: 'Planning', icon: Calendar, context: 'planning', description: 'Calendrier', gradient: 'from-cyan-500 to-cyan-600', bgLight: 'bg-cyan-50', iconColor: 'text-cyan-600' },
    { id: 'pointage', label: 'Pointage GPS', icon: Target, context: 'pointage', description: 'Géoloc', gradient: 'from-violet-500 to-violet-600', bgLight: 'bg-violet-50', iconColor: 'text-violet-600' },
  ];
  return <HubGeneric title="Chantiers" tools={tools} onSelectTool={onSelectTool} isMobile={isMobile} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB MARKETING (COCKPIT PRO)
// ═══════════════════════════════════════════════════════════════════════════════
function HubMarketing({ onSelectTool, isMobile = false }) {
  const tools = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Home, context: 'marketing_dashboard', description: 'Vue d\'ensemble', gradient: 'from-orange-500 to-orange-600', bgLight: 'bg-orange-50', iconColor: 'text-orange-600' },
    { id: 'stats', label: 'Statistiques', icon: BarChart2, context: 'stats', description: 'Analytics', gradient: 'from-blue-500 to-blue-600', bgLight: 'bg-blue-50', iconColor: 'text-blue-600' },
    { id: 'campagnes', label: 'Campagnes email', icon: Mail, context: 'campagnes_email', description: 'Emailing', gradient: 'from-emerald-500 to-emerald-600', bgLight: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { id: 'promos', label: 'Promotions', icon: Percent, context: 'promotions', description: 'Offres', gradient: 'from-pink-500 to-pink-600', bgLight: 'bg-pink-50', iconColor: 'text-pink-600' },
    { id: 'ventes-flash', label: 'Ventes Flash', icon: Zap, context: 'ventes_flash', description: 'Urgentes', gradient: 'from-red-500 to-red-600', bgLight: 'bg-red-50', iconColor: 'text-red-600' },
    { id: 'landing', label: 'Landing Pages', icon: Layout, context: 'landing_pages', description: 'Pages', gradient: 'from-violet-500 to-violet-600', bgLight: 'bg-violet-50', iconColor: 'text-violet-600' },
  ];
  return <HubGeneric title="Marketing" tools={tools} onSelectTool={onSelectTool} isMobile={isMobile} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB RÉSEAU SOCIAL PRO (COCKPIT PRO)
// ═══════════════════════════════════════════════════════════════════════════════
function HubReseauSocialPro({ onSelectTool, isMobile = false }) {
  const tools = [
    { id: 'reseau-pro', label: 'Réseau Pro', icon: Network, context: 'reseau_pro', description: 'Pros', gradient: 'from-blue-500 to-blue-600', bgLight: 'bg-blue-50', iconColor: 'text-blue-600' },
    { id: 'sous-traitance', label: 'Sous-traitance', icon: Handshake, context: 'sous_traitance', description: 'Missions', gradient: 'from-emerald-500 to-emerald-600', bgLight: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { id: 'recrutement', label: 'Recrutement', icon: Briefcase, context: 'emploi', description: 'Emploi', gradient: 'from-orange-500 to-orange-600', bgLight: 'bg-orange-50', iconColor: 'text-orange-600' },
    { id: 'visio', label: 'Visio', icon: Video, context: 'visio', description: 'Meetings', gradient: 'from-violet-500 to-violet-600', bgLight: 'bg-violet-50', iconColor: 'text-violet-600' },
  ];
  return <HubGeneric title="Réseau Social" tools={tools} onSelectTool={onSelectTool} isMobile={isMobile} />;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HUB MON ESPACE PRO (COCKPIT PRO) - Même design que Mon Espace Particulier
// ═══════════════════════════════════════════════════════════════════════════════
function HubMonEspacePro({ onSelectTool, isMobile = false }) {
  // Détection des grands écrans mobiles (Pro Max, Ultra)
  const [isLargeMobile, setIsLargeMobile] = useState(false);
  
  useEffect(() => {
    const checkScreenSize = () => {
      const isLarge = window.innerWidth < 768 && window.innerHeight > 900;
      setIsLargeMobile(isLarge);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Mêmes 8 outils que Mon Espace Particulier, mais contextes Pro
  const tools = [
    { 
      id: 'taches', 
      label: 'Liste des Tâches', 
      context: 'taches_pro_perso',
      icon: Check,
      description: 'Organise ton quotidien',
      gradient: 'from-blue-500 to-blue-600',
      bgLight: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    { 
      id: 'contacts', 
      label: 'Mes Contacts', 
      context: 'contacts_pro',
      icon: Users,
      description: 'Gère tes contacts',
      gradient: 'from-green-500 to-green-600',
      bgLight: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    { 
      id: 'emails', 
      label: 'Mes Emails', 
      context: 'emails_pro',
      icon: Mail,
      description: 'Gère ta boîte mail',
      gradient: 'from-violet-500 to-violet-600',
      bgLight: 'bg-violet-50',
      iconColor: 'text-violet-600'
    },
    { 
      id: 'agenda', 
      label: 'Mon Agenda', 
      context: 'agenda_pro_perso',
      icon: Calendar,
      description: 'Planifie tes rendez-vous',
      gradient: 'from-emerald-500 to-emerald-600',
      bgLight: 'bg-emerald-50',
      iconColor: 'text-emerald-600'
    },
    { 
      id: 'visio', 
      label: 'Visioconférence', 
      context: 'visio_pro',
      icon: Video,
      description: 'Appels vidéo',
      gradient: 'from-cyan-500 to-cyan-600',
      bgLight: 'bg-cyan-50',
      iconColor: 'text-cyan-600'
    },
    { 
      id: 'notes', 
      label: 'Mes Notes', 
      context: 'notes_pro',
      icon: FileText,
      description: 'Prends des notes',
      gradient: 'from-amber-500 to-amber-600',
      bgLight: 'bg-amber-50',
      iconColor: 'text-amber-600'
    },
    { 
      id: 'idees', 
      label: 'Boîte à idées', 
      context: 'boite_idees_pro',
      icon: Lightbulb,
      description: 'Capture tes inspirations',
      gradient: 'from-pink-500 to-pink-600',
      bgLight: 'bg-pink-50',
      iconColor: 'text-pink-600'
    },
    { 
      id: 'documents', 
      label: 'Mes Documents', 
      context: 'documents_pro',
      icon: FolderOpen,
      description: 'Stocke tes fichiers',
      gradient: 'from-slate-500 to-slate-600',
      bgLight: 'bg-slate-100',
      iconColor: 'text-slate-600'
    },
  ];

  return (
    <div className={`h-full flex flex-col ${isLargeMobile ? 'justify-center' : ''}`} data-testid="hub-mon-espace-pro">
      {/* Header - Même typographie que Mon Espace Particulier */}
      <div className={isMobile ? (isLargeMobile ? 'mb-6' : 'mb-2') : 'mb-4'}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1e3a5f]">Mon Espace</h1>
            {!isMobile && (
              <p className="text-sm text-gray-500">
                Tous tes outils personnels au même endroit
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Grille de cartes - Identique au Particulier */}
      <div className={`${isMobile ? '' : 'flex-1 overflow-y-auto pb-4'}`}>
        <div className={`grid grid-cols-2 ${isMobile ? (isLargeMobile ? 'gap-3' : 'gap-2.5') : 'md:grid-cols-3 lg:grid-cols-4 gap-4'}`}>
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <motion.button
                key={tool.id}
                onClick={() => onSelectTool(tool.context, tool.label)}
                data-testid={`hub-card-pro-${tool.id}`}
                className={`group relative flex flex-col items-center bg-white rounded-2xl border border-gray-100 hover:border-gray-200 transition-all duration-300 ${
                  isMobile 
                    ? 'p-3' 
                    : 'p-5 hover:shadow-lg'
                }`}
                whileHover={!isMobile ? { y: -2 } : {}}
                whileTap={{ scale: 0.98 }}
              >
                {/* Icône dans cercle coloré */}
                <div className={`${isMobile ? 'w-12 h-12 mb-2' : 'w-14 h-14 mb-3'} rounded-full ${tool.bgLight} flex items-center justify-center transition-transform duration-300 group-hover:scale-110`}>
                  <Icon size={isMobile ? 22 : 26} className={tool.iconColor} strokeWidth={1.8} />
                </div>
                
                {/* Label */}
                <span className={`font-semibold text-[#1e3a5f] text-center leading-tight ${isMobile ? 'text-[11px]' : 'text-sm'}`}>
                  {tool.label}
                </span>
                
                {/* Description - Desktop seulement */}
                {!isMobile && (
                  <span className="text-xs text-gray-400 mt-1 text-center">
                    {tool.description}
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export default function CockpitAssistantUnified({ userType = 'pro', contexteInitial = null, sidebarReplieeInitiale = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Visiteur non connecté : la barre du haut affiche « Se connecter / S'inscrire »
  const estConnecteAbra =
    typeof window !== 'undefined' &&
    !!(localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token') || localStorage.getItem('buyer_token'));
  const userId = getUserId();
  const userName = getUserName();
  
  // Panier
  const { cartCount } = useBuyer();
  
  // Design unifié - même couleur pour Pro et Particulier
  const isPro = userType === 'pro';
  
  // Détecter le rôle Superadmin
  const getUserRole = () => {
    try {
      const clientUser = localStorage.getItem('client_user');
      if (clientUser) {
        const parsed = JSON.parse(clientUser);
        return parsed.role || 'user';
      }
    } catch (e) {}
    // Visiteur non connecté : simple utilisateur — le menu 👑 Plateforme ne doit
    // JAMAIS apparaître pour un anonyme (cockpits ouverts à tous depuis le 11-12/06).
    // Connecté sans profil stocké : mode démo superadmin conservé (tests fondateur).
    return estConnecteAbra ? 'superadmin' : 'user';
  };
  const userRole = getUserRole();
  const isSuperAdmin = userRole === 'superadmin';
  
  // Mode Dark/Light
  let isDarkMode = false;
  let toggleTheme = () => {};
  try {
    const theme = useTheme();
    isDarkMode = theme?.isDarkMode || false;
    toggleTheme = theme?.toggleTheme || (() => {});
  } catch (e) {
    isDarkMode = localStorage.getItem('abracadabati-theme') === 'dark';
  }
  
  // Menu config - Ajouter Plateforme pour Superadmin
  const MENU_CONFIG = isPro 
    ? (isSuperAdmin ? { ...MENU_PRO, ...MENU_PLATEFORME } : MENU_PRO)
    : MENU_PARTICULIER;
  
  // État principal
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  // Menu réduit (icônes seulement) — peut démarrer replié (arrivée porte abracadabati.com)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => sidebarReplieeInitiale === true || location.state?.sidebarRepliee === true
  );
  const [showSpaceSelector, setShowSpaceSelector] = useState(false); // Dropdown sélecteur d'espace
  const [showArtifacts, setShowArtifacts] = useState(false); // Masqué par défaut
  const [artifacts, setArtifacts] = useState([]);
  const [activeArtifact, setActiveArtifact] = useState(null);
  const [isMobile, setIsMobile] = useState(false); // Détection mobile pour design ChatGPT
  
  // État UX Desktop : Outil en pleine page avec bouton flottant pour le chat
  const [isDesktopToolFullScreen, setIsDesktopToolFullScreen] = useState(false);
  const [showFloatingChat, setShowFloatingChat] = useState(false); // Panneau chat coulissant
  
  // État UX Mobile : Page dédiée en plein écran
  const [mobileFullScreenPage, setMobileFullScreenPage] = useState(null); // 'tasks', 'messages', etc.
  
  // État du menu
  const [expandedEspaces, setExpandedEspaces] = useState([]);
  // Contexte de départ : l'arrivée par abracadabati.com ouvre l'accueil Bâti intégré
  const [activeContext, setActiveContext] = useState(() => contexteInitial || location.state?.contexteInitial || null);
  const [activeContextLabel, setActiveContextLabel] = useState(null);
  const [activeEspaceLabel, setActiveEspaceLabel] = useState(null);
  
  // État du nom personnalisé de l'assistant
  const [assistantName, setAssistantNameState] = useState(getAssistantName());
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [showVoiceNotes, setShowVoiceNotes] = useState(false);
  
  // État projets
  const [projects, setProjects] = useState([
    { id: 'default', name: 'Projet par défaut', sessions: [] }
  ]);
  const [activeProjectId, setActiveProjectId] = useState('default');
  const [showProjectMenu, setShowProjectMenu] = useState(false);
  
  // État menu Conversations
  const [conversationsExpanded, setConversationsExpanded] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingSessionTitle, setEditingSessionTitle] = useState('');
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeContextRef = useRef(null);
  
  // États pour l'upload de fichiers
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  
  // État pour le modal "À venir"
  const [comingSoonModal, setComingSoonModal] = useState(null);
  
  // États pour les modales de navigation (menu horizontal)
  const [showRayonsModal, setShowRayonsModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showArtisansFiltersModal, setShowArtisansFiltersModal] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [showParticuliersFiltersModal, setShowParticuliersFiltersModal] = useState(false);
  const [showEmploiFiltersModal, setShowEmploiFiltersModal] = useState(false);
  
  // États pour les filtres (données partagées)
  const [rayons, setRayons] = useState([]);
  const [sousRayons, setSousRayons] = useState([]);
  const [marketplaceFilters, setMarketplaceFilters] = useState({});
  const [artisansFilters, setArtisansFilters] = useState({});
  const [particuliersFilters, setParticuliersFilters] = useState({});
  const [emploiFilters, setEmploiFilters] = useState({});
  
  // Toujours garder le ref à jour (pas de stale closure dans les useEffects)
  activeContextRef.current = activeContext;

  // Lire le contexte depuis sessionStorage ou query param pour initialiser automatiquement
  useEffect(() => {
    // ═══════════════════════════════════════════════════════════════════════════
    // DÉTECTION DE L'URL POUR NAVIGATION DIRECTE (NAVBAR DU BAS)
    // PRIORITÉ ABSOLUE : Cette logique doit s'exécuter À CHAQUE changement d'URL
    // pour que les boutons Espace/Marketplace/Réseau/Service/Emploi fonctionnent
    // ═══════════════════════════════════════════════════════════════════════════
    const pathMapping = {
      '/cockpit-particulier/marketplace': { context: 'hub_marketplace', label: 'Marketplace', espace: 'Marketplace' },
      '/cockpit-particulier/reseau': { context: 'hub_reseau', label: 'Réseau Social', espace: 'Réseau Social' },
      '/cockpit-particulier/artisans': { context: 'hub_prestataires', label: 'Prestataires', espace: 'Prestataires' },
      '/cockpit-particulier/emploi': { context: 'hub_emploi', label: 'Emploi', espace: 'Emploi' },
    };
    
    // Vérifier si l'URL correspond EXACTEMENT à une des pages de hub
    // (pas les sous-pages comme /cockpit-particulier/artisans/favoris)
    for (const [path, mapping] of Object.entries(pathMapping)) {
      if (location.pathname === path || location.pathname === path + '/') {
        console.log('[NAVBAR] Navigation vers hub:', path, '→', mapping.context);
        // Reset complet pour afficher le hub (identique à la navbar inline)
        setActiveArtifact(null);
        setArtifacts([]);
        setMessages([]);
        setShowArtifacts(false);
        setIsDesktopToolFullScreen(false);
        setActiveContext(mapping.context);
        setActiveContextLabel(mapping.label);
        setActiveEspaceLabel(mapping.espace);
        setShowSidebar(false); // Toujours fermer le sidebar pour voir le contenu
        return; // Sortir après avoir trouvé une correspondance EXACTE
      }
    }
    
    // Si on est sur /cockpit-particulier/messages, rediriger vers AbracadaTchat
    if (location.pathname === '/cockpit-particulier/messages') {
      console.log('[NAVBAR] Redirection vers AbracadaTchat /messages');
      navigate('/messages');
      return;
    }
    
    // Si on est sur /cockpit-particulier (racine) SANS paramètre page, afficher hub_espace
    const hasPageParam = new URLSearchParams(location.search).has('page');
    if ((location.pathname === '/cockpit-particulier' || location.pathname === '/cockpit-particulier/') && !hasPageParam) {
      // Guard : ne pas réinitialiser si une page externe est active.
      // Les composants embarqués (MarketplaceProduitsPage, EntreParticuliersPage…) appellent
      // setSearchParams() ce qui change location.search et déclencherait ce reset à tort.
      if (activeContextRef.current?.startsWith('page_')) return;
      console.log('[NAVBAR] Navigation vers hub_espace (racine)');
      // Reset complet pour afficher le hub
      setActiveArtifact(null);
      setArtifacts([]);
      setMessages([]);
      setShowArtifacts(false);
      setIsDesktopToolFullScreen(false);
      setActiveContext('hub_espace');
      setActiveContextLabel('Mon Espace');
      setActiveEspaceLabel('Mon Espace');
      setShowSidebar(false);
      return;
    }
    
    // Détecter navigation vers la liste artisans (URL directe)
    if (location.pathname === '/artisans' || location.pathname === '/artisans/') {
      // Guard : ne pas écraser un contexte page_/hub_ non-artisans déjà actif
      const cur = activeContextRef.current;
      if (cur && cur !== 'page_artisans' && cur !== 'page_artisans_5_etoiles' && cur !== 'page_artisans_carte' && cur !== 'page_decrire_projet' && (cur.startsWith('page_') || cur.startsWith('hub_'))) return;
      setActiveContext('page_artisans');
      setActiveContextLabel('Artisans');
      setShowSidebar(false);
      return;
    }

    // Détecter navigation vers le détail d'un artisan
    if (location.pathname.startsWith('/artisans/') &&
        location.pathname !== '/artisans/carte' &&
        !location.pathname.startsWith('/artisans/5-etoiles')) {
      setActiveContext('page_artisan_detail');
      setActiveContextLabel('Fiche artisan');
      setShowSidebar(false);
      return;
    }

    // NOTE: Pour les autres URLs (/cockpit-particulier/taches, etc.)
    // on ne change PAS le contexte - on laisse le contenu actuel s'afficher
    
    // Vérifier si on doit cacher le sidebar (venant de la homepage)
    const hideSidebar = sessionStorage.getItem('cockpit_hide_sidebar');
    if (hideSidebar) {
      sessionStorage.removeItem('cockpit_hide_sidebar');
      setShowSidebar(false);
    }
    
    // Vérifier s'il y a une action spéciale (depuis la homepage)
    const cockpitAction = sessionStorage.getItem('cockpit_action');
    if (cockpitAction) {
      sessionStorage.removeItem('cockpit_action');
      
      if (cockpitAction === 'open_vocalia') {
        // Ouvrir Vocalia (notes vocales)
        setTimeout(() => setShowVoiceNotes(true), 300);
      } else if (cockpitAction === 'upload_photo' || cockpitAction === 'upload_doc' || cockpitAction === 'upload_video') {
        // Ouvrir le menu d'attachments
        setTimeout(() => setShowAttachMenu(true), 300);
      }
    }
    
    // Vérifier s'il y a un message initial à envoyer
    const initialMessage = sessionStorage.getItem('cockpit_initial_message');
    if (initialMessage) {
      sessionStorage.removeItem('cockpit_initial_message');
      setInputValue(initialMessage);
      // Focus sur l'input
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    
    // D'abord vérifier sessionStorage (utilisé par le FAB)
    const storedContext = sessionStorage.getItem('cockpit_open_context');
    if (storedContext) {
      sessionStorage.removeItem('cockpit_open_context'); // Nettoyer après lecture
      
      const contextMapping = {
        'deposer': { context: 'page_deposer_annonce', label: 'Déposer une annonce', espace: 'Entre Particuliers' },
        'page_deposer_annonce': { context: 'page_deposer_annonce', label: 'Déposer une annonce', espace: 'Entre Particuliers' },
        'mon_cv': { context: 'mon_cv', label: 'Mon CV', espace: 'Emploi' },
        'alertes_emploi': { context: 'alertes_emploi', label: 'Alertes emploi', espace: 'Emploi' },
      };
      
      const mapping = contextMapping[storedContext];
      if (mapping) {
        setActiveContext(mapping.context);
        setActiveContextLabel(mapping.label);
        setActiveEspaceLabel(mapping.espace);
        return;
      }
    }
    
    // Sinon vérifier les query params
    const searchParams = new URLSearchParams(location.search);
    const contextParam = searchParams.get('context');
    const pageParam = searchParams.get('page');
    
    // Gérer le paramètre page (pour les liens directs comme Messages)
    if (pageParam) {
      const pageMapping = {
        'messagerie': { context: 'messagerie', label: 'Mes Messages', espace: 'Mon Espace' },
        'messages': { context: 'messagerie', label: 'Mes Messages', espace: 'Mon Espace' },
        'notifications': { context: 'notifications', label: 'Notifications', espace: 'Mon Espace' },
      };
      
      const mapping = pageMapping[pageParam];
      if (mapping) {
        setActiveContext(mapping.context);
        setActiveContextLabel(mapping.label);
        setActiveEspaceLabel(mapping.espace);
        setShowArtifacts(true);
        window.history.replaceState({}, '', location.pathname);
        return;
      }
    }
    
    if (contextParam) {
      const contextMapping = {
        'deposer': { context: 'page_deposer_annonce', label: 'Déposer une annonce', espace: 'Entre Particuliers' },
        'page_deposer_annonce': { context: 'page_deposer_annonce', label: 'Déposer une annonce', espace: 'Entre Particuliers' },
        'mon_cv': { context: 'mon_cv', label: 'Mon CV', espace: 'Emploi' },
        'alertes_emploi': { context: 'alertes_emploi', label: 'Alertes emploi', espace: 'Emploi' },
      };
      
      const mapping = contextMapping[contextParam];
      if (mapping) {
        setActiveContext(mapping.context);
        setActiveContextLabel(mapping.label);
        setActiveEspaceLabel(mapping.espace);
        window.history.replaceState({}, '', location.pathname);
      }
    }
  }, [location.pathname, location.search]);

  // Charger les rayons et sous-rayons au démarrage
  useEffect(() => {
    const fetchRayons = async () => {
      try {
        // Utiliser l'endpoint /full qui inclut les sous-rayons
        const res = await fetch(`${API_URL}/api/marketplace/rayons/full`);
        if (res.ok) {
          const data = await res.json();
          setRayons(data);
          
          // Extraire les sous-rayons avec leur rayon_id parent
          const allSousRayons = [];
          data.forEach(rayon => {
            if (rayon.sous_rayons && rayon.sous_rayons.length > 0) {
              rayon.sous_rayons.forEach(sr => {
                allSousRayons.push({
                  ...sr,
                  rayon_id: rayon.id
                });
              });
            }
          });
          setSousRayons(allSousRayons);
        }
      } catch (e) {
        console.error('Erreur chargement rayons:', e);
      }
    };
    fetchRayons();
  }, []);
  
  // État pour la modale d'inscription contextuelle (partagé avec les pages enfants)
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerModalAction, setRegisterModalAction] = useState('cette fonctionnalité');
  const [registerModalShowGoogle, setRegisterModalShowGoogle] = useState(false);
  
  // État pour le badge d'invitations sur "Mes Messages"
  const [pendingInvitationsCount, setPendingInvitationsCount] = useState(0);
  
  // Charger le nombre d'invitations en attente
  const loadPendingInvitations = useCallback(async () => {
    try {
      const token = localStorage.getItem('buyer_token') || localStorage.getItem('client_token') || localStorage.getItem('authToken');
      
      // Si pas de token, utiliser données de démo
      if (!token) {
        setPendingInvitationsCount(2);
        return;
      }
      
      const res = await fetch(`${API_URL}/api/messages/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setPendingInvitationsCount(data.invitations?.length || 0);
      } else {
        // Données de démo si pas d'API
        setPendingInvitationsCount(2);
      }
    } catch (error) {
      // Fallback: données de démo
      setPendingInvitationsCount(2);
    }
  }, []);
  
  // Charger les invitations au montage
  useEffect(() => {
    loadPendingInvitations();
    // Recharger toutes les 60 secondes
    const interval = setInterval(loadPendingInvitations, 60000);
    return () => clearInterval(interval);
  }, [loadPendingInvitations]);
  
  // Callback pour ouvrir la modale d'inscription depuis les pages enfants
  const openRegisterModal = useCallback((actionLabel = 'cette fonctionnalité', showGoogle = false) => {
    console.log('[CockpitAssistant] Opening register modal:', actionLabel);
    setRegisterModalAction(actionLabel);
    setRegisterModalShowGoogle(showGoogle);
    setShowRegisterModal(true);
  }, []);
  
  // ══════════════════════════════════════════════════════════════════════════════
  // HANDLER "DEMANDER À ABRA" - Gère mobile et desktop différemment
  // Sur mobile : ferme le panneau artifacts et affiche le chat principal EN PLEIN ÉCRAN
  // Sur desktop : ouvre le panneau chat coulissant
  // ══════════════════════════════════════════════════════════════════════════════
  const handleAskAbra = useCallback((context, contextLabel, message) => {
    setActiveContext(context);
    setActiveContextLabel(contextLabel);
    setMessages([{ role: 'assistant', content: message }]);
    
    if (isMobile) {
      // Mobile : fermer le panneau artifacts et revenir au chat principal EN PLEIN ÉCRAN
      setShowArtifacts(false);
      setArtifacts([]);
      setActiveArtifact(null);
      // S'assurer que le sidebar reste fermé pour afficher le chat en plein écran
      setShowSidebar(false);
      // PAS de focus automatique sur mobile - le clavier ne doit apparaître que quand l'utilisateur clique sur l'input
    } else {
      // Desktop : ouvrir le panneau chat coulissant
      setShowFloatingChat(true);
    }
  }, [isMobile]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // EFFETS
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Détection mobile et gestion responsive
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      if (mobile) {
        setSidebarCollapsed(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Ouvrir le menu si ?menu=open dans l'URL ou changer de hub
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldOpenMenu = urlParams.get('menu') === 'open';
    const hubContext = urlParams.get('hub');
    
    if (shouldOpenMenu) {
      // Attendre que isMobile soit détecté puis ouvrir le menu
      setTimeout(() => {
        setShowSidebar(true);
        // Nettoyer l'URL
        window.history.replaceState({}, '', window.location.pathname);
      }, 100);
    }
    
    // Si un hub est spécifié dans l'URL, ouvrir directement cette section
    if (hubContext) {
      const hubLabels = {
        'hub_espace': { label: 'Mon Espace', espaceLabel: 'Mon Espace' },
        'hub_marketplace': { label: 'Marketplace', espaceLabel: 'Marketplace' },
        'hub_reseau': { label: 'Réseau Social', espaceLabel: 'Réseau Social' },
        'hub_prestataires': { label: 'Prestataires', espaceLabel: 'Prestataires' },
        'hub_emploi': { label: 'Emploi', espaceLabel: 'Emploi' },
      };
      const hubInfo = hubLabels[hubContext];
      if (hubInfo) {
        setActiveContext(hubContext);
        setActiveContextLabel(hubInfo.label);
        setActiveEspaceLabel(hubInfo.espaceLabel);
        setShowSidebar(false);
      }
      // Nettoyer l'URL après un court délai pour laisser le temps de changer de contexte
      setTimeout(() => {
        window.history.replaceState({}, '', window.location.pathname);
      }, 100);
    }
  }, [location.search]);
  
  // Fermer le dropdown sélecteur d'espace quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSpaceSelector && !e.target.closest('[data-space-selector]')) {
        setShowSpaceSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSpaceSelector]);
  
  // Réinitialiser les sous-menus quand on ferme la sidebar
  useEffect(() => {
    if (!showSidebar) {
      setExpandedEspaces([]);
      setConversationsExpanded(false);
    }
  }, [showSidebar]);
  
  // Masquer le navbar du bas quand le menu hamburger est ouvert sur mobile
  useEffect(() => {
    if (isMobile && showSidebar) {
      document.body.classList.add('modal-fullscreen-open');
    } else {
      document.body.classList.remove('modal-fullscreen-open');
    }
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
    };
  }, [showSidebar, isMobile]);
  
  // Ouvrir le menu si on vient du switch d'espace
  useEffect(() => {
    if (location.state?.keepMenuOpen) {
      setShowSidebar(true);
      // Nettoyer le state pour éviter de réouvrir le menu au refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Charger les sessions
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
        const res = await fetch(`${API_URL}/api/ai/sessions/${userId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (res.ok) {
          const data = await res.json();
          setSessions(data.sessions || []);
        }
      } catch (e) {
        console.error('Erreur chargement sessions:', e);
      }
    };
    if (userId) fetchSessions();
  }, [userId]);
  
  // Renommer une session
  const handleRenameSession = async (sessionId, newTitle) => {
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
      const res = await fetch(`${API_URL}/api/ai/session/${sessionId}/rename`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ title: newTitle })
      });
      if (res.ok) {
        setSessions(prev => prev.map(s => 
          s.session_id === sessionId ? { ...s, title: newTitle } : s
        ));
        toast.success('Conversation renommée');
      }
    } catch (e) {
      console.error('Erreur renommage session:', e);
      toast.error('Erreur lors du renommage');
    }
    setEditingSessionId(null);
  };
  
  // Supprimer une session
  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Supprimer cette conversation ?')) return;
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
      const res = await fetch(`${API_URL}/api/ai/session/${sessionId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.session_id !== sessionId));
        if (currentSessionId === sessionId) {
          handleNewConversation();
        }
        toast.success('Conversation supprimée');
      }
    } catch (e) {
      console.error('Erreur suppression session:', e);
      toast.error('Erreur lors de la suppression');
    }
  };
  
  // Scroll auto
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const toggleEspace = (espaceId) => {
    setExpandedEspaces(prev => 
      prev.includes(espaceId) 
        ? prev.filter(id => id !== espaceId)
        : [...prev, espaceId]
    );
  };
  
  // Fonction pour charger les données selon le contexte
  const loadContextData = async (context, label) => {
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
    
    // Cas spécial pour Paramètres - afficher directement le panneau
    if (context === 'parametres') {
      const parametresArtifact = {
        id: `parametres-${Date.now()}`,
        type: 'parametres',
        title: 'Paramètres',
        data: { userId }
      };
      setArtifacts([parametresArtifact]);
      setActiveArtifact(parametresArtifact);
      setShowArtifacts(true);
      return null;
    }
    
    // Mapping complet des contextes vers les endpoints API
    const contextEndpoints = {
      // ══════════════════════════════════════════════════════════
      // COCKPIT PRO - Commercial
      // ══════════════════════════════════════════════════════════
      'commercial_dashboard': { endpoint: '/api/commercial/dashboard', type: 'dashboard' },
      'demandes_devis': { endpoint: '/api/devis/requests', type: 'devis' },
      'prospects': { endpoint: '/api/crm/prospects', type: 'prospects' },
      'pipeline': { endpoint: '/api/crm/pipeline', type: 'pipeline' },
      'clients': { endpoint: '/api/clients', type: 'clients' },
      'devis': { endpoint: '/api/devis/user/me', type: 'devis' },
      'factures': { endpoint: '/api/devis/factures', type: 'factures' },
      'relances': { endpoint: '/api/devis?status=pending&needs_followup=true', type: 'devis' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PRO - Collaboration
      // ══════════════════════════════════════════════════════════
      'collaboration_dashboard': { endpoint: '/api/team/dashboard', type: 'dashboard' },
      'equipe': { endpoint: '/api/team/members', type: 'team' },
      'collabs': { endpoint: '/api/collabs', type: 'collabs' },
      'taches': { endpoint: '/api/tasks', type: 'tasks' },
      'agenda': { endpoint: '/api/calendar/events', type: 'events' },
      'tchat_equipe': { endpoint: '/api/team/chat/conversations', type: 'messages' },
      'feed_equipe': { endpoint: '/api/team/feed', type: 'feed' },
      'documents': { endpoint: '/api/documents', type: 'documents' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PRO - Catalogue
      // ══════════════════════════════════════════════════════════
      'catalogue_dashboard': { endpoint: '/api/cockpit/products/stats', type: 'dashboard' },
      'produits': { endpoint: '/api/cockpit/products', type: 'products' },
      'prestations': { endpoint: '/api/cockpit/prestations', type: 'products' },
      'tarifs': { endpoint: '/api/cockpit/tarifs', type: 'tarifs' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PRO - Chantiers
      // ══════════════════════════════════════════════════════════
      'chantiers_dashboard': { endpoint: '/api/batiment/chantiers/stats', type: 'dashboard' },
      'chantiers_en_cours': { endpoint: '/api/batiment/chantiers?status=in_progress', type: 'chantiers' },
      'chantiers_a_venir': { endpoint: '/api/batiment/chantiers?status=upcoming', type: 'chantiers' },
      'chantiers_termines': { endpoint: '/api/batiment/chantiers?status=completed', type: 'chantiers' },
      'planning': { endpoint: '/api/batiment/planning', type: 'events' },
      'pointage': { endpoint: '/api/pointage/today', type: 'pointage' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PRO - Marketing
      // ══════════════════════════════════════════════════════════
      'marketing_dashboard': { endpoint: '/api/marketing/stats', type: 'dashboard' },
      'stats': { endpoint: '/api/marketing/analytics', type: 'stats' },
      'campagnes_email': { endpoint: '/api/marketing/campaigns', type: 'campaigns' },
      'promotions': { endpoint: '/api/marketing/promotions', type: 'promotions' },
      'ventes_flash': { endpoint: '/api/flash-sales', type: 'flash_sales' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PRO - Réseau Social & Pro
      // ══════════════════════════════════════════════════════════
      'feed_social': { endpoint: '/api/social/feed', type: 'feed' },
      'publications': { endpoint: '/api/social/my-posts', type: 'posts' },
      'galerie': { endpoint: '/api/social/gallery', type: 'gallery' },
      'abonnes': { endpoint: '/api/social/followers', type: 'users' },
      'messages_social': { endpoint: '/api/social/conversations', type: 'messages' },
      'feed_pro': { endpoint: '/api/reseau-pro/feed', type: 'feed' },
      'annuaire': { endpoint: '/api/reseau-pro/annuaire', type: 'annuaire' },
      'partenaires': { endpoint: '/api/reseau-pro/partners', type: 'users' },
      'opportunites': { endpoint: '/api/reseau-pro/opportunities', type: 'opportunities' },
      'evenements_pro': { endpoint: '/api/reseau-pro/events', type: 'events' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PRO - Emploi
      // ══════════════════════════════════════════════════════════
      'recherche_emploi': { endpoint: '/api/jobs/search', type: 'jobs' },
      'offres_emploi': { endpoint: '/api/jobs/my-offers', type: 'jobs' },
      'candidatures_recues': { endpoint: '/api/jobs/applications/received', type: 'applications' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PRO - Boutique
      // ══════════════════════════════════════════════════════════
      'boutique_apercu': { endpoint: '/api/cockpit/boutique', type: 'boutique' },
      'boutique_infos': { endpoint: '/api/cockpit/boutique/info', type: 'boutique_info' },
      'boutique_visuels': { endpoint: '/api/cockpit/boutique/visuals', type: 'visuals' },
      'boutique_apropos': { endpoint: '/api/cockpit/boutique/about', type: 'text' },
      'mentions_devis': { endpoint: '/api/cockpit/boutique/mentions', type: 'text' },
      'cgv': { endpoint: '/api/cockpit/boutique/cgv', type: 'text' },
      'horaires': { endpoint: '/api/cockpit/boutique/horaires', type: 'horaires' },
      'blog': { endpoint: '/api/cockpit/boutique/blog', type: 'posts' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PRO - Administration
      // ══════════════════════════════════════════════════════════
      'mon_compte': { endpoint: '/api/ecosystem/settings/me', type: 'settings' },
      'abonnement': { endpoint: '/api/subscription/current', type: 'subscription' },
      'facturation_admin': { endpoint: '/api/billing/invoices', type: 'factures' },
      'securite': { endpoint: '/api/security/settings', type: 'security' },
      'notifications_settings': { endpoint: '/api/notifications/preferences', type: 'notifications' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Réseau Social
      // ══════════════════════════════════════════════════════════
      'feed': { endpoint: '/api/social/feed', type: 'feed' },
      'notifications': { endpoint: '/api/notifications', type: 'notifications_list' },
      'cercles': { endpoint: '/api/social/circles', type: 'circles' },
      'cercles_feed': { endpoint: '/api/social/circles/feed', type: 'feed' },
      'evenements': { endpoint: '/api/social/events', type: 'events' },
      'salons': { endpoint: '/api/social/salons', type: 'salons' },
      'abonnements': { endpoint: '/api/social/following', type: 'users' },
      'messages_prives': { endpoint: '/api/conversations', type: 'messages' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Marketplace
      // ══════════════════════════════════════════════════════════
      'marketplace_dashboard': { endpoint: '/api/marketplace/dashboard', type: 'dashboard' },
      'annonces': { endpoint: '/api/marketplace/my-listings', type: 'products' },
      'commandes': { endpoint: '/api/marketplace/orders', type: 'orders' },
      'ventes': { endpoint: '/api/marketplace/sales', type: 'orders' },
      'achats': { endpoint: '/api/marketplace/purchases', type: 'orders' },
      'messages_marketplace': { endpoint: '/api/marketplace/conversations', type: 'messages' },
      'favoris': { endpoint: '/api/marketplace/favorites', type: 'products' },
      'avis': { endpoint: '/api/marketplace/reviews', type: 'reviews' },
      'vendeurs_suivis': { endpoint: '/api/marketplace/followed-sellers', type: 'users' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Artisans/Prestataires
      // ══════════════════════════════════════════════════════════
      'artisans_dashboard': { endpoint: '/api/artisans/dashboard', type: 'dashboard' },
      'recherche_artisan': { endpoint: '/api/artisans/search', type: 'artisans' },
      'demandes_devis_part': { endpoint: '/api/project-requests/my', type: 'project_requests' },
      'mes_chantiers': { endpoint: '/api/client/chantiers', type: 'chantiers' },
      'artisans_favoris': { endpoint: '/api/artisans/favorites', type: 'artisans' },
      'avis_artisans': { endpoint: '/api/artisans/my-reviews', type: 'reviews' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Emploi
      // ══════════════════════════════════════════════════════════
      'emploi_dashboard': { endpoint: '/api/jobs/dashboard', type: 'dashboard' },
      'offres_sauvegardees': { endpoint: '/api/jobs/saved', type: 'jobs' },
      'candidatures': { endpoint: '/api/jobs/applications/my', type: 'applications' },
      'mon_cv': { endpoint: '/api/jobs/cv', type: 'cv' },
      'alertes_emploi': { endpoint: '/api/job-alerts', type: 'alerts' },
      // Nouveaux contextes Emploi
      'recherche_emploi_public': { endpoint: '/api/jobs/search', type: 'jobs' },
      'offres_btp': { endpoint: '/api/jobs', type: 'jobs' },
      'entreprises_recrutent': { endpoint: '/api/jobs/employers', type: 'employers' },
      'deposer_cv': { endpoint: '/api/jobs/cv', type: 'cv' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Outils
      // ══════════════════════════════════════════════════════════
      'taches_perso': { endpoint: '/api/tasks/personal', type: 'tasks' },
      'messagerie': { endpoint: '/api/conversations', type: 'messages' },
      'emails': { endpoint: '/api/emails', type: 'emails' },
      'notes': { endpoint: '/api/notes', type: 'notes' },
      'agenda_perso': { endpoint: '/api/calendar/events', type: 'events' },
      'visio': { endpoint: '/api/visio/meetings', type: 'meetings' },
      
      // ══════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Contacts
      // ══════════════════════════════════════════════════════════
      'contacts_tous': { endpoint: '/api/ecosystem/contacts/{userId}', type: 'contacts', dynamic: true },
      'contacts_recents': { endpoint: '/api/ecosystem/contacts/{userId}', type: 'contacts', dynamic: true, filter: 'recent' },
      'contacts_favoris': { endpoint: '/api/ecosystem/contacts/{userId}', type: 'contacts', dynamic: true, filter: 'favorites' },
      'contacts_historique': { endpoint: '/api/ecosystem/contacts/{userId}', type: 'contacts_history', dynamic: true },
    };
    
    const config = contextEndpoints[context];
    
    // ═══════════════════════════════════════════════════════════════════════════
    // MAPPING CONTEXTE → COMPOSANT PAGE
    // Chaque contexte de menu est mappé vers son composant React existant
    // ═══════════════════════════════════════════════════════════════════════════
    const contextPageComponents = {
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Collaboration
      // ══════════════════════════════════════════════════════════════════════
      'collaboration_dashboard': 'CockpitProDashboard',
      'equipe': 'EquipeProPage',
      'collabs': 'CollabPage',
      'pointage_rh': 'TimeClockPage',
      'pointage_manager': 'TimeClockManagerPage',
      'taches': 'TachesCollabProPage',
      'agenda': 'AgendaProPage',
      'tchat_equipe': 'TchatProPage',
      'feed_equipe': 'FeedProPage',
      'documents': 'DocumentsProPage',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Commercial
      // ══════════════════════════════════════════════════════════════════════
      'commercial_dashboard': 'CockpitProDashboard',
      'demandes_devis': 'DemandesDevisPage',
      // prospects, pipeline, clients, devis, factures, relances utilisent les DataArtifacts
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Catalogue
      // ══════════════════════════════════════════════════════════════════════
      'catalogue_dashboard': 'CockpitProDashboard',
      // produits, prestations, tarifs utilisent les DataArtifacts
      'import_csv': 'ImportCSVPage',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Chantiers
      // ══════════════════════════════════════════════════════════════════════
      'chantiers_dashboard': 'CockpitProDashboard',
      // chantiers_en_cours, chantiers_a_venir, chantiers_termines utilisent DataArtifacts
      'planning': 'AgendaProPage',
      // pointage utilise DataArtifact
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Marketing
      // ══════════════════════════════════════════════════════════════════════
      'marketing_dashboard': 'CockpitProDashboard',
      // stats, campagnes_email, promotions, ventes_flash utilisent DataArtifacts
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Réseau Social
      // ══════════════════════════════════════════════════════════════════════
      'feed_social': 'FeedReseau',
      'publications': 'MesPublications',
      'galerie': 'MaGalerie',
      'abonnes': 'MesAbonnes',
      'abonnements': 'MesAbonnements',
      'messages_social': 'MessagerieProPage',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Réseau Pro
      // ══════════════════════════════════════════════════════════════════════
      'feed_pro': 'ReseauProPage',
      'annuaire': 'ReseauProPage',
      'partenaires': 'ReseauProPage',
      'opportunites': 'ReseauProPage',
      'evenements_pro': 'ReseauProPage',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Emploi
      // ══════════════════════════════════════════════════════════════════════
      'recherche_emploi': 'EmploiProPage',
      'offres_emploi': 'EmploiProPage',
      'candidatures_recues': 'EmploiProPage',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Boutique
      // ══════════════════════════════════════════════════════════════════════
      'boutique_apercu': 'MesOptionsPage',
      'boutique_infos': 'MesOptionsPage',
      'boutique_visuels': 'MesOptionsPage',
      'boutique_apropos': 'MesOptionsPage',
      'mentions_devis': 'MesOptionsPage',
      'cgv': 'MesOptionsPage',
      'horaires': 'MesOptionsPage',
      'blog': 'MesOptionsPage',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Administration
      // ══════════════════════════════════════════════════════════════════════
      'mon_compte': 'MesOptionsPage',
      'abonnement': 'MesOptionsPage',
      'facturation_admin': 'MesOptionsPage',
      'securite': 'MesOptionsPage',
      'notifications_settings': 'MesOptionsPage',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Plateforme (Superadmin uniquement)
      // ══════════════════════════════════════════════════════════════════════
      'plateforme_dashboard': 'PlateformeDashboardPage',
      'plateforme_cercle_confiance': 'CercleConfiancePage',
      'plateforme_abonnements': 'PlateformeAbonnementsPage',
      'plateforme_vendeurs': 'PlateformeDashboardPage',
      'plateforme_utilisateurs': 'PlateformeDashboardPage',
      'plateforme_coins': 'PlateformeDashboardPage',
      'plateforme_promos': 'PlateformeDashboardPage',
      'plateforme_analytics': 'PlateformeDashboardPage',
      'plateforme_moderation': 'PlateformeDashboardPage',
      'plateforme_tickets': 'PlateformeDashboardPage',
      'plateforme_factures': 'PlateformeDashboardPage',
      'plateforme_parametres': 'PlateformeDashboardPage',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Réseau Social
      // ══════════════════════════════════════════════════════════════════════
      'feed': 'FeedReseau',
      'notifications': 'NotificationsPage',
      'cercles': 'CerclesPage',
      'cercles_feed': 'GroupesPage',
      'blog': 'BlogPage',
      'evenements': 'EvenementsPage',
      'salons': 'SalonsPage',
      'abonnements': 'MesAbonnements',
      'messages_prives': 'MessagesReseau',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Marketplace
      // ══════════════════════════════════════════════════════════════════════
      'marketplace_dashboard': 'DashboardMarketplace',
      'annonces': 'MesAnnoncesPage',
      'commandes': 'CommandesPage',
      'ventes': 'VentesPage',
      'achats': 'AchatsPage',
      'messages_marketplace': 'MessagerieParticulierPage',
      'favoris': 'FavorisMarketplacePage',
      'avis': 'AvisMarketplacePage',
      'vendeurs_suivis': 'VendeursSuivisPage',
      'tickets_pro': 'TicketsProPage',
      'deposer': 'DeposerAnnoncePage',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Artisans/Prestataires
      // ══════════════════════════════════════════════════════════════════════
      'artisans_dashboard': 'DashboardArtisans',
      'recherche_artisan': 'RechercheArtisans',
      'demandes_devis_part': 'DevisParticulierPage',
      'mes_chantiers': 'ChantiersParticulierPage',
      'artisans_favoris': 'ArtisansFavoris',
      'avis_artisans': 'AvisArtisans',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Emploi
      // ══════════════════════════════════════════════════════════════════════
      'emploi_dashboard': 'DashboardEmploi',
      'offres_sauvegardees': 'OffresEmploiPage',
      'candidatures': 'CandidaturesPage',
      'mon_cv': 'CVPage',
      'alertes_emploi': 'AlertesEmploiPage',
      // Nouveaux contextes Emploi
      'recherche_emploi_public': 'RechercheEmploiPage',
      'offres_btp': 'OffresBTPPage',
      'entreprises_recrutent': 'EntreprisesRecrutentPage',
      'deposer_cv': 'DeposerCVPage',
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PARTICULIER - Outils
      // ══════════════════════════════════════════════════════════════════════
      'hub_espace': 'HubMonEspace', // Hub Mon Espace
      'hub_reseau': 'HubReseauSocial', // Hub Réseau Social
      'hub_marketplace': 'HubMarketplace', // Hub Marketplace
      'hub_prestataires': 'HubPrestataires', // Hub Prestataires
      'hub_emploi': 'HubEmploi', // Hub Emploi
      
      // ══════════════════════════════════════════════════════════════════════
      // COCKPIT PRO - Hubs
      // ══════════════════════════════════════════════════════════════════════
      'hub_espace_pro': 'HubMonEspacePro', // Hub Mon Espace Pro
      'hub_collaboration': 'HubCollaboration', // Hub Collaboration
      'hub_commercial': 'HubCommercial', // Hub Commercial
      'hub_catalogue': 'HubCatalogue', // Hub Catalogue
      'hub_chantiers_pro': 'HubChantiersPro', // Hub Chantiers Pro
      'hub_marketing': 'HubMarketing', // Hub Marketing
      'hub_reseau_pro': 'HubReseauSocialPro', // Hub Réseau Social Pro
      
      'taches_perso': 'TachesKanbanPage',
      'messagerie': 'MessagerieParticulierPage',
      'emails': 'MailsParticulierPage',
      'agenda_perso': 'AgendaParticulierPage',
      'visio': 'VisioconferencePage',
      'notes': 'MesNotesPage',
      'boite_idees': 'BoiteAIdeesPage',
      'documents': 'DocumentsParticulierPage',
      
      // ══════════════════════════════════════════════════════════════════════
      // MON ESPACE PRO - Mêmes pages que Particulier, contexte Pro
      // ══════════════════════════════════════════════════════════════════════
      'taches_pro_perso': 'TachesKanbanPage',
      'contacts_pro': 'MesContactsPage',
      'emails_pro': 'MailsParticulierPage',
      'agenda_pro_perso': 'AgendaParticulierPage',
      'visio_pro': 'VisioconferencePage',
      'notes_pro': 'MesNotesPage',
      'boite_idees_pro': 'BoiteAIdeesPage',
      'documents_pro': 'DocumentsParticulierPage',
    };
    
    // Si ce contexte a une page dédiée, l'afficher
    if (contextPageComponents[context]) {
      const pageArtifact = {
        id: `page-${context}-${Date.now()}`,
        type: 'page_component',
        componentName: contextPageComponents[context],
        title: label,
        context: context
      };
      setArtifacts([pageArtifact]);
      setActiveArtifact(pageArtifact);
      setShowArtifacts(true);
      return { hasPage: true };
    }
    
    // Sinon, essayer de charger les données via API
    if (config) {
      try {
        // Construire l'URL (remplacer {userId} si présent)
        let endpoint = config.endpoint;
        if (config.dynamic && endpoint.includes('{userId}')) {
          endpoint = endpoint.replace('{userId}', userId);
        }
        
        const res = await fetch(`${API_URL}${endpoint}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (res.ok) {
          const data = await res.json();
          
          // Vérifier si on a des vraies données (pas un tableau vide, pas un objet vide)
          const hasRealData = data && (
            (Array.isArray(data) && data.length > 0) ||
            (Array.isArray(data?.items) && data.items.length > 0) ||
            (typeof data === 'object' && Object.keys(data).length > 0 && !data.detail)
          );
          
          if (hasRealData) {
            // Créer un artifact avec les données chargées
            const dataArtifact = {
              id: `context-${context}-${Date.now()}`,
              type: config.type,
              title: label,
              context: context,
              data: data
            };
            
            setArtifacts([dataArtifact]);
            setActiveArtifact(dataArtifact);
            setShowArtifacts(true);
            return data;
          }
        }
      } catch (e) {
        console.log(`Données ${context} non disponibles:`, e.message);
      }
    }
    
    // Si pas de page ni de données, afficher un placeholder contextuel
    const placeholderArtifact = {
      id: `placeholder-${context}-${Date.now()}`,
      type: 'context_placeholder',
      title: label,
      context: context,
      data: {
        context,
        message: `Section "${label}" - Aucune donnée pour le moment.`,
        suggestions: getSuggestionsForContext(context)
      }
    };
    setArtifacts([placeholderArtifact]);
    setActiveArtifact(placeholderArtifact);
    setShowArtifacts(true);
    
    return null;
  };
  
  // Suggestions contextuelles
  const getSuggestionsForContext = (context) => {
    const suggestions = {
      'devis': ['Crée un nouveau devis', 'Montre les devis en attente', 'Quel est mon CA du mois ?'],
      'relances': ['Quelles relances sont urgentes ?', 'Relance M. Dupont', 'Envoie un rappel groupé'],
      'taches': ['Crée une nouvelle tâche', 'Quelles tâches sont urgentes ?', 'Montre les tâches de l\'équipe'],
      'equipe': ['Qui est disponible demain ?', 'Envoie un message à l\'équipe', 'Planifie une réunion'],
      'pointage_rh': ['Pointer mon entrée', 'Voir mon historique de pointage', 'Déclarer une absence'],
      'pointage_manager': ['Qui est absent aujourd\'hui ?', 'Voir les retards', 'Exporter le rapport du mois'],
      'messagerie': ['Montre mes messages non lus', 'Réponds à Marie', 'Envoie un message à Jean'],
      'emails': ['Montre mes emails non lus', 'Rédige un email de relance', 'Réponds au dernier email'],
      'blog_article': [
        'Rédige tout l\'article pour moi',
        'Trouve-moi un titre accrocheur',
        'Écris le résumé / accroche',
        'Suggère-moi des tags pertinents',
        'Optimise le SEO (titre + description)',
        'Améliore mon texte existant'
      ],
      'publications': ['Crée une nouvelle publication', 'Quelles publications ont le plus d\'engagement ?', 'Aide-moi à écrire une légende'],
    };
    return suggestions[context] || ['Que veux-tu faire ?', 'Pose-moi une question', 'Demande-moi de l\'aide'];
  };
  
  const handleSelectContext = async (context, label, espaceLabel) => {
    setActiveContext(context);
    setActiveContextLabel(label);
    setActiveEspaceLabel(espaceLabel);
    
    // ═══════════════════════════════════════════════════════════════════════════
    // HUB PAGES : Afficher dans la zone centrale SANS mode pleine page
    // Garde la barre de saisie visible
    // ═══════════════════════════════════════════════════════════════════════════
    const isHubContext = context?.startsWith('hub_');
    
    if (isHubContext) {
      // Pour les hubs : fermer le panneau artifacts et désactiver le mode pleine page
      setShowArtifacts(false);
      setIsDesktopToolFullScreen(false);
      setMessages([]); // Reset messages pour afficher le Hub
      if (isMobile) {
        setShowSidebar(false); // Fermer le menu mobile
      }
      return; // Stop ici, le Hub sera affiché dans la zone centrale
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // PAGES EXTERNES : Navigation horizontale (Artisans, Produits, Réseau, etc.)
    // Desktop : Active le mode pleine page (masque le chat, affiche bouton flottant)
    // Mobile : Affiche dans la zone centrale
    // ═══════════════════════════════════════════════════════════════════════════
    const isPageContext = context?.startsWith('page_');
    
    if (isPageContext) {
      // Fermer le panneau artifacts
      setShowArtifacts(false);
      setMessages([]); // Reset messages
      
      // Desktop : activer le mode pleine page (chat masqué, bouton flottant visible)
      if (!isMobile) {
        setIsDesktopToolFullScreen(true);
        setShowFloatingChat(false);
      }
      
      if (isMobile) {
        setShowSidebar(false); // Fermer le menu mobile
      }
      return; // Stop ici, la page sera affichée dans la zone centrale
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // UX Mobile : Ouvrir la page dédiée en plein écran
    // ═══════════════════════════════════════════════════════════════════════════
    if (isMobile) {
      // Mapper le contexte vers la page mobile correspondante
      const mobilePageMapping = {
        'taches_perso': 'tasks',
        'liste_taches': 'tasks',
        // Ajouter d'autres mappings ici au fur et à mesure
      };
      
      if (mobilePageMapping[context]) {
        setMobileFullScreenPage(mobilePageMapping[context]);
        setShowSidebar(false); // Fermer le menu
        return; // Ne pas continuer avec la logique desktop
      }
    }
    
    // ═══════════════════════════════════════════════════════════════════════════
    // UX Desktop : Activer le mode pleine page pour l'outil
    // ═══════════════════════════════════════════════════════════════════════════
    if (!isMobile) {
      setIsDesktopToolFullScreen(true);
      setShowFloatingChat(false); // Fermer le chat flottant si ouvert
    }
    
    // Charger les données correspondantes (ouvrira le panneau droit seulement si données réelles)
    const hasData = await loadContextData(context, label);
    
    // Obtenir les suggestions pour ce contexte
    const suggestions = getSuggestionsForContext(context);
    
    // Créer le message système avec suggestions intégrées
    let messageContent = `Tu es maintenant dans **${espaceLabel} > ${label}**.\n\n`;
    
    if (!hasData) {
      // Pas de données = on affiche les suggestions dans le chat
      messageContent += `Je peux t'aider avec cette section. Voici quelques idées :\n`;
      suggestions.forEach(s => {
        messageContent += `• ${s}\n`;
      });
      messageContent += `\nQue veux-tu faire ?`;
    } else {
      messageContent += `Les données sont affichées à droite. Comment puis-je t'aider ?`;
    }
    
    const contextMessage = {
      role: 'system',
      content: messageContent,
      timestamp: new Date().toISOString(),
      suggestions: !hasData ? suggestions : null // Pour les rendre cliquables
    };
    
    // Réinitialiser les messages avec le nouveau contexte
    setMessages([contextMessage]);
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS UPLOAD DE FICHIERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Mode visiteur : envoyer un fichier à Abra demande un compte
    if (!estConnecteAbra) {
      if (e.target) e.target.value = '';
      setShowAttachMenu(false);
      openRegisterModal('l\'envoi de documents à Abra', true);
      return;
    }

    setIsUploading(true);
    setShowAttachMenu(false);
    
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
    
    for (const file of files) {
      try {
        // Vérifier le type de fichier
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        const isPDF = file.type === 'application/pdf';
        const isDocument = file.type.includes('document') || file.type.includes('word') || 
                          file.type.includes('spreadsheet') || file.type.includes('excel');
        
        // Upload le fichier
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadRes = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          
          // Ajouter le fichier à la liste
          const newFile = {
            id: uploadData.id || `file-${Date.now()}`,
            name: file.name,
            url: uploadData.url || uploadData.image_url,
            thumbnailUrl: uploadData.thumbnail_url,
            type: isImage ? 'image' : isVideo ? 'video' : isPDF ? 'pdf' : 'document',
            mimeType: file.type,
            size: file.size,
          };
          
          setUploadedFiles(prev => [...prev, newFile]);
          
          // Déterminer le contexte du fichier et envoyer un message automatique
          let analysisPrompt = `J'ai uploadé un fichier : "${file.name}"`;
          
          if (isImage) {
            // Vérifier si c'est potentiellement un devis/facture
            const fileName = file.name.toLowerCase();
            if (fileName.includes('devis')) {
              analysisPrompt = `J'ai uploadé une image d'un devis : "${file.name}". Peux-tu l'analyser ?`;
            } else if (fileName.includes('facture') || fileName.includes('invoice')) {
              analysisPrompt = `J'ai uploadé une image d'une facture : "${file.name}". Peux-tu l'analyser ?`;
            } else if (fileName.includes('chantier') || fileName.includes('travaux')) {
              analysisPrompt = `J'ai uploadé une photo de chantier : "${file.name}". Que vois-tu ?`;
            } else {
              analysisPrompt = `J'ai uploadé cette image : "${file.name}". Peux-tu me dire ce que tu vois ?`;
            }
          } else if (isPDF) {
            const fileName = file.name.toLowerCase();
            if (fileName.includes('devis')) {
              analysisPrompt = `J'ai uploadé un devis PDF : "${file.name}". Peux-tu l'analyser et me faire un résumé ?`;
            } else if (fileName.includes('facture') || fileName.includes('invoice')) {
              analysisPrompt = `J'ai uploadé une facture PDF : "${file.name}". Peux-tu l'analyser ?`;
            } else if (fileName.includes('cgv') || fileName.includes('conditions')) {
              analysisPrompt = `J'ai uploadé un document de CGV : "${file.name}". Peux-tu me résumer les points importants ?`;
            } else if (fileName.includes('contrat') || fileName.includes('contract')) {
              analysisPrompt = `J'ai uploadé un contrat : "${file.name}". Peux-tu m'en faire un résumé ?`;
            } else {
              analysisPrompt = `J'ai uploadé ce document PDF : "${file.name}". Peux-tu l'analyser ?`;
            }
          } else if (isVideo) {
            analysisPrompt = `J'ai uploadé une vidéo : "${file.name}". C'est une vidéo de chantier/travaux.`;
          }
          
          // Ajouter message utilisateur avec l'image
          const userMessage = {
            role: 'user',
            content: analysisPrompt,
            timestamp: new Date().toISOString(),
            attachments: [newFile]
          };
          
          setMessages(prev => [...prev, userMessage]);
          
          // Envoyer à l'IA pour analyse
          setIsLoading(true);
          
          const analysisRes = await fetch(`${API_URL}/api/assistant/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              message: analysisPrompt,
              session_id: currentSessionId,
              user_id: userId,
              context: {
                user_type: userType,
                active_section: activeEspaceLabel,
                active_subsection: activeContextLabel,
                context_key: activeContext,
                attachment: {
                  type: newFile.type,
                  url: newFile.url,
                  name: newFile.name
                }
              }
            })
          });
          
          if (analysisRes.ok) {
            const analysisData = await analysisRes.json();
            const assistantMessage = {
              role: 'assistant',
              content: analysisData.response || analysisData.message,
              timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, assistantMessage]);
            
            // Gérer les artifacts si présents
            if (analysisData.has_artifact && analysisData.artifact) {
              const newArtifact = {
                id: `artifact-${Date.now()}`,
                type: analysisData.artifact.type,
                title: analysisData.artifact.title || 'Analyse',
                data: analysisData.artifact.data || analysisData.artifact
              };
              setArtifacts(prev => [newArtifact, ...prev]);
              setActiveArtifact(newArtifact);
              setShowArtifacts(true);
            }
          }
          
          toast.success(`${file.name} uploadé avec succès`);
        } else {
          toast.error(`Erreur upload: ${file.name}`);
        }
      } catch (error) {
        console.error('Erreur upload:', error);
        toast.error(`Erreur: ${file.name}`);
      }
    }
    
    setIsUploading(false);
    // Reset l'input file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const removeUploadedFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };
  
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    // Mode visiteur : on peut TOUT voir, mais parler à Abra (= faire) demande un
    // compte. La pop-up d'inscription s'ouvre, le texte reste dans la barre.
    if (!estConnecteAbra) {
      openRegisterModal('Abra, ton assistant personnel', true);
      return;
    }

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
      
      // Construire le contexte à envoyer à l'IA
      const contextInfo = {
        user_type: userType,
        active_section: activeEspaceLabel,
        active_subsection: activeContextLabel,
        context_key: activeContext
      };
      
      const res = await fetch(`${API_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          message: inputValue,
          session_id: currentSessionId,
          user_id: userId,
          context: contextInfo
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        const assistantMessage = {
          role: 'assistant',
          content: data.response || data.message,
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        
        // Gérer les artifacts
        if (data.has_artifact && data.artifact) {
          const newArtifact = {
            id: `artifact-${Date.now()}`,
            type: data.artifact.type,
            title: data.artifact.title || 'Résultat',
            data: data.artifact.data || data.artifact
          };
          setArtifacts(prev => [...prev, newArtifact]);
          setActiveArtifact(newArtifact);
          setShowArtifacts(true);
        }
        
        // Mettre à jour la session
        if (data.session_id && !currentSessionId) {
          setCurrentSessionId(data.session_id);
          
          // Si une entité est liée, lier la session
          if (linkedEntity && linkedEntity.entity) {
            const entityId = linkedEntity.entity?.id || linkedEntity.entity?._id;
            if (entityId) {
              try {
                const linkData = {};
                if (linkedEntity.entityType === 'client') {
                  linkData.client_id = entityId;
                } else if (linkedEntity.entityType === 'prospect') {
                  linkData.prospect_id = entityId;
                } else if (linkedEntity.entityType === 'contact') {
                  linkData.contact_id = entityId;
                }
                
                await fetch(`${API_URL}/api/ai/session/${data.session_id}/link`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(linkData)
                });
                console.log('Session liée à:', linkedEntity.entityType, entityId);
              } catch (linkError) {
                console.error('Erreur liaison session:', linkError);
              }
            }
            // Reset après liaison
            setLinkedEntity(null);
          }
        }
      } else {
        throw new Error('Erreur API');
      }
    } catch (e) {
      console.error('Erreur envoi message:', e);
      toast.error('Erreur de communication avec l\'assistant');
      
      // Message d'erreur
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Désolé, je rencontre un problème technique. Réessaie dans quelques instants.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleNewConversation = () => {
    setMessages([]);
    setCurrentSessionId(null);
    setArtifacts([]);
    setActiveArtifact(null);
    setActiveContext(null);
    setActiveContextLabel(null);
    setActiveEspaceLabel(null);
    // Reset linked entity
    setLinkedEntity(null);
    // Quitter le mode pleine page Desktop
    setIsDesktopToolFullScreen(false);
    setShowFloatingChat(false);
    setShowArtifacts(false);
  };
  
  // État pour l'entité liée à la conversation en cours
  const [linkedEntity, setLinkedEntity] = useState(null);
  
  // Démarrer une nouvelle conversation liée à un client/prospect/contact
  const handleStartLinkedConversation = async (entity, entityType) => {
    // Reset la conversation
    setMessages([]);
    setCurrentSessionId(null);
    setArtifacts([]);
    setActiveArtifact(null);
    
    // Stocker l'entité liée pour la lier après le premier message
    setLinkedEntity({ entity, entityType });
    
    // Fermer le panneau de droite et focus sur le chat
    setActiveContext(null);
    setActiveContextLabel(null);
    setActiveEspaceLabel(null);
    
    // Préremplir avec un message de contexte
    const entityName = entity?.name || entity?.company || `${entity?.first_name || ''} ${entity?.last_name || ''}`.trim() || 'cette personne';
    toast.success(`Conversation avec ${entityName} initiée`);
    
    // Envoyer un message initial automatique pour créer la session
    const initialMessage = `Je veux discuter de ${entityName}.`;
    await handleSendMessage(initialMessage);
  };
  
  // Ouvrir une conversation existante
  const handleOpenLinkedConversation = async (sessionId) => {
    if (!sessionId) return;
    await handleLoadSession(sessionId);
    
    // Fermer le panneau de droite
    setActiveContext(null);
    setActiveContextLabel(null);
    setActiveEspaceLabel(null);
  };
  
  const handleLoadSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
      const res = await fetch(`${API_URL}/api/assistant/sessions/${sessionId}/messages`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setCurrentSessionId(sessionId);
        
        // Charger les artifacts de la session
        if (data.artifacts) {
          setArtifacts(data.artifacts);
          if (data.artifacts.length > 0) {
            setActiveArtifact(data.artifacts[data.artifacts.length - 1]);
          }
        }
      }
    } catch (e) {
      console.error('Erreur chargement session:', e);
    }
  };
  
  const handleSaveAssistantName = () => {
    if (tempName.trim()) {
      setAssistantNameState(tempName.trim());
      setAssistantNameStorage(tempName.trim());
    }
    setIsEditingName(false);
  };
  
  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_user');
    localStorage.removeItem('ecosystem_token');
    localStorage.removeItem('ecosystem_user');
    navigate('/');
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER ARTIFACT
  // ═══════════════════════════════════════════════════════════════════════════
  const renderArtifactContent = (artifact) => {
    if (!artifact) return null;
    
    // ═══════════════════════════════════════════════════════════════════════════
    // FONCTION RETOUR AU HUB - Détermine dynamiquement si retour vers Hub Pro ou Particulier
    // Basé sur le contexte de l'artifact (ex: 'notes_pro' → Hub Pro, 'notes' → Hub Particulier)
    // ═══════════════════════════════════════════════════════════════════════════
    const handleBackToHub = () => {
      setShowArtifacts(false);
      const ctx = artifact?.context || '';
      // Si le contexte contient '_pro' ou 'pro_', on retourne au Hub Pro
      if (ctx.includes('_pro') || ctx.includes('pro_')) {
        handleSelectContext('hub_espace_pro', 'Mon Espace', 'Mon Espace Pro');
      } else {
        handleSelectContext('hub_espace', 'Mon Espace', 'Mon Espace');
      }
    };
    
    // Fonction de refresh pour recharger les données
    const handleRefresh = () => {
      if (artifact.context) {
        loadContextData(artifact.context, artifact.title);
      }
    };
    
    // Fonction pour sélectionner un item et le montrer en détail
    const handleSelectItem = (item) => {
      // On pourrait afficher les détails ou envoyer une commande au chat
      toast.info(`${item.name || item.title || item.number || 'Élément'} sélectionné`);
    };
    
    switch (artifact.type) {
      // ══════════════════════════════════════════════════════════════════════
      // ARTIFACTS INTERACTIFS (créés par l'IA)
      // ══════════════════════════════════════════════════════════════════════
      case 'devis_interactif':
        return (
          <ArtifactDevisInteractif 
            data={artifact.data}
            onUpdate={(updated) => {
              setActiveArtifact(prev => ({ ...prev, data: updated }));
            }}
          />
        );
      case 'tasks_interactif':
        return (
          <ArtifactTasksInteractif
            data={artifact.data}
            onUpdate={(updated) => {
              setActiveArtifact(prev => ({ ...prev, data: updated }));
            }}
          />
        );
      case 'projet':
        return (
          <ArtifactProjetInteractif
            data={artifact.data || DEMO_PROJECT}
            onUpdate={(updated) => {
              setActiveArtifact(prev => ({ ...prev, data: updated }));
            }}
          />
        );
      case 'search_artisans':
        return <ArtifactSearchArtisans data={artifact.data} />;
      case 'search_produits':
        return <ArtifactSearchProduits data={artifact.data} />;
      case 'search_posts':
        return <ArtifactSearchPosts data={artifact.data} />;
      
      // ══════════════════════════════════════════════════════════════════════
      // PAGES EXISTANTES (composants complets) - MAPPING EXHAUSTIF
      // ══════════════════════════════════════════════════════════════════════
      case 'page_component':
        // Rendre le composant de page correspondant
        switch (artifact.componentName) {
          // ────────────────────────────────────────────────────────────────────
          // COCKPIT PRO - Collaboration
          // ────────────────────────────────────────────────────────────────────
          case 'EquipeProPage':
            return <EquipeProPage />;
          case 'CollabPage':
            return <CollabPage />;
          case 'TimeClockPage':
            return <TimeClockPage />;
          case 'TimeClockManagerPage':
            return <TimeClockManagerPage />;
          case 'FeedProPage':
            return <FeedProPage />;
          case 'TachesProPage':
          case 'TachesCollabProPage':
            return <TachesCollabProPage />;
          case 'TchatProPage':
            return <TchatProPage />;
          case 'AgendaProPage':
            return <AgendaProPage />;
          case 'DocumentsProPage':
            return <DocumentsProPage />;
            
          // ────────────────────────────────────────────────────────────────────
          // COCKPIT PRO - Pages principales
          // ────────────────────────────────────────────────────────────────────
          case 'CockpitProDashboard':
            return <CockpitProDashboard />;
          case 'MessagerieProPage':
            return <MessagerieProPage />;
          case 'DemandesDevisPage':
            return <DemandesDevisPage />;
          case 'MailsProPage':
            return <MailsProPage />;
          case 'ReseauProPage':
            return <ReseauProPage />;
          case 'ReseauSocialProPage':
            return <ReseauSocialProPage />;
          case 'EmploiProPage':
            return <EmploiProPage />;
          case 'VisioProPage':
            return <VisioProPage />;
          case 'MesOptionsPage':
            return <MesOptionsPage />;
          case 'ImportCSVPage':
            return <ImportCSVPage />;
          case 'TicketsProPage':
            return <TicketsProPage onSelectTool={handleSelectContext} />;
            
          // ────────────────────────────────────────────────────────────────────
          // COCKPIT PRO - Plateforme (Superadmin uniquement)
          // ────────────────────────────────────────────────────────────────────
          case 'PlateformeDashboardPage':
            return <PlateformeDashboardPage />;
          case 'PlateformeAbonnementsPage':
            return <PlateformeAbonnementsPage />;
          case 'CercleConfiancePage':
            return <CercleConfiancePage />;
            
          // ────────────────────────────────────────────────────────────────────
          // COCKPIT PARTICULIER - Pages principales
          // ────────────────────────────────────────────────────────────────────
          
          // Hub Mon Espace - Page centrale avec accès aux outils
          case 'HubMonEspace':
            return (
              <HubMonEspace 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Mon Espace')}
                pendingInvitationsCount={pendingInvitationsCount}
                isMobile={isMobile}
                onOpenMenu={() => setShowSidebar(true)}
              />
            );
          
          // Hub Réseau Social
          case 'HubReseauSocial':
            return (
              <HubReseauSocial 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Réseau Social')}
                isMobile={isMobile}
                onOpenMenu={() => setShowSidebar(true)}
              />
            );
          
          // Hub Marketplace
          case 'HubMarketplace':
            return (
              <HubMarketplace 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Marketplace')}
                isMobile={isMobile}
                onOpenMenu={() => setShowSidebar(true)}
              />
            );
          
          // Hub Prestataires
          case 'HubPrestataires':
            return (
              <HubPrestataires 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Prestataires')}
                isMobile={isMobile}
                onOpenMenu={() => setShowSidebar(true)}
              />
            );
          
          // Hub Emploi
          case 'HubEmploi':
            return (
              <HubEmploi 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Emploi')}
                isMobile={isMobile}
                onOpenMenu={() => setShowSidebar(true)}
              />
            );
          
          // ══════════════════════════════════════════════════════════════════
          // COCKPIT PRO - Hubs
          // ══════════════════════════════════════════════════════════════════
          
          // Hub Mon Espace Pro
          case 'HubMonEspacePro':
            return (
              <HubMonEspacePro 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Mon Espace')}
                isMobile={isMobile}
              />
            );
          
          // Hub Collaboration
          case 'HubCollaboration':
            return (
              <HubCollaboration 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Collaboration')}
                isMobile={isMobile}
              />
            );
          
          // Hub Commercial
          case 'HubCommercial':
            return (
              <HubCommercial 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Commercial')}
                isMobile={isMobile}
              />
            );
          
          // Hub Catalogue
          case 'HubCatalogue':
            return (
              <HubCatalogue 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Catalogue')}
                isMobile={isMobile}
              />
            );
          
          // Hub Chantiers Pro
          case 'HubChantiersPro':
            return (
              <HubChantiersPro 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Chantiers')}
                isMobile={isMobile}
              />
            );
          
          // Hub Marketing
          case 'HubMarketing':
            return (
              <HubMarketing 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Marketing')}
                isMobile={isMobile}
              />
            );
          
          // Hub Réseau Social Pro
          case 'HubReseauSocialPro':
            return (
              <HubReseauSocialPro 
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Réseau Social')}
                isMobile={isMobile}
              />
            );
          
          case 'NotesPage':
          case 'MesNotesPage':
            return <MesNotesPage 
              onAskAbra={(data) => {
                const message = data?.content 
                  ? `Tu travailles sur une note : "${data.content.substring(0, 50)}...". Comment puis-je t'aider à la structurer ou l'améliorer ?`
                  : `Tu veux créer une nouvelle note. Décris-moi ce que tu veux noter et je t'aide à la structurer !`;
                handleAskAbra('notes', 'Mes Notes', message);
              }}
              onBackToHub={handleBackToHub}
              isPro={artifact?.context?.includes('_pro') || artifact?.context?.includes('pro_') || false}
            />;
          case 'DashboardParticulierPage':
            return <DashboardParticulierPage />;
          case 'DocumentsParticulierPage':
            return <DocumentsParticulierPage 
              onOpenRegisterModal={openRegisterModal}
              onBackToHub={handleBackToHub}
            />;
          case 'MesContactsPage':
            return <MesContactsPage 
              onAskAbra={(data) => {
                const message = data?.contact?.name 
                  ? `Tu veux gérer le contact "${data.contact.name}". Comment puis-je t'aider ?`
                  : data?.isNew 
                    ? `Tu veux créer un nouveau contact. Décris-moi les informations et je t'aide à l'organiser !`
                    : `Tu as besoin d'aide avec tes contacts. Que veux-tu faire ?`;
                handleAskAbra('contacts', 'Mes Contacts', message);
              }}
              onBackToHub={handleBackToHub}
            />;
          case 'AgendaParticulierPage':
            return <AgendaParticulierPage 
              onAskAbra={(data) => {
                const message = data?.title 
                  ? `Tu veux planifier l'événement "${data.title}". Comment puis-je t'aider à l'organiser ?`
                  : `Tu veux créer un nouvel événement. Décris-moi le contexte et je t'aide à le planifier !`;
                handleAskAbra('agenda_perso', 'Mon Agenda', message);
              }} 
              onOpenRegisterModal={openRegisterModal}
              onBackToHub={handleBackToHub}
            />;
          case 'MessagerieParticulierPage':
            return <MessagerieParticulierPage 
              onAskAbra={(data) => {
                const message = data?.contact 
                  ? `Tu veux envoyer un message à ${data.contact.name}. Comment puis-je t'aider à le rédiger ?`
                  : `Tu veux envoyer un nouveau message. Décris-moi le contexte et je t'aide à le rédiger !`;
                handleAskAbra('messagerie', 'Mes Messages', message);
              }}
              onBackToHub={handleBackToHub}
            />;
          case 'MailsParticulierPage':
            return <MailsParticulierPage 
              onAskAbra={(data) => {
                const message = data?.subject 
                  ? `Tu veux écrire un email avec le sujet "${data.subject}". Comment puis-je t'aider à le rédiger ?`
                  : `Tu veux écrire un nouvel email. Décris-moi le contexte et je t'aide à le rédiger !`;
                handleAskAbra('emails', 'Mes Emails', message);
              }} 
              onOpenRegisterModal={openRegisterModal}
              onBackToHub={handleBackToHub}
            />;
          case 'TachesKanbanPage':
            // Sur mobile : afficher la version mobile dédiée
            if (isMobile) {
              const navigateToPage = (componentName, title) => {
                const pageArtifact = {
                  id: `page-${componentName}-${Date.now()}`,
                  type: 'page_component',
                  componentName: componentName,
                  title: title,
                  context: componentName.toLowerCase()
                };
                setArtifacts([pageArtifact]);
                setActiveArtifact(pageArtifact);
                setShowArtifacts(true);
              };
              
              return (
                <MobileTasksListPage 
                  onOpenMenu={() => setShowSidebar(true)}
                  onOpenMessages={() => navigateToPage('MessagesReseau', 'Messages')}
                  onOpenNotifications={() => navigateToPage('NotificationsPage', 'Notifications')}
                  onOpenProfile={() => navigateToPage('ProfilPage', 'Mon Profil')}
                  onOpenCoins={() => navigateToPage('TarifsAbracaCoinsPage', 'AbracadaCoins')}
                  onNavigateToHub={(hub) => {
                    // Reset complet et navigation vers le hub demandé
                    const hubMapping = {
                      'hub_espace': { label: 'Mon Espace', espace: 'Mon Espace', path: '/cockpit-particulier' },
                      'hub_marketplace': { label: 'Marketplace', espace: 'Marketplace', path: '/cockpit-particulier/marketplace' },
                      'hub_reseau': { label: 'Réseau Social', espace: 'Réseau Social', path: '/cockpit-particulier/reseau' },
                      'hub_prestataires': { label: 'Prestataires', espace: 'Prestataires', path: '/cockpit-particulier/artisans' },
                      'hub_emploi': { label: 'Emploi', espace: 'Emploi', path: '/cockpit-particulier/emploi' },
                    };
                    const mapping = hubMapping[hub];
                    if (mapping) {
                      setActiveArtifact(null);
                      setArtifacts([]);
                      setMessages([]);
                      setShowArtifacts(false);
                      setIsDesktopToolFullScreen(false);
                      setActiveContext(hub);
                      setActiveContextLabel(mapping.label);
                      setActiveEspaceLabel(mapping.espace);
                      setShowSidebar(false);
                      navigate(mapping.path);
                    }
                  }}
                  isPro={isPro}
                  userId={userId}
                />
              );
            }
            return <TachesKanbanPage onAskAbra={(task) => {
              // Ouvrir le chat flottant avec le contexte de la tâche
              setActiveContext('taches_perso');
              setActiveContextLabel('Liste des Tâches');
              
              // Message différent selon qu'on crée ou édite une tâche
              let message;
              if (task.isNewTask) {
                message = task.title 
                  ? `Tu veux créer une nouvelle tâche "${task.title}". Comment puis-je t'aider à la définir ?`
                  : `Tu veux créer une nouvelle tâche. Décris-moi ce que tu dois faire et je t'aide à l'organiser !`;
              } else {
                message = `Je vois que tu travailles sur la tâche **"${task.title}"**. Comment puis-je t'aider avec ça ?`;
              }
              
              setMessages([{
                role: 'assistant',
                content: message
              }]);
              setShowFloatingChat(true);
            }} />;
          case 'BoiteAIdeesPage':
            return <BoiteAIdeesPage 
              onAskAbra={(data) => {
                const message = data?.title 
                  ? `Tu veux développer l'idée "${data.title}". Comment puis-je t'aider à l'approfondir ?`
                  : `Tu veux noter une nouvelle idée. Décris-la moi et je t'aide à la développer !`;
                handleAskAbra('boite_idees', 'Boîte à idées', message);
              }}
              onBackToHub={handleBackToHub}
              isPro={artifact?.context?.includes('_pro') || artifact?.context?.includes('pro_') || false}
            />;
          case 'VisioconferencePage':
            return <VisioconferencePage 
              onAskAbra={(data) => {
                const message = data?.title 
                  ? `Tu veux planifier la visio "${data.title}". Comment puis-je t'aider à l'organiser ?`
                  : `Tu veux planifier une nouvelle visioconférence. Décris-moi le contexte et je t'aide à l'organiser !`;
                handleAskAbra('visio', 'Visioconférence', message);
              }} 
              onOpenRegisterModal={openRegisterModal}
              onBackToHub={handleBackToHub}
            />;
          case 'FavorisPage':
            return <FavorisPage onSelectTool={handleSelectContext} />;
          case 'AchatsPage':
            return <AchatsPage onSelectTool={handleSelectContext} />;
          case 'VentesPage':
            return <VentesPage onSelectTool={handleSelectContext} />;
          case 'MesAnnoncesPage':
            return <MesAnnoncesPage onSelectTool={handleSelectContext} />;
          case 'VendeursSuivisPage':
            return <VendeursSuivisPage onSelectTool={handleSelectContext} />;
          case 'CerclesPage':
            return <CerclesPage />;
          case 'GroupesPage':
            return <GroupesPage />;
            
          // ────────────────────────────────────────────────────────────────────
          // COCKPIT PARTICULIER - Réseau Social
          // ────────────────────────────────────────────────────────────────────
          case 'FeedReseau':
            return <FeedReseau onOpenMenu={() => setShowSidebar(true)} />;
          case 'NotificationsPage':
            return <NotificationsPage />;
          case 'MesPublications':
            return <MesPublications onAskAbra={(data) => {
              // Détecter si c'est une demande d'aide pour un article blog
              if (data?.type === 'article_blog' || data?.isPublication) {
                const contextInfo = [];
                if (data?.title) contextInfo.push(`Titre : "${data.title}"`);
                if (data?.metier) contextInfo.push(`Métier : ${data.metier}`);
                if (data?.content) contextInfo.push(`Contenu en cours : ${data.content.substring(0, 200)}...`);
                
                const message = contextInfo.length > 0
                  ? `Je t'aide à rédiger ton article blog.\n\n**Contexte actuel :**\n${contextInfo.map(i => `• ${i}`).join('\n')}\n\nChoisis ce que tu veux que je fasse :`
                  : `Je peux t'aider à créer un super article blog ! Choisis une action :`;
                
                handleAskAbra('blog_article', 'Article Blog', message);
              } else {
                const message = data?.content 
                  ? `Tu travailles sur une publication. Comment puis-je t'aider ?`
                  : `Tu veux créer une nouvelle publication. Décris-moi ce que tu veux partager !`;
                handleAskAbra('publications', 'Mes Publications', message);
              }
            }} />;
          case 'BlogPage':
            return <BlogPage />;
          case 'MaGalerie':
            return <MaGalerie onAskAbra={(data) => {
              const message = data?.title 
                ? `Tu travailles sur la photo "${data.title}". Comment puis-je t'aider ?`
                : `Tu veux ajouter des photos à ta galerie. Décris-moi ce que tu veux mettre en valeur !`;
              handleAskAbra('galerie', 'Ma Galerie', message);
            }} />;
          case 'EvenementsPage':
            return <EvenementsPage />;
          case 'SalonsPage':
            return <SalonsPage />;
          case 'MesAbonnes':
            return <MesAbonnes onAskAbra={(data) => {
              const message = data?.name 
                ? `Tu veux interagir avec ${data.name}. Comment puis-je t'aider ?`
                : `Tu veux gérer tes abonnés. Que veux-tu faire ?`;
              handleAskAbra('abonnes', 'Mes Abonnés', message);
            }} />;
          case 'MesAbonnements':
            return <MesAbonnements onAskAbra={(data) => {
              const message = data?.name 
                ? `Tu veux interagir avec ${data.name}. Comment puis-je t'aider ?`
                : `Tu veux gérer tes abonnements. Que veux-tu faire ?`;
              handleAskAbra('abonnements', 'Mes Abonnements', message);
            }} />;
          case 'MessagesReseau':
            return <MessagesReseau />;
            
          // ────────────────────────────────────────────────────────────────────
          // COCKPIT PARTICULIER - Marketplace
          // ────────────────────────────────────────────────────────────────────
          case 'DashboardMarketplace':
            return <DashboardMarketplace onSelectTool={handleSelectContext} onOpenMenu={() => setShowSidebar(true)} />;
          case 'MesAnnoncesPage':
            return <MesAnnoncesPage onSelectTool={handleSelectContext} />;
          case 'CommandesPage':
            return <CommandesPage onSelectTool={handleSelectContext} />;
          case 'VentesPage':
            return <VentesPage onSelectTool={handleSelectContext} />;
          case 'AchatsPage':
            return <AchatsPage onSelectTool={handleSelectContext} />;
          case 'AvisMarketplacePage':
            return <AvisMarketplacePage onSelectTool={handleSelectContext} />;
          case 'FavorisMarketplacePage':
            return <FavorisMarketplacePage onSelectTool={handleSelectContext} />;
          case 'FavorisPage':
            return <FavorisPage onSelectTool={handleSelectContext} />;
          case 'VendeursSuivisPage':
            return <VendeursSuivisPage onSelectTool={handleSelectContext} />;
          case 'DeposerAnnoncePage':
            return <DeposerAnnoncePage onSelectTool={handleSelectContext} />;
            
          // ────────────────────────────────────────────────────────────────────
          // COCKPIT PARTICULIER - Artisans/Prestataires
          // ────────────────────────────────────────────────────────────────────
          case 'DashboardArtisans':
            return <DashboardArtisans onOpenMenu={() => setShowSidebar(true)} />;
          case 'RechercheArtisans':
            return <RechercheArtisans />;
          case 'DevisParticulierPage':
            return <DevisParticulierPage />;
          case 'ChantiersParticulierPage':
            return <ChantiersParticulierPage />;
          case 'ArtisansFavoris':
            return <ArtisansFavoris />;
          case 'AvisArtisans':
            return <AvisArtisans />;
            
          // ────────────────────────────────────────────────────────────────────
          // COCKPIT PARTICULIER - Emploi
          // ────────────────────────────────────────────────────────────────────
          case 'DashboardEmploi':
            return <DashboardEmploi onNavigate={(context, label) => handleSelectContext(context, label, 'Emploi')} onOpenMenu={() => setShowSidebar(true)} />;
          case 'OffresEmploiPage':
            return <OffresEmploiPage onNavigate={(context, label) => handleSelectContext(context, label, 'Emploi')} />;
          case 'CandidaturesPage':
            return <CandidaturesPage />;
          case 'CVPage':
            return <CVPage />;
          case 'AlertesEmploiPage':
            return <AlertesEmploiPage />;
          case 'RechercheEmploiPage':
            return <RechercheEmploiPage />;
          case 'OffresBTPPage':
            return <OffresBTPPage />;
          case 'EntreprisesRecrutentPage':
            return <EntreprisesRecrutentPage />;
          case 'DeposerCVPage':
            return <DeposerCVPage />;
            
          default:
            return (
              <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                  <AlertTriangle size={28} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">Page en construction</h3>
                <p className="text-gray-500 text-sm">
                  La page "{artifact.componentName}" sera bientôt disponible.
                </p>
              </div>
            );
        }
      
      // ══════════════════════════════════════════════════════════════════════
      // LISTES DE DONNÉES (chargées depuis le menu)
      // ══════════════════════════════════════════════════════════════════════
      
      // Devis
      case 'devis':
        return (
          <ArtifactDevisList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
          />
        );
      
      // Factures
      case 'factures':
        return (
          <ArtifactFacturesList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
          />
        );
      
      // Clients
      case 'clients':
        return (
          <ArtifactClientsList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
            onStartConversation={handleStartLinkedConversation}
            onOpenConversation={handleOpenLinkedConversation}
          />
        );
      
      // Prospects
      case 'prospects':
        return (
          <ArtifactProspectsList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
            onStartConversation={handleStartLinkedConversation}
            onOpenConversation={handleOpenLinkedConversation}
          />
        );
      
      // Tâches
      case 'tasks':
        return (
          <ArtifactTasksList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
          />
        );
      
      // Équipe
      case 'team':
        return (
          <ArtifactTeamList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
          />
        );
      
      // Événements / Agenda
      case 'events':
        return (
          <ArtifactEventsList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
          />
        );
      
      // Messages / Conversations
      case 'messages':
        return (
          <ArtifactMessagesList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
          />
        );
      
      // Chantiers
      case 'chantiers':
        return (
          <ArtifactChantiersList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
          />
        );
      
      // Produits / Catalogue
      case 'products':
        return (
          <ArtifactProductsList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
          />
        );
      
      // Notes
      case 'notes':
        return (
          <ArtifactNotesList 
            data={artifact.data} 
            onSelectItem={handleSelectItem}
            onRefresh={handleRefresh}
          />
        );
      
      // Dashboards
      case 'dashboard':
        return (
          <ArtifactDashboard 
            data={artifact.data} 
            title={artifact.title}
          />
        );
      
      // ══════════════════════════════════════════════════════════════════════
      // CONTACTS (Cockpit Particulier)
      // ══════════════════════════════════════════════════════════════════════
      case 'contacts':
        return (
          <MesContactsPage 
            onAskAbra={(data) => {
              const message = data?.contact?.name 
                ? `Tu veux gérer le contact "${data.contact.name}". Comment puis-je t'aider ?`
                : data?.isNew 
                  ? `Tu veux créer un nouveau contact. Décris-moi les informations et je t'aide à l'organiser !`
                  : `Tu as besoin d'aide avec tes contacts. Que veux-tu faire ?`;
              handleAskAbra('contacts', 'Mes Contacts', message);
            }}
          />
        );
      
      case 'contacts_history':
        return (
          <div className="h-full flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-4">
              <MessageSquare size={28} className="text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">Historique des échanges</h3>
            <p className="text-gray-500 text-sm mb-4">
              L'historique est maintenant accessible en cliquant sur un contact.
            </p>
          </div>
        );
      
      // ══════════════════════════════════════════════════════════════════════
      // PANNEAU PARAMÈTRES
      // ══════════════════════════════════════════════════════════════════════
      case 'parametres':
        return <ParametresPanel isPro={isPro} />;
      
      // ══════════════════════════════════════════════════════════════════════
      // PLACEHOLDER CONTEXTUEL (quand pas de données)
      // ══════════════════════════════════════════════════════════════════════
      case 'context_placeholder':
        // Pour certains contextes, on a des pages dédiées à afficher au lieu du placeholder
        if (artifact.context === 'contacts' || artifact.data?.context === 'contacts') {
          return (
            <MesContactsPage 
              onAskAbra={(data) => {
                const message = data?.contact?.name 
                  ? `Tu veux gérer le contact "${data.contact.name}". Comment puis-je t'aider ?`
                  : data?.isNew 
                    ? `Tu veux créer un nouveau contact. Décris-moi les informations et je t'aide à l'organiser !`
                    : `Tu as besoin d'aide avec tes contacts. Que veux-tu faire ?`;
                handleAskAbra('contacts', 'Mes Contacts', message);
              }}
            />
          );
        }
        
        // Placeholder par défaut
        return (
          <div className="h-full flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] rounded-2xl flex items-center justify-center mb-5 shadow-lg">
              <Sparkles size={28} className="text-white" />
            </div>
            <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">
              {artifact.title}
            </h3>
            <p className="text-gray-500 mb-6 max-w-sm">
              {artifact.data?.message}
            </p>
            
            {/* Suggestions cliquables */}
            {artifact.data?.suggestions && (
              <div className="space-y-2 w-full max-w-sm">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Suggestions</p>
                {artifact.data.suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setInputValue(suggestion);
                      inputRef.current?.focus();
                    }}
                    className="w-full px-4 py-3 text-left text-sm bg-gray-50 hover:bg-[#F97316]/10 border border-gray-200 hover:border-[#F97316] rounded-xl transition-all duration-200 text-gray-700 hover:text-[#1E3A5F]"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
        
      // ══════════════════════════════════════════════════════════════════════
      // DEFAULT - Affichage JSON brut
      // ══════════════════════════════════════════════════════════════════════
      default:
        // Si on a des données mais pas de composant spécifique, on essaie de deviner
        if (artifact.data && Array.isArray(artifact.data)) {
          // C'est probablement une liste, on utilise un affichage générique
          return (
            <div className="h-full flex flex-col">
              <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
                <h2 className="text-lg font-bold text-[#1E3A5F]">{artifact.title}</h2>
                <p className="text-sm text-gray-500">{artifact.data.length} éléments</p>
              </div>
              <div className="flex-1 overflow-y-auto scrollbar-hide p-4">
                <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 p-4 rounded-xl">
                  {JSON.stringify(artifact.data, null, 2)}
                </pre>
              </div>
            </div>
          );
        }
        
        return (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-[#1E3A5F] mb-4">{artifact.title}</h3>
            <pre className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 p-4 rounded-xl overflow-auto max-h-96">
              {JSON.stringify(artifact.data, null, 2)}
            </pre>
          </div>
        );
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU MOBILE PLEIN ÉCRAN - Pages dédiées
  // ═══════════════════════════════════════════════════════════════════════════
  if (isMobile && mobileFullScreenPage) {
    switch (mobileFullScreenPage) {
      case 'tasks':
        const navigateToPageTasks = (componentName, title) => {
          setMobileFullScreenPage(null);
          const pageArtifact = {
            id: `page-${componentName}-${Date.now()}`,
            type: 'page_component',
            componentName: componentName,
            title: title,
            context: componentName.toLowerCase()
          };
          setArtifacts([pageArtifact]);
          setActiveArtifact(pageArtifact);
          setShowArtifacts(true);
        };
        
        return (
          <MobileTasksListPage 
            onOpenMenu={() => {
              setMobileFullScreenPage(null);
              setShowSidebar(true);
            }}
            onBackToHub={() => {
              setMobileFullScreenPage(null);
              handleSelectContext('hub_espace', 'Mon Espace', 'Mon Espace');
            }}
            onNavigateToHub={(hub) => {
              // Reset complet et navigation vers le hub demandé
              const hubMapping = {
                'hub_espace': { label: 'Mon Espace', espace: 'Mon Espace', path: '/cockpit-particulier' },
                'hub_marketplace': { label: 'Marketplace', espace: 'Marketplace', path: '/cockpit-particulier/marketplace' },
                'hub_reseau': { label: 'Réseau Social', espace: 'Réseau Social', path: '/cockpit-particulier/reseau' },
                'hub_prestataires': { label: 'Prestataires', espace: 'Prestataires', path: '/cockpit-particulier/artisans' },
                'hub_emploi': { label: 'Emploi', espace: 'Emploi', path: '/cockpit-particulier/emploi' },
              };
              const mapping = hubMapping[hub];
              if (mapping) {
                setMobileFullScreenPage(null);
                setActiveArtifact(null);
                setArtifacts([]);
                setMessages([]);
                setShowArtifacts(false);
                setIsDesktopToolFullScreen(false);
                setActiveContext(hub);
                setActiveContextLabel(mapping.label);
                setActiveEspaceLabel(mapping.espace);
                setShowSidebar(false);
                navigate(mapping.path);
              }
            }}
            onOpenMessages={() => navigateToPageTasks('MessagesReseau', 'Messages')}
            onOpenNotifications={() => navigateToPageTasks('NotificationsPage', 'Notifications')}
            onOpenProfile={() => navigateToPageTasks('ProfilPage', 'Mon Profil')}
            onOpenCoins={() => navigateToPageTasks('TarifsAbracaCoinsPage', 'AbracadaCoins')}
            isPro={isPro}
            userId={userId}
          />
        );
      
      case 'messages':
        return (
          <CockpitMessagesPage 
            onOpenMenu={() => {
              setMobileFullScreenPage(null);
              setShowSidebar(true);
            }}
            onBackToHub={() => {
              setMobileFullScreenPage(null);
              handleSelectContext('hub_espace', 'Mon Espace', 'Mon Espace');
            }}
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
          />
        );
        
      default:
        break;
    }
  }
  
  return (
    <div 
      className={`flex bg-[#f8f9fa] overflow-hidden fixed inset-x-0 ${isMobile ? 'top-[94px]' : 'top-0'}`}
      style={{ 
        bottom: isMobile ? '60px' : 0, // Espace pour la navbar du bas sur mobile
      }}
    >
      {/* ══════════════════════════════════════════════════════════════════════
          SIDEBAR GAUCHE - MENU COCKPIT (Design Premium Bleu Navy)
          Desktop: Mode complet (300px) ou réduit (70px icônes seulement)
          Mobile: Plein écran type ChatGPT quand ouvert
      ══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {(isMobile ? showSidebar : true) && (
          <motion.aside
            initial={{ x: isMobile ? '-100%' : 0, width: isMobile ? '100%' : (sidebarCollapsed ? 70 : 300), opacity: isMobile ? 0 : 1 }}
            animate={{
              x: 0,
              width: isMobile ? '100%' : (sidebarCollapsed ? 70 : 300),
              opacity: 1
            }}
            exit={{ x: isMobile ? '-100%' : 0, width: isMobile ? '100%' : (sidebarCollapsed ? 70 : 300), opacity: isMobile ? 0 : 1 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className={`h-full flex flex-col overflow-hidden ${isMobile ? 'fixed inset-x-0 z-50' : 'relative'}`}
            style={{
              minWidth: isMobile ? '100%' : (sidebarCollapsed ? 70 : 300),
              maxWidth: isMobile ? '100%' : (sidebarCollapsed ? 70 : 300),
              background: isMobile ? '#FAF7F2' : 'linear-gradient(180deg, #1E3A5F 0%, #152a45 100%)',
              top: isMobile ? '94px' : 'auto', // Sous le TopBanner global
              bottom: isMobile ? '60px' : 'auto', // Espace pour la navbar du bas
            }}
          >
            {/* Bouton Toggle Collapse - Desktop uniquement - Sur le bord droit du sidebar */}
            {!isMobile && !sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(true)}
                className="absolute -right-3 top-7 z-20 w-6 h-6 rounded-full flex items-center justify-center transition-all hover:scale-110 shadow-md"
                style={{
                  background: 'linear-gradient(135deg, #C9A15D 0%, #D4AF37 100%)',
                  border: '2px solid #1E3A5F'
                }}
                title="Réduire le menu"
              >
                <ChevronLeft size={14} strokeWidth={2.5} className="text-[#1E3A5F]" />
              </button>
            )}
            {/* ═══════════════════════════════════════════════════════════════
                HEADER PREMIUM LUXE - Signature Abracada haut de gamme
            ═══════════════════════════════════════════════════════════════ */}
            <div 
              className={`relative px-5 py-4 ${isMobile ? '' : ''}`}
              style={{ 
                background: 'linear-gradient(145deg, #1a3461 0%, #1F3B6E 30%, #243F73 60%, #1a3461 100%)'
              }}
            >
              {/* Détails décoratifs premium subtils */}
              {isMobile && (
                <>
                  {/* Lignes courbes dorées très subtiles */}
                  <svg className="absolute top-0 right-0 w-40 h-40 opacity-[0.06] pointer-events-none" viewBox="0 0 100 100">
                    <path d="M80,0 Q100,20 100,50" stroke="#C9A15D" strokeWidth="0.5" fill="none"/>
                    <path d="M60,0 Q100,30 100,70" stroke="#C9A15D" strokeWidth="0.3" fill="none"/>
                    <path d="M90,0 Q100,10 100,30" stroke="#D4AF37" strokeWidth="0.4" fill="none"/>
                  </svg>
                  {/* Micro-étoiles champagne */}
                  <div className="absolute top-4 right-20 w-1 h-1 bg-[#D4AF37]/40 rounded-full" style={{ boxShadow: '0 0 3px rgba(212,175,55,0.3)' }} />
                  <div className="absolute top-7 right-28 w-0.5 h-0.5 bg-[#C9A15D]/35 rounded-full" />
                  <div className="absolute top-5 right-14 w-0.5 h-0.5 bg-white/20 rounded-full" />
                  <div className="absolute top-9 right-24 w-[3px] h-[3px] bg-[#D4AF37]/25 rounded-full" style={{ boxShadow: '0 0 4px rgba(212,175,55,0.2)' }} />
                  {/* Lueur dorée subtile en haut à droite */}
                  <div 
                    className="absolute top-0 right-0 w-48 h-32 pointer-events-none"
                    style={{
                      background: 'radial-gradient(ellipse at top right, rgba(201,161,93,0.08) 0%, transparent 60%)'
                    }}
                  />
                </>
              )}
              <div className="flex items-center">
                {/* Logo avec cercle doré premium affiné */}
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                  style={{
                    border: '2px solid #C9A15D',
                    background: 'linear-gradient(145deg, #1F3B6E 0%, #2a4a7a 50%, #1F3B6E 100%)',
                    boxShadow: '0 3px 10px rgba(0,0,0,0.2), inset 0 1px 2px rgba(255,255,255,0.1), 0 0 15px rgba(201,161,93,0.15)'
                  }}
                >
                  <img 
                    src="/abracadaworld-globe.svg" 
                    alt="Abracada" 
                    className="w-9 h-9 object-contain"
                  />
                </div>
                {(!sidebarCollapsed || isMobile) && (
                  <>
                    {/* Sélecteur d'espace - Dropdown */}
                    <div className="flex-1 min-w-0 relative ml-3" data-space-selector>
                      <button
                        onClick={() => setShowSpaceSelector(!showSpaceSelector)}
                        className="w-full text-left group"
                      >
                        <div className="flex items-center gap-1.5">
                          <h1 
                            className="text-xl min-[410px]:text-[22px] font-semibold text-white leading-tight whitespace-nowrap"
                            style={{ 
                              letterSpacing: '0.5px',
                              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                            }}
                          >
                            {isPro ? 'Abracada Pro' : 'Abracada'}
                          </h1>
                          <ChevronDown 
                            size={15} 
                            className={`text-[#C9A15D]/70 group-hover:text-[#C9A15D] transition-all ${showSpaceSelector ? 'rotate-180' : ''}`} 
                          />
                        </div>
                        <p 
                          className="text-sm leading-tight mt-0.5"
                          style={{ 
                            color: '#C9A15D',
                            opacity: 0.85,
                            letterSpacing: '0.3px'
                          }}
                        >
                          {isPro ? 'Gère ton compte pro' : 'Bienvenue chez toi'}
                        </p>
                      </button>
                      
                      {/* Dropdown sélecteur d'espace */}
                      <AnimatePresence>
                        {showSpaceSelector && (
                          <motion.div
                            initial={{ opacity: 0, y: -5, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -5, scale: 0.95 }}
                            transition={{ duration: 0.15 }}
                            className="absolute left-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
                          >
                            <div className="py-1">
                              {/* Espace Particulier */}
                              <Link
                                to="/cockpit-particulier"
                                state={{ keepMenuOpen: true }}
                                onClick={() => setShowSpaceSelector(false)}
                                className={`flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors ${!isPro ? 'bg-blue-50' : ''}`}
                              >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${!isPro ? 'bg-[#1E3A5F]' : 'bg-gray-100'}`}>
                                  <User size={16} className={!isPro ? 'text-white' : 'text-gray-500'} />
                                </div>
                                <div className="flex-1">
                                  <p className={`text-sm font-semibold ${!isPro ? 'text-[#1E3A5F]' : 'text-gray-700'}`}>Abracada</p>
                                  <p className="text-xs text-gray-500">Espace Particulier</p>
                                </div>
                                {!isPro && <Check size={16} className="text-[#22C55E]" />}
                              </Link>
                              
                              {/* Séparateur */}
                              <div className="border-t border-gray-100 my-1" />
                              
                              {/* Espace Pro */}
                              <Link
                                to="/cockpit-pro"
                                state={{ keepMenuOpen: true }}
                                onClick={() => setShowSpaceSelector(false)}
                                className={`flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors ${isPro ? 'bg-orange-50' : ''}`}
                              >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isPro ? 'bg-[#F97316]' : 'bg-gray-100'}`}>
                                  <Briefcase size={16} className={isPro ? 'text-white' : 'text-gray-500'} />
                                </div>
                                <div className="flex-1">
                                  <p className={`text-sm font-semibold ${isPro ? 'text-[#F97316]' : 'text-gray-700'}`}>Abracada Pro</p>
                                  <p className="text-xs text-gray-500">Espace Professionnel</p>
                                </div>
                                {isPro && <Check size={16} className="text-[#22C55E]" />}
                              </Link>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    {/* Croix fermer menu - Mobile uniquement - Style doré premium discret */}
                    {isMobile && (
                      <button
                        onClick={() => setShowSidebar(false)}
                        className="w-9 h-9 rounded-lg flex items-center justify-center transition-all ml-auto"
                        style={{
                          background: 'rgba(201, 161, 93, 0.08)',
                          border: '1px solid rgba(201, 161, 93, 0.4)'
                        }}
                        title="Fermer le menu"
                      >
                        <X size={18} strokeWidth={1.5} className="text-[#C9A15D]/80" />
                      </button>
                    )}
                  </>
                )}
                {sidebarCollapsed && !isMobile && (
                  <button
                    onClick={() => setSidebarCollapsed(false)}
                    className="absolute -right-3 top-5 z-10 w-6 h-6 bg-white border border-gray-200 rounded-full shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                    title="Agrandir le menu"
                  >
                    <ChevronRight size={14} className="text-[#1E3A5F]" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Contenu du menu - Fond crème ivoire premium sur mobile */}
            <div className={`flex-1 flex flex-col overflow-hidden ${isMobile ? '' : ''}`} style={{ background: isMobile ? '#FAF7F2' : 'transparent' }}>
              {/* Bouton principal - différent selon Particulier ou Pro */}
              <div className={`px-5 pt-5 pb-4 ${sidebarCollapsed ? 'px-2' : ''}`}>
                {isPro ? (
                  /* Cockpit Pro : Nouvelle conversation - Style terracotta premium */
                  <button
                    onClick={handleNewConversation}
                    className={`${sidebarCollapsed ? 'w-full p-3 justify-center' : 'w-full flex items-center gap-2 px-4 py-3'} flex hover:brightness-105 rounded-2xl transition-all duration-200 text-sm font-semibold text-white`}
                    style={{
                      background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)',
                      boxShadow: isMobile ? '0 5px 18px rgba(232, 149, 92, 0.30)' : '0 4px 12px rgba(232, 149, 92, 0.20)'
                    }}
                    title="Nouvelle conversation"
                  >
                    <Plus size={18} />
                    {!sidebarCollapsed && <span>Nouvelle conversation</span>}
                  </button>
                ) : (
                  /* Cockpit Particulier : Abracadaworld - Style premium luxe */
                  <button
                    onClick={() => {
                      if (isMobile) {
                        setShowSidebar(false);
                        window.dispatchEvent(new CustomEvent('openUniversModal'));
                      } else {
                        handleSelectContext('page_abracadaworld_home', 'AbracadaWorld', 'Univers');
                      }
                    }}
                    className={`${sidebarCollapsed ? 'w-full p-3 justify-center' : 'w-full flex items-center gap-4 px-4 py-4'} flex rounded-2xl transition-all duration-200 relative overflow-hidden`}
                    style={{
                      background: activeContext === 'page_abracadaworld_home'
                        ? 'linear-gradient(135deg, #FFF0E0 0%, #FFE4C4 100%)'
                        : 'linear-gradient(135deg, #FFFCF7 0%, #FFF8EE 100%)',
                      border: activeContext === 'page_abracadaworld_home' ? '1.5px solid #D4784A' : '1.5px solid #E8955C',
                      boxShadow: '0 4px 20px rgba(232, 149, 92, 0.10), 0 1px 3px rgba(0,0,0,0.04)'
                    }}
                    title="Découvre les univers Abracadaworld"
                  >
                    {/* Icône orange terracotta premium */}
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)',
                        boxShadow: '0 3px 10px rgba(232, 149, 92, 0.25)'
                      }}
                    >
                      <LayoutGrid size={22} className="text-white" />
                    </div>
                    {!sidebarCollapsed && (
                      <div className="text-left flex-1">
                        <span className="text-[17px] font-bold text-[#1F3B6E] block" style={{ letterSpacing: '0.3px' }}>AbracadaWorld</span>
                        <span className="text-[13px] text-[#9C8B7A]">Découvre les univers</span>
                      </div>
                    )}
                    {/* Étoile décorative champagne */}
                    {!sidebarCollapsed && isMobile && (
                      <div className="absolute top-3 right-3">
                        <Sparkles size={14} strokeWidth={1.5} className="text-[#C9A15D]/30" />
                      </div>
                    )}
                  </button>
                )}
              </div>
            
            {/* Menu des espaces - scrollbar masquée */}
            <nav className={`flex-1 overflow-y-auto py-2 scrollbar-hide ${sidebarCollapsed ? 'px-2' : 'px-5'}`}>
              
              {/* ═══════════════════════════════════════════════════════════════
                  SECTION CONVERSATIONS - Style carte premium luxe
              ═══════════════════════════════════════════════════════════════ */}
              {!sidebarCollapsed && (
                <div className="mb-4">
                  <button
                    onClick={() => setConversationsExpanded(!conversationsExpanded)}
                    className={`w-full flex items-center justify-between px-4 py-4 rounded-2xl transition-all duration-200 ${
                      isMobile 
                        ? 'bg-white/90 text-[#1F3B6E]' 
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                    style={isMobile ? {
                      boxShadow: '0 2px 15px rgba(31, 59, 110, 0.05), 0 1px 3px rgba(0,0,0,0.03)'
                    } : {}}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isMobile ? 'bg-[#F0F5FA]' : 'bg-white/10'}`}>
                        <MessageSquare size={20} className={isMobile ? 'text-[#1F3B6E]' : ''} />
                      </div>
                      <span className="text-[15px] font-semibold">Mes demandes</span>
                      {sessions.length > 0 && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${isMobile ? 'bg-[#1F3B6E]/8 text-[#1F3B6E]' : 'bg-white/20'}`}>
                          {sessions.length}
                        </span>
                      )}
                    </div>
                    <ChevronRight 
                      size={18} 
                      strokeWidth={1.5}
                      className={`transition-transform duration-200 ${isMobile ? 'text-[#C9A15D]/70' : ''} ${conversationsExpanded ? 'rotate-90' : ''}`} 
                    />
                  </button>
                  
                  {/* Liste des conversations */}
                  {conversationsExpanded && (
                    <div className={`mt-2 space-y-1 max-h-60 overflow-y-auto scrollbar-hide ${isMobile ? 'bg-white rounded-2xl p-3' : ''}`}
                      style={isMobile ? { boxShadow: '0 2px 12px rgba(31, 59, 110, 0.06)' } : {}}
                    >
                      {sessions.length === 0 ? (
                        <p className={`px-3 py-2 text-sm italic ${isMobile ? 'text-[#8B7355]' : 'text-white/40'}`}>
                          Aucune conversation sauvegardée
                        </p>
                      ) : (
                        sessions.map((session) => (
                          <div
                            key={session.session_id}
                            className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                              currentSessionId === session.session_id
                                ? isMobile ? 'bg-[#E97A2F] text-white' : 'bg-[#E8650A] text-white'
                                : isMobile ? 'text-[#1F3B6E]/70 hover:bg-[#EEF4FA]' : 'text-white/70 hover:text-white hover:bg-white/10'
                            }`}
                          >
                            {editingSessionId === session.session_id ? (
                              <input
                                type="text"
                                value={editingSessionTitle}
                                onChange={(e) => setEditingSessionTitle(e.target.value)}
                                onBlur={() => handleRenameSession(session.session_id, editingSessionTitle)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRenameSession(session.session_id, editingSessionTitle);
                                  if (e.key === 'Escape') setEditingSessionId(null);
                                }}
                                className="flex-1 bg-white/20 text-white text-xs px-2 py-1 rounded outline-none"
                                autoFocus
                              />
                            ) : (
                              <>
                                <button
                                  onClick={() => handleLoadSession(session.session_id)}
                                  className="flex-1 text-left text-xs truncate"
                                  title={session.title || 'Sans titre'}
                                >
                                  {session.title || 'Sans titre'}
                                </button>
                                <div className="hidden group-hover:flex items-center gap-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingSessionId(session.session_id);
                                      setEditingSessionTitle(session.title || '');
                                    }}
                                    className="p-1 hover:bg-white/20 rounded"
                                    title="Renommer"
                                  >
                                    <Pen size={12} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSession(session.session_id);
                                    }}
                                    className="p-1 hover:bg-red-500/50 rounded"
                                    title="Supprimer"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Séparateur simple */}
              {!sidebarCollapsed && isMobile && (
                <div className="my-3" />
              )}
              {!sidebarCollapsed && !isMobile && (
                <div className="border-t border-white/10 my-3" />
              )}
              
              {sidebarCollapsed ? (
                // Mode réduit : icônes seulement
                <div className="space-y-1">
                  {Object.values(MENU_CONFIG).map((espace) => {
                    const Icon = espace.icon;
                    const hasActiveChild = espace.context 
                      ? activeContext === espace.context 
                      : (espace.subItems?.some(item => activeContext === item.context) || false);
                    return (
                      <button
                        key={espace.id}
                        onClick={() => {
                          if (espace.context && (!espace.subItems || espace.subItems.length === 0)) {
                            // Lien direct sans sous-menu
                            handleSelectContext(espace.context, espace.label, espace.label);
                          } else {
                            setSidebarCollapsed(false);
                            setTimeout(() => toggleEspace(espace.id), 100);
                          }
                        }}
                        className={`
                          w-full p-3 rounded-xl flex items-center justify-center transition-all duration-200
                          ${hasActiveChild ? 'bg-[#F97316]' : 'bg-white/10 hover:bg-white/20'}
                        `}
                        title={espace.label}
                      >
                        <Icon size={20} className="text-white" />
                      </button>
                    );
                  })}
                </div>
              ) : (
                // Mode complet : menu avec labels
                Object.values(MENU_CONFIG).map((espace) => (
                  <MenuEspaceItem
                    key={espace.id}
                    espace={espace}
                    isExpanded={expandedEspaces.includes(espace.id)}
                    onToggle={() => toggleEspace(espace.id)}
                    activeContext={activeContext}
                    onSelectContext={handleSelectContext}
                    onShowComingSoon={setComingSoonModal}
                    isMobileView={isMobile}
                    pendingInvitationsCount={pendingInvitationsCount}
                  />
                ))
              )}
            </nav>
            
            {/* Footer sidebar - Actions premium */}
            <div className={`px-5 py-4 ${isMobile ? '' : 'border-t border-white/10'}`}>
              {/* Sur mobile : pas besoin de Passer Pro et Paramètres (dans la navbar via Profil) */}
              {/* Sur Desktop : afficher les options */}
              {!isMobile && (
                <>
                  {/* Passer Pro - Visible uniquement pour les non-Pro */}
                  {userType !== 'pro' && (
                    <Link
                      to="/pro/fondateur"
                      data-testid="btn-passer-pro"
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                    >
                      <Crown size={20} />
                      <span className="font-semibold text-base">Passer Pro</span>
                      <ChevronRight size={18} className="ml-auto opacity-50" />
                    </Link>
                  )}
                  
                  {/* Paramètres du compte */}
                  <button
                    data-testid="btn-parametres"
                    onClick={() => handleSelectContext('parametres', 'Paramètres', 'Mon Compte')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 text-white/70 hover:text-white hover:bg-white/10"
                  >
                    <Settings size={20} />
                    <span className="font-medium text-base">Paramètres</span>
                    <ChevronRight size={18} className="ml-auto opacity-50" />
                  </button>
                </>
              )}
            </div>
            
            {/* Navbar du bas - Mobile uniquement - Cockpit Particulier */}
            {isMobile && (
              <div className="mt-auto border-t border-[#2A4A6F]" style={{ background: '#1E3A5F' }}>
                {/* Navbar Cockpit: Espace | Marketplace | Réseau | Service | Emploi */}
                <div className="grid grid-cols-5 items-center justify-items-center" style={{ height: '60px' }}>
                  {/* Espace */}
                  <button 
                    onClick={() => { setShowSidebar(false); handleSelectContext('hub_espace', 'Mon Espace', 'Mon Espace'); }}
                    className="flex flex-col items-center justify-center gap-[2px]" 
                    data-testid="sidebar-nav-espace"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeContext === 'hub_espace' ? '#F97316' : 'rgba(255,255,255,0.75)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <span style={{ color: activeContext === 'hub_espace' ? '#F97316' : 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: activeContext === 'hub_espace' ? 600 : 500 }}>Espace</span>
                  </button>
                  {/* Marketplace */}
                  <button 
                    onClick={() => { setShowSidebar(false); handleSelectContext('hub_marketplace', 'Marketplace', 'Marketplace'); }}
                    className="flex flex-col items-center justify-center gap-[2px]" 
                    data-testid="sidebar-nav-marketplace"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeContext === 'hub_marketplace' ? '#F97316' : 'rgba(255,255,255,0.75)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <path d="M16 10a4 4 0 0 1-8 0"/>
                    </svg>
                    <span style={{ color: activeContext === 'hub_marketplace' ? '#F97316' : 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: activeContext === 'hub_marketplace' ? 600 : 500 }}>Marketplace</span>
                  </button>
                  {/* Réseau */}
                  <button 
                    onClick={() => { setShowSidebar(false); handleSelectContext('hub_reseau', 'Réseau Social', 'Réseau Social'); }}
                    className="flex flex-col items-center justify-center gap-[2px]" 
                    data-testid="sidebar-nav-reseau"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeContext === 'hub_reseau' ? '#F97316' : 'rgba(255,255,255,0.75)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    <span style={{ color: activeContext === 'hub_reseau' ? '#F97316' : 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: activeContext === 'hub_reseau' ? 600 : 500 }}>Réseau</span>
                  </button>
                  {/* Service */}
                  <button 
                    onClick={() => { setShowSidebar(false); handleSelectContext('hub_prestataires', 'Prestataires', 'Prestataires'); }}
                    className="flex flex-col items-center justify-center gap-[2px]" 
                    data-testid="sidebar-nav-service"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeContext === 'hub_prestataires' ? '#F97316' : 'rgba(255,255,255,0.75)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                    </svg>
                    <span style={{ color: activeContext === 'hub_prestataires' ? '#F97316' : 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: activeContext === 'hub_prestataires' ? 600 : 500 }}>Service</span>
                  </button>
                  {/* Emploi */}
                  <button 
                    onClick={() => { setShowSidebar(false); handleSelectContext('hub_emploi', 'Emploi', 'Emploi'); }}
                    className="flex flex-col items-center justify-center gap-[2px]" 
                    data-testid="sidebar-nav-emploi"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeContext === 'hub_emploi' ? '#F97316' : 'rgba(255,255,255,0.75)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                    </svg>
                    <span style={{ color: activeContext === 'hub_emploi' ? '#F97316' : 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: activeContext === 'hub_emploi' ? 600 : 500 }}>Emploi</span>
                  </button>
                </div>
              </div>
            )}
            
            {/* Safe area padding pour iPhone */}
            {isMobile && (
              <div style={{ height: 'env(safe-area-inset-bottom, 0px)', background: '#1E3A5F' }} />
            )}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════════════════════════════════════════
          ZONE CENTRALE - CHAT IA (structure avec barre fixe en bas)
          Desktop: Masqué quand un outil est en pleine page (isDesktopToolFullScreen)
          Mobile: PLEIN ÉCRAN quand sidebar et artifacts fermés
      ══════════════════════════════════════════════════════════════════════ */}
      {(isMobile || (!isDesktopToolFullScreen && !activeContext?.startsWith('page_'))) && (
      <div 
        className={`flex flex-col ${
          isMobile && !showSidebar && !showArtifacts
            ? 'fixed inset-x-0 z-40' 
            : isMobile 
              ? 'flex-1 min-w-0 relative'
              : 'flex-1 min-w-0 relative'
        } ${isDarkMode ? 'bg-[#0f172a]' : 'bg-white'}`}
        style={{
          top: (isMobile && !showSidebar && !showArtifacts) ? '94px' : 'auto', // Sous le TopBanner global (64px header + 30px bandeau)
          bottom: (isMobile && !showSidebar && !showArtifacts) ? '70px' : 'auto', // Espace pour la navbar du bas
        }}
      >
        {/* Header Chat - MASQUÉ sur mobile et en mode pleine page */}
        {!isMobile && !isDesktopToolFullScreen && (
        <header 
          className={`sticky top-0 z-10 flex items-center justify-between border-b ${isDarkMode ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100'}`}
          style={{ height: isMobile ? '64px' : '56px', paddingLeft: isMobile ? '20px' : '12px', paddingRight: isMobile ? '16px' : '12px' }}
        >
          <div className="flex items-center gap-2 md:gap-3">
            {/* Hamburger - mobile uniquement (sidebar toujours visible sur desktop) */}
            {isMobile && !showSidebar && (
              <button
                onClick={() => setShowSidebar(true)}
                className={`p-1 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                data-testid="hamburger-menu"
              >
                <Menu className="w-6 h-6" strokeWidth={1.8} style={{ color: isDarkMode ? 'white' : '#1E3A5F' }} />
              </button>
            )}
            
            {/* Nom de l'assistant - Simplifié sur mobile */}
            {!isMobile && (
              <div className="flex items-center gap-2">
                {isEditingName ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      className={`px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F97316] ${isDarkMode ? 'bg-[#0f172a] border-[#334155] text-white' : 'border-gray-200'}`}
                      autoFocus
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveAssistantName()}
                    />
                    <button onClick={handleSaveAssistantName} className={`p-1.5 rounded-lg ${isDarkMode ? 'text-green-400 hover:bg-green-900/30' : 'text-green-600 hover:bg-green-50'}`}>
                      <Check size={16} />
                    </button>
                    <button onClick={() => setIsEditingName(false)} className={`p-1.5 rounded-lg ${isDarkMode ? 'text-gray-400 hover:bg-white/10' : 'text-gray-400 hover:bg-gray-100'}`}>
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setTempName(assistantName); setIsEditingName(true); }}
                    className={`flex items-center gap-2 text-base font-semibold group transition-colors ${isDarkMode ? 'text-white hover:text-[#F97316]' : 'text-[#1E3A5F] hover:text-[#F97316]'}`}
                  >
                    <Sparkles size={18} className="text-[#F97316]" />
                    <span>{assistantName}</span>
                    <Pencil size={14} className={`opacity-0 group-hover:opacity-100 transition-opacity ${isDarkMode ? 'text-gray-500' : 'text-gray-300'}`} />
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* ═══════════════════════════════════════════════════════════════
              BARRE DE RECHERCHE - Desktop uniquement
              Style identique au site (loupe orange, bordure orange)
              Placeholder contextuel selon la page active
          ═══════════════════════════════════════════════════════════════ */}
          {!isMobile && (
            <div className="flex-1 max-w-xl mx-4">
              <div 
                className="flex items-center bg-white rounded-full border-2 border-[#E8650A] hover:shadow-md transition-shadow"
                style={{ height: '44px', paddingLeft: '4px', paddingRight: '16px' }}
              >
                {/* Loupe orange circulaire */}
                <button
                  type="button"
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ 
                    width: '36px', 
                    height: '36px', 
                    minWidth: '36px',
                    background: '#E8650A', 
                    borderRadius: '50%',
                  }}
                  data-testid="cockpit-search-btn"
                >
                  <Search size={16} className="text-white" />
                </button>
                {/* Input de recherche */}
                <input
                  type="text"
                  placeholder={
                    activeContext === 'page_artisans' || activeContext === 'page_artisans_5_etoiles' || activeContext === 'page_artisans_carte'
                      ? 'Rechercher un artisan, un métier, une ville...'
                      : activeContext?.startsWith('page_marketplace') || activeContext?.startsWith('page_produits') || activeContext?.startsWith('page_prestations') || activeContext?.startsWith('page_ventes') || activeContext?.startsWith('page_meilleures') || activeContext?.startsWith('page_nouveautes')
                        ? 'Rechercher un produit, une marque, un rayon...'
                        : activeContext?.startsWith('page_entre_particuliers') || activeContext?.startsWith('page_annonces') || activeContext === 'page_deposer_annonce'
                          ? 'Rechercher une annonce, outillage, matériaux...'
                          : activeContext?.startsWith('page_reseau')
                            ? 'Rechercher une actualité, un pro, un chantier...'
                            : activeContext?.startsWith('page_emploi')
                              ? 'Rechercher une offre, un métier, une ville...'
                              : 'Rechercher un artisan, un produit, une annonce...'
                  }
                  className="flex-1 bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-400 ml-3"
                  data-testid="cockpit-search-input"
                />
              </div>
            </div>
          )}
          
          {/* ═══════════════════════════════════════════════════════════════
              NAVBAR DROITE - Identique sur mobile et desktop
              Mobile: Coins, Dark Mode, Messages, Notifications, Profil
              Desktop: Notifications, Coins, Dark Mode
          ═══════════════════════════════════════════════════════════════ */}
          <div className="flex items-center gap-2">
            {/* Badge Superadmin - SUPPRIMÉ de la barre du haut */}
            {/* L'accès admin se fait uniquement via le menu gauche → Plateforme */}

            {/* Visiteur non connecté : Se connecter / S'inscrire à la place des icônes */}
            {!estConnecteAbra ? (
              <>
                <button
                  onClick={() => navigate('/connexion')}
                  className="h-9 px-4 rounded-full text-[13px] font-semibold text-white"
                  style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}
                  data-testid="topbar-login-btn"
                >
                  Se connecter
                </button>
                <button
                  onClick={() => navigate('/reseau/inscription')}
                  className="h-9 px-4 rounded-full text-[13px] font-semibold border border-[#1E3A5F]/30 text-[#1E3A5F] hover:bg-gray-50"
                  data-testid="topbar-register-btn"
                >
                  S'inscrire
                </button>
              </>
            ) : (
            <>
            {/* AbracadaCoins - Style compact identique aux autres pages */}
            <button 
              onClick={() => handleSelectContext('plateforme_coins', 'AbracaCoins', 'Plateforme')}
              className={`h-9 px-2.5 rounded-full flex items-center gap-1.5 ${
                isDarkMode 
                  ? 'bg-amber-900/30 border border-amber-700/40' 
                  : 'bg-[#FEF7ED] border border-[#F5E6D3]'
              }`}
              data-testid="abracadacoins-btn"
            >
              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <span className="text-[8px] font-bold text-white">A</span>
              </div>
              <span className={`text-[12px] font-semibold ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>4.3k</span>
            </button>
            
            {/* Panier - Toujours visible, à gauche des messages */}
            <button 
              onClick={() => navigate('/panier')}
              className={`relative ${isMobile ? 'p-1.5' : 'p-2 rounded-lg hover:bg-gray-100'}`}
              data-testid="cart-btn"
            >
              <ShoppingCart size={isMobile ? 22 : 18} className={isDarkMode ? 'text-gray-400' : isMobile ? 'text-[#1E3A5F]/70' : 'text-gray-500'} strokeWidth={1.6} />
              {/* Badge nombre d'articles */}
              {cartCount > 0 && (
                <span className={`absolute ${isMobile ? 'top-0.5 right-0.5' : 'top-1 right-1'} min-w-[16px] h-[16px] px-1 bg-[#F97316] rounded-full flex items-center justify-center`}>
                  <span className="text-[9px] font-bold text-white">{cartCount > 99 ? '99+' : cartCount}</span>
                </span>
              )}
            </button>
            
            {/* Messages - Visible sur Desktop (à gauche de la cloche) et Mobile */}
            <button 
              onClick={() => navigate('/messages')}
              className={`relative ${isMobile ? 'p-1.5' : 'p-2 rounded-lg hover:bg-gray-100'}`}
              data-testid="messages-btn"
            >
              <MessageCircle size={isMobile ? 22 : 18} className={isDarkMode ? 'text-gray-400' : isMobile ? 'text-[#1E3A5F]/70' : 'text-gray-500'} strokeWidth={1.6} />
              {/* Badge nombre de messages non lus */}
              <span className={`absolute ${isMobile ? 'top-0.5 right-0.5' : 'top-1 right-1'} min-w-[16px] h-[16px] px-1 bg-[#F97316] rounded-full flex items-center justify-center`}>
                <span className="text-[9px] font-bold text-white">3</span>
              </span>
            </button>
            
            {/* Notifications */}
            <button
              onClick={() => handleSelectContext('notifications', 'Notifications', 'Communication')}
              className={`relative p-1.5 ${!isMobile ? 'rounded-lg hover:bg-gray-100' : ''}`}
              data-testid="notifications-btn"
            >
              <Bell size={isMobile ? 22 : 18} className={isDarkMode ? 'text-gray-400' : isMobile ? 'text-[#1E3A5F]/70' : 'text-gray-500'} strokeWidth={1.6} />
              <span className={`absolute ${isMobile ? 'top-1 right-1' : 'top-1.5 right-1.5'} w-2 h-2 bg-[#F97316] rounded-full`} />
            </button>
            
            {/* Profil - Mobile uniquement */}
            {isMobile && (
              <button 
                onClick={() => handleSelectContext('parametres', 'Paramètres', 'Mon Compte')}
                className="relative p-1.5" 
                data-testid="profile-btn"
              >
                <User size={22} className={isDarkMode ? 'text-gray-400' : 'text-[#1E3A5F]/70'} strokeWidth={1.6} />
              </button>
            )}
            
            {/* Badge Formule - Desktop uniquement */}
            {!isMobile && <FormulaBadge isPro={userType === 'pro'} compact={false} />}
            </>
            )}
          </div>
        </header>
        )}
        
        {/* Menu de navigation principal - Desktop uniquement, masqué en mode pleine page */}
        {!isMobile && !isDesktopToolFullScreen && (
          isPro ? <CockpitProTopNav /> : (
            <CockpitMainNav
              onNavigate={(context, label) => handleSelectContext(context, label, 'Navigation')}
              activeContext={activeContext}
              onOpenRayonsMenu={() => setShowRayonsModal(true)}
              onOpenFilters={() => setShowFiltersModal(true)}
              onOpenArtisansFilters={() => setShowArtisansFiltersModal(true)}
              onOpenParticuliersCategories={() => setShowCategoriesModal(true)}
              onOpenParticuliersFilters={() => setShowParticuliersFiltersModal(true)}
              onOpenEmploiFilters={() => setShowEmploiFiltersModal(true)}
            />
          )
        )}

        {/* Zone des messages - SCROLLABLE avec padding pour la barre fixe */}
        <div className={`flex-1 overflow-y-auto scrollbar-hide p-6 pb-28 ${isDarkMode ? 'bg-[#0f172a]' : ''}`} data-testid="chat-messages-area">
          {/* ═══════════════════════════════════════════════════════════════════
              HUB PAGES - Affichés dans la zone centrale
              Sur mobile : sans barre de saisie, juste navbar en bas
          ═══════════════════════════════════════════════════════════════════ */}
          {activeContext?.startsWith('hub_') && messages.length === 0 ? (
            <>
              {activeContext === 'hub_espace' && (
                <HubMonEspace 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Mon Espace')}
                  pendingInvitationsCount={pendingInvitationsCount}
                  isMobile={isMobile}
                  onOpenMenu={() => setShowSidebar(true)}
                />
              )}
              {activeContext === 'hub_reseau' && (
                <HubReseauSocial 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Réseau Social')}
                  isMobile={isMobile}
                  onOpenMenu={() => setShowSidebar(true)}
                />
              )}
              {activeContext === 'hub_marketplace' && (
                <HubMarketplace 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Marketplace')}
                  isMobile={isMobile}
                  onOpenMenu={() => setShowSidebar(true)}
                />
              )}
              {activeContext === 'hub_prestataires' && (
                <HubPrestataires 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Prestataires')}
                  isMobile={isMobile}
                  onOpenMenu={() => setShowSidebar(true)}
                />
              )}
              {activeContext === 'hub_emploi' && (
                <HubEmploi 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Emploi')}
                  isMobile={isMobile}
                  onOpenMenu={() => setShowSidebar(true)}
                />
              )}
              {/* ════════════════════════════════════════════════════════════
                  COCKPIT PRO - Hubs
              ════════════════════════════════════════════════════════════ */}
              {activeContext === 'hub_espace_pro' && (
                <HubMonEspacePro 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Mon Espace')}
                  isMobile={isMobile}
                />
              )}
              {activeContext === 'hub_collaboration' && (
                <HubCollaboration 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Collaboration')}
                  isMobile={isMobile}
                />
              )}
              {activeContext === 'hub_commercial' && (
                <HubCommercial 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Commercial')}
                  isMobile={isMobile}
                />
              )}
              {activeContext === 'hub_catalogue' && (
                <HubCatalogue 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Catalogue')}
                  isMobile={isMobile}
                />
              )}
              {activeContext === 'hub_chantiers_pro' && (
                <HubChantiersPro 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Chantiers')}
                  isMobile={isMobile}
                />
              )}
              {activeContext === 'hub_marketing' && (
                <HubMarketing 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Marketing')}
                  isMobile={isMobile}
                />
              )}
              {activeContext === 'hub_reseau_pro' && (
                <HubReseauSocialPro 
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Réseau Social')}
                  isMobile={isMobile}
                />
              )}
            </>
          ) : activeContext?.startsWith('page_') ? (
            /* ═══════════════════════════════════════════════════════════════════
                PAGES EXTERNES - Navigation horizontale (Artisans, Produits, etc.)
                Affichées dans la zone centrale du cockpit
                - Masque UnifiedHeader et MainNavigation via CSS
                - Le contenu reste dans la zone blanche
            ═══════════════════════════════════════════════════════════════════ */
            <div className="cockpit-embedded-page overflow-auto" style={{
              /* Masquer les headers des pages externes */
              '--hide-external-headers': '1'
            }}>
              <style>{`
                .cockpit-embedded-page [data-testid="unified-header"],
                .cockpit-embedded-page header:first-of-type,
                .cockpit-embedded-page nav[data-testid="main-navigation"],
                .cockpit-embedded-page > div > header,
                .cockpit-embedded-page > div > nav {
                  display: none !important;
                }
                .cockpit-embedded-page .h-11.md\\:hidden,
                .cockpit-embedded-page .hidden.md\\:block[style*="height: 100px"] {
                  display: none !important;
                }
              `}</style>
              {/* Univers */}
              {activeContext === 'page_univers' && <UniversPage embedded={true} />}
              {activeContext === 'page_abracadaworld_home' && <AbracadaWorldHome onClose={() => {
                setIsDesktopToolFullScreen(false);
                setActiveContext('hub_espace');
                setActiveContextLabel('Mon Espace');
                setActiveEspaceLabel('Mon Espace');
                setMessages([]);
              }} />}
              {activeContext === 'page_accueil_bati' && <AccueilBatiPremium />}

              {/* Artisans */}
              {activeContext === 'page_artisans' && !isMobile && <ArtisansPage embedded={true} />}
              {activeContext === 'page_artisan_detail' && <ShopDetailPage onBack={() => handleSelectContext('page_artisans', 'Artisans', 'Prestataires')} />}
              {activeContext === 'page_artisans_5_etoiles' && <Artisans5EtoilesPage embedded={true} />}
              {activeContext === 'page_artisans_carte' && <ArtisansMapView embedded={true} sidebarWidth={sidebarCollapsed ? 70 : 300} onBack={() => handleSelectContext('page_artisans', 'Artisans', 'Prestataires')} />}
              {activeContext === 'page_decrire_projet' && <DecrireProjetPage embedded={true} onBack={() => handleSelectContext('page_artisans', 'Artisans', 'Prestataires')} />}

              {/* Marketplace (Acheter à un pro) */}
              {activeContext === 'page_marketplace' && <MarketplaceProduitsPage embedded={true} />}
              {activeContext === 'page_produits' && <MarketplaceProduitsPage embedded={true} filter="produits" />}
              {activeContext === 'page_prestations' && <MarketplaceProduitsPage embedded={true} filter="prestations" />}
              {activeContext === 'page_ventes_flash' && <MarketplaceProduitsPage embedded={true} filter="ventes-flash" />}
              {activeContext === 'page_meilleures_ventes' && <MarketplaceProduitsPage embedded={true} filter="meilleures-ventes" />}
              {activeContext === 'page_nouveautes' && <MarketplaceProduitsPage embedded={true} filter="nouveautes" />}
              
              {/* Entre Particuliers */}
              {activeContext === 'page_entre_particuliers' && <EntreParticuliersPage embedded={true} />}
              {activeContext === 'page_annonces_recentes' && <EntreParticuliersPage embedded={true} filter="recentes" />}
              {activeContext === 'page_annonces_urgentes' && <EntreParticuliersPage embedded={true} filter="urgentes" />}
              {activeContext === 'page_deposer_annonce' && <DeposerAnnoncePage onSelectTool={handleSelectContext} />}
              
              {/* Réseau Social */}
              {activeContext === 'page_reseau' && <ActuPage embedded={true} />}
              {activeContext === 'page_reseau_cinema' && <DesktopCinemaPage embedded={true} />}
              {activeContext === 'page_reseau_galerie' && <DesktopGaleriePage embedded={true} />}
              {activeContext === 'page_reseau_salon' && <DesktopSalonPage embedded={true} />}
              {activeContext === 'page_reseau_evenements' && <DesktopEvenementsPage embedded={true} />}
              
              {/* Emploi */}
              {activeContext === 'page_emploi' && <EmploiUnifiedPage />}
              {activeContext === 'page_emploi_entreprises' && <EmploiUnifiedPage defaultTab="entreprises" />}
              {activeContext === 'page_deposer_cv' && <EmploiUnifiedPage defaultTab="cv" />}
              
              {/* Espace Pro - Redirige vers le hub pro */}
              {activeContext === 'page_espace_pro' && (
                <HubMonEspacePro
                  onSelectTool={(context, label) => handleSelectContext(context, label, 'Mon Espace')}
                  isMobile={isMobile}
                />
              )}

              {/* Cockpit Pro — Gestion Catalogue */}
              {activeContext === 'page_pro_catalogue' && <CockpitProDashboard />}
              {activeContext === 'page_pro_produits' && <ProduitsPage />}
              {activeContext === 'page_pro_prestations' && <PrestationsPage />}
              {activeContext === 'page_pro_tarifs' && <HourlyRatesPage />}
              {activeContext === 'page_pro_import' && <ImportCSVPage />}

              {/* Cockpit Pro — Boutique */}
              {activeContext === 'page_pro_boutique' && <BoutiqueProPage />}
            </div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-start text-center px-4 pt-8" data-testid="chat-welcome-screen">
              
              {/* ═══════════════════════════════════════════════════════════════
                  NOUVEAU : Bouton Micro Central - Ouvre VoiceNotes
              ═══════════════════════════════════════════════════════════════ */}
              <div
                onClick={() => setShowVoiceNotes(true)}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '24px',
                  background: isDarkMode 
                    ? 'linear-gradient(135deg, #F97316 0%, #ea580c 100%)'
                    : 'linear-gradient(135deg, #1E3A5F 0%, #0d1b2e 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: isDarkMode 
                    ? '0 8px 32px rgba(249, 115, 22, 0.35)'
                    : '0 8px 32px rgba(30, 58, 95, 0.35)',
                  marginBottom: '16px',
                  transition: 'all 0.3s ease',
                }}
                className="hover:scale-105 active:scale-95"
                data-testid="welcome-mic-button"
              >
                <Mic size={32} className="text-white" />
              </div>
              
              <h2 style={{
                fontSize: '20px',
                fontWeight: '800',
                color: isDarkMode ? '#ffffff' : '#1E3A5F',
                marginBottom: '8px',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}>
                Bonjour {userName} !
              </h2>
              <p style={{
                fontSize: '14px',
                color: isDarkMode ? '#94a3b8' : '#9CA3AF',
                lineHeight: '1.5',
                maxWidth: '280px',
                marginBottom: '24px',
              }}>
                {activeContextLabel 
                  ? `Tu es dans ${activeEspaceLabel} > ${activeContextLabel}. Comment puis-je t'aider ?`
                  : 'Appuie sur le micro pour parler, ou écris ton message ci-dessous'
                }
              </p>
              
              {/* ═══════════════════════════════════════════════════════════════
                  NOUVEAU : 6 Boutons d'actions rapides (Pro)
              ═══════════════════════════════════════════════════════════════ */}
              {!activeContext && (
                <>
                  {/* Séparateur */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    maxWidth: '560px',
                    marginBottom: '16px',
                  }}>
                    <div style={{ flex: 1, height: '0.5px', background: isDarkMode ? '#334155' : '#e8ecf5' }} />
                    <span style={{ fontSize: '11px', color: isDarkMode ? '#64748b' : '#9CA3AF', fontWeight: '600' }}>
                      Actions rapides
                    </span>
                    <div style={{ flex: 1, height: '0.5px', background: isDarkMode ? '#334155' : '#e8ecf5' }} />
                  </div>
                  
                  {/* Grille 6 boutons */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '10px',
                    width: '100%',
                    maxWidth: '560px',
                  }} className="sm:grid-cols-3 grid-cols-2" data-testid="chat-suggestions">
                    {[
                      { id: 'compte-rendu', icon: '🎙️', label: 'Compte rendu', desc: 'Parle, je rédige', bg: isDarkMode ? '#1e293b' : '#fff3ec', border: isDarkMode ? '#334155' : '#fcd9b8', isMicAction: true },
                      { id: 'devis', icon: '📄', label: 'Créer un devis', desc: 'Génère en questions', bg: isDarkMode ? '#1e293b' : '#f0f3f8', border: isDarkMode ? '#334155' : '#d0d8e8', prompt: 'Je veux créer un devis' },
                      { id: 'agenda', icon: '📅', label: 'Mon agenda', desc: 'Planning du jour', bg: isDarkMode ? '#1e293b' : '#eff6ff', border: isDarkMode ? '#334155' : '#bfdbfe', prompt: "Montre-moi mon agenda" },
                      { id: 'clients', icon: '👥', label: 'Prospects & clients', desc: 'Suivi, relances', bg: isDarkMode ? '#1e293b' : '#f0fdf4', border: isDarkMode ? '#334155' : '#bbf7d0', prompt: 'Montre-moi mes prospects et clients' },
                      { id: 'mails', icon: '✉️', label: 'Boîte mail', desc: 'Tous tes mails', bg: isDarkMode ? '#1e293b' : '#fdf4ff', border: isDarkMode ? '#334155' : '#e9d5ff', prompt: 'Ouvre ma boîte mail' },
                      { id: 'messages', icon: '💬', label: 'Messages', desc: 'Commentaires', bg: isDarkMode ? '#1e293b' : '#fff8f4', border: isDarkMode ? '#334155' : '#fcd9b8', prompt: 'Montre-moi mes messages' },
                    ].map((action) => (
                      <button
                        key={action.id}
                        data-testid={`suggestion-btn-${action.id}`}
                        onClick={() => {
                          if (action.isMicAction) {
                            setShowVoiceNotes(true);
                          } else {
                            setInputValue(action.prompt);
                            setTimeout(() => inputRef.current?.focus(), 100);
                          }
                        }}
                        style={{
                          background: action.bg,
                          border: `1px solid ${action.border}`,
                          borderRadius: '12px',
                          padding: '12px 14px',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: '6px',
                          textAlign: 'left',
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                          fontFamily: "'Plus Jakarta Sans', 'DM Sans', sans-serif",
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = isDarkMode 
                            ? '0 4px 16px rgba(0,0,0,0.3)'
                            : '0 4px 16px rgba(0,0,0,0.08)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '9px',
                          background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(30, 58, 95, 0.06)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                        }}>
                          {action.icon}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: isDarkMode ? '#ffffff' : '#1E3A5F',
                          lineHeight: '1.3',
                        }}>
                          {action.label}
                        </div>
                        <div style={{
                          fontSize: '10px',
                          color: isDarkMode ? '#94a3b8' : '#9CA3AF',
                          lineHeight: '1.4',
                        }}>
                          {action.desc}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-5" data-testid="chat-messages-list">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  data-testid={`chat-message-${msg.role}-${idx}`}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'system' ? (
                    <div className="w-full">
                      <div className={`px-5 py-4 rounded-xl text-sm border ${isDarkMode ? 'bg-white/5 text-gray-300 border-white/10' : 'bg-[#1E3A5F]/5 text-[#1E3A5F]/80 border-[#1E3A5F]/10'}`}>
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                      {/* Suggestions cliquables sous le message système */}
                      {msg.suggestions && msg.suggestions.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {msg.suggestions.map((suggestion, sIdx) => (
                            <button
                              key={sIdx}
                              onClick={() => {
                                setInputValue(suggestion);
                                inputRef.current?.focus();
                              }}
                              className="px-4 py-2 text-sm bg-white hover:bg-[#F97316]/10 border border-gray-200 hover:border-[#F97316] rounded-xl transition-all duration-200 text-gray-700 hover:text-[#1E3A5F]"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : msg.role === 'user' ? (
                    <div className="max-w-[75%]">
                      {/* Afficher les attachments si présents */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2 justify-end">
                          {msg.attachments.map((att, attIdx) => (
                            <div key={attIdx} className="relative">
                              {att.type === 'image' && att.url ? (
                                <img 
                                  src={att.url} 
                                  alt={att.name}
                                  className="max-w-[200px] max-h-[150px] rounded-xl object-cover border border-white/20"
                                />
                              ) : (
                                <div className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg">
                                  {att.type === 'video' && <Film size={16} className="text-purple-300" />}
                                  {att.type === 'pdf' && <FileText size={16} className="text-red-300" />}
                                  {att.type === 'document' && <File size={16} className="text-green-300" />}
                                  <span className="text-xs text-white/80 truncate max-w-[150px]">{att.name}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="px-5 py-3 rounded-2xl rounded-tr-sm bg-[#1E3A5F] text-white text-sm shadow-lg">
                        {msg.content}
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-[75%] flex gap-3">
                      <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] shadow-md">
                        <Sparkles size={16} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="px-5 py-4 bg-white border border-gray-100 rounded-2xl rounded-tl-sm text-sm text-gray-700 shadow-sm">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                        
                        {/* Actions sur le message */}
                        <div className="flex items-center gap-1 mt-2 ml-2">
                          <button
                            onClick={() => navigator.clipboard.writeText(msg.content)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Copier"
                          >
                            <Copy size={14} className="text-gray-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="flex gap-3">
                    <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a]">
                      <Loader2 size={16} className="text-white animate-spin" />
                    </div>
                    <div className="px-5 py-4 bg-white border border-gray-100 rounded-2xl rounded-tl-sm shadow-sm">
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            ZONE DE SAISIE - STYLE CHATGPT - FIXE EN BAS
            Masquée sur mobile quand le Hub est affiché
        ═══════════════════════════════════════════════════════════════════ */}
        <div 
          className={`absolute bottom-0 left-0 right-0 z-20 ${isDarkMode ? 'bg-[#0f172a]' : 'bg-white'}`}
        >
          {/* Barre de saisie - Masquée sur mobile quand le Hub est affiché */}
          {!(isMobile && activeContext?.startsWith('hub_') && messages.length === 0) && (
          <>
          {/* Conteneur pour la barre de saisie avec padding */}
          <div className="px-4 pt-3 pb-3">
            <div className="max-w-3xl mx-auto">
              {/* Affichage des fichiers uploadés en attente */}
              {uploadedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {uploadedFiles.map(file => (
                    <div key={file.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border shadow-sm ${isDarkMode ? 'bg-[#1e293b] border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                      {file.type === 'image' && <ImageIcon size={14} className="text-blue-500" />}
                      {file.type === 'video' && <Film size={14} className="text-purple-500" />}
                      {file.type === 'pdf' && <FileText size={14} className="text-red-500" />}
                      {file.type === 'document' && <File size={14} className="text-green-500" />}
                      <span className={`text-xs truncate max-w-[150px] ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{file.name}</span>
                      <button onClick={() => removeUploadedFile(file.id)} className="text-gray-400 hover:text-red-500">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            
            {/* ═══ BARRE DE SAISIE CHATGPT - PIXEL PERFECT ═══ */}
            <div 
              className="flex items-center rounded-full"
              style={{ 
                height: '52px',
                paddingLeft: '16px',
                paddingRight: '8px',
                backgroundColor: isDarkMode ? '#2d3748' : '#ececec',
                border: '2px solid #F97316'
              }}
            >
              
              {/* Bouton + */}
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setShowAttachMenu(!showAttachMenu)}
                  disabled={isUploading}
                  className={`flex items-center justify-center ${
                    isUploading ? 'opacity-50' : ''
                  } ${isDarkMode ? 'text-gray-300' : 'text-[#676767]'}`}
                >
                  {isUploading ? (
                    <Loader2 size={24} className="animate-spin" />
                  ) : (
                    <Plus size={24} strokeWidth={2} />
                  )}
                </button>
                
                {/* Menu dropdown pour les attachments */}
                <AnimatePresence>
                  {showAttachMenu && (
                    <>
                      {/* Overlay pour fermer en cliquant ailleurs */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowAttachMenu(false)}
                      />
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute bottom-full left-0 mb-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
                      >
                        <div className="p-2">
                          {/* Header avec bouton fermer */}
                          <div className="flex items-center justify-between px-3 py-1.5">
                            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                              Importer
                            </p>
                            <button
                              onClick={() => setShowAttachMenu(false)}
                              className="w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                            >
                              <X size={14} className="text-gray-400" />
                            </button>
                          </div>
                          <button
                            onClick={() => {
                              fileInputRef.current?.setAttribute('accept', 'image/*');
                              fileInputRef.current?.click();
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                              <ImageIcon size={16} className="text-blue-600" />
                            </div>
                            <div className="text-left">
                              <p className="text-sm font-medium text-gray-800">Photos</p>
                              <p className="text-xs text-gray-500">Images, captures</p>
                            </div>
                          </button>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', '.pdf,.doc,.docx,.xls,.xlsx');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                            <FileText size={16} className="text-red-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-800">Documents</p>
                            <p className="text-xs text-gray-500">PDF, Word, Excel</p>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', 'video/*');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Film size={16} className="text-purple-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-800">Vidéos</p>
                            <p className="text-xs text-gray-500">MP4, MOV</p>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', '*/*');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Paperclip size={16} className="text-gray-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-800">Autres fichiers</p>
                            <p className="text-xs text-gray-500">Tous types</p>
                          </div>
                        </button>
                      </div>
                    </motion.div>
                    </>
                  )}
                </AnimatePresence>
                
                {/* Input file caché */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
              
              {/* Texte - DIRECTEMENT dans la barre, aucune bordure, aucun fond différent */}
              <input
                ref={inputRef}
                data-testid="chat-input"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                placeholder={activeContextLabel 
                  ? `${activeContextLabel}...`
                  : 'Écris ta demande...'
                }
                className="flex-1 bg-transparent border-none outline-none shadow-none appearance-none"
                style={{
                  marginLeft: '4px',
                  marginRight: '8px',
                  fontSize: '16px',
                  fontWeight: '400',
                  color: isDarkMode ? '#fff' : '#1a1a1a',
                  caretColor: isDarkMode ? '#fff' : '#1a1a1a'
                }}
                disabled={isLoading || isUploading}
              />
              
              {/* Micro gris */}
              <button
                data-testid="btn-voice-notes"
                onClick={() => setShowVoiceNotes(true)}
                className="flex-shrink-0"
                style={{ marginLeft: '8px' }}
              >
                <Mic size={24} strokeWidth={1.5} className={isDarkMode ? 'text-gray-400' : 'text-[#9a9a9a]'} />
              </button>
              
              {/* Bouton Envoyer orange */}
              <button
                data-testid="btn-send-message"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="flex-shrink-0 rounded-full flex items-center justify-center bg-[#E8650A] hover:bg-[#d55a09] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ width: '40px', height: '40px' }}
              >
                <ArrowUp size={20} strokeWidth={2.5} />
              </button>
            </div>
            </div>
          </div>
          </>
          )}
          
          {/* ═══ NAVBAR MOBILE - Gérée globalement par MobileNavWrapper dans App.js ═══ */}
          {/* La navbar standard (Actu, Boutiques, Cockpit, Acheter, Plus) s'affiche automatiquement */}
        </div>
      </div>
      )}

      {/* ══ ABRACADAWORLD UNIVERS - Pleine page sans header ══ */}
      {!isMobile && activeContext === 'page_abracadaworld_home' && (
        <div className="flex-1 overflow-auto" style={{background:'#FFF8F4'}}>
          <AbracadaWorldHome onClose={() => {
            setIsDesktopToolFullScreen(false);
            setActiveContext('hub_espace');
            setActiveContextLabel('Mon Espace');
            setActiveEspaceLabel('Mon Espace');
            setMessages([]);
          }} />
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PAGES EXTERNES EN MODE PLEINE PAGE - Desktop uniquement
          Affichées quand isDesktopToolFullScreen ET contexte commence par page_
          Avec navigation horizontale en haut et bouton flottant "Demande à Abra"
      ══════════════════════════════════════════════════════════════════════ */}
      {!isMobile && activeContext?.startsWith('page_') && activeContext !== 'page_abracadaworld_home' && (
        <div 
          className="flex-1 flex flex-col overflow-hidden bg-white"
          data-testid="external-page-fullscreen"
        >
          {/* Header avec navigation horizontale */}
          <div className="sticky top-0 z-20 bg-white border-b border-gray-100">
            {/* Header complet identique au mode chat */}
            <div className="flex items-center justify-between px-4" style={{ height: '56px', paddingLeft: '12px', paddingRight: '12px' }}>
              {/* Gauche : bouton Fermer + logo AbracadaBati */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setActiveContext('hub_espace');
                    setActiveContextLabel('Mon Espace');
                    setActiveEspaceLabel('Mon Espace');
                    setMessages([]);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium transition-colors"
                  title="Fermer et revenir à l'assistant"
                >
                  <ArrowLeft size={15} />
                  <span>Fermer</span>
                </button>
                <img
                  src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/4450nlnf_abracadabait_logo_transparent.png"
                  alt="AbracadaBati"
                  style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
                />
              </div>

              {/* Centre : barre de recherche */}
              <div className="flex-1 max-w-xl mx-4">
                <div
                  className="flex items-center bg-white rounded-full border-2 border-[#E8650A] hover:shadow-md transition-shadow"
                  style={{ height: '44px', paddingLeft: '4px', paddingRight: '16px' }}
                >
                  <button
                    type="button"
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ width: '36px', height: '36px', minWidth: '36px', background: '#E8650A', borderRadius: '50%' }}
                  >
                    <Search size={16} className="text-white" />
                  </button>
                  <input
                    type="text"
                    placeholder={
                      activeContext === 'page_artisans' || activeContext === 'page_artisans_5_etoiles' || activeContext === 'page_artisans_carte'
                        ? 'Rechercher un artisan, un métier, une ville...'
                        : activeContext?.startsWith('page_marketplace') || activeContext?.startsWith('page_produits') || activeContext?.startsWith('page_prestations') || activeContext?.startsWith('page_ventes') || activeContext?.startsWith('page_meilleures') || activeContext?.startsWith('page_nouveautes')
                          ? 'Rechercher un produit, une marque, un rayon...'
                          : activeContext?.startsWith('page_entre_particuliers') || activeContext?.startsWith('page_annonces')
                            ? 'Rechercher une annonce, outillage, matériaux...'
                            : activeContext?.startsWith('page_reseau')
                              ? 'Rechercher une actualité, un pro, un chantier...'
                              : activeContext?.startsWith('page_emploi')
                                ? 'Rechercher une offre, un métier, une ville...'
                                : 'Rechercher un artisan, un produit, une annonce...'
                    }
                    className="flex-1 bg-transparent border-none outline-none text-sm text-gray-700 placeholder-gray-400 ml-3"
                  />
                </div>
              </div>

              {/* Droite : icônes identiques au header normal */}
              <div className="flex items-center gap-2">
                {/* Visiteur non connecté : Se connecter / S'inscrire à la place des icônes */}
                {!estConnecteAbra ? (
                  <>
                    <button
                      onClick={() => navigate('/connexion')}
                      className="h-9 px-4 rounded-full text-[13px] font-semibold text-white"
                      style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}
                    >
                      Se connecter
                    </button>
                    <button
                      onClick={() => navigate('/reseau/inscription')}
                      className="h-9 px-4 rounded-full text-[13px] font-semibold border border-[#1E3A5F]/30 text-[#1E3A5F] hover:bg-gray-50"
                    >
                      S'inscrire
                    </button>
                  </>
                ) : (
                <>
                <button
                  onClick={() => handleSelectContext('plateforme_coins', 'AbracaCoins', 'Plateforme')}
                  className="h-9 px-2.5 rounded-full flex items-center gap-1.5 bg-[#FEF7ED] border border-[#F5E6D3]"
                >
                  <div className="w-4 h-4 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <span className="text-[8px] font-bold text-white">A</span>
                  </div>
                  <span className="text-[12px] font-semibold text-amber-700">4.3k</span>
                </button>
                <button
                  onClick={() => navigate('/panier')}
                  className="relative p-2 rounded-lg hover:bg-gray-100"
                >
                  <ShoppingCart size={18} className="text-gray-500" strokeWidth={1.6} />
                  {cartCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 bg-[#F97316] rounded-full flex items-center justify-center">
                      <span className="text-[9px] font-bold text-white">{cartCount > 99 ? '99+' : cartCount}</span>
                    </span>
                  )}
                </button>
                <button
                  onClick={() => navigate('/messages')}
                  className="relative p-2 rounded-lg hover:bg-gray-100"
                >
                  <MessageCircle size={18} className="text-gray-500" strokeWidth={1.6} />
                  <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 bg-[#F97316] rounded-full flex items-center justify-center">
                    <span className="text-[9px] font-bold text-white">3</span>
                  </span>
                </button>
                <button
                  onClick={() => handleSelectContext('notifications', 'Notifications', 'Communication')}
                  className="relative p-1.5 rounded-lg hover:bg-gray-100"
                >
                  <Bell size={18} className="text-gray-500" strokeWidth={1.6} />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#F97316] rounded-full" />
                </button>
                <FormulaBadge isPro={userType === 'pro'} compact={false} />
                </>
                )}
                {/* Bouton plein écran : collapse sidebar → bande bleue icônes */}
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="ml-1 p-2 rounded-lg bg-[#F97316] hover:bg-[#EA580C] text-white transition-colors"
                  title={sidebarCollapsed ? 'Réduire le plein écran' : 'Plein écran'}
                >
                  {sidebarCollapsed ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
              </div>
            </div>

            {/* Navigation horizontale */}
            {isPro ? <CockpitProTopNav /> : (
              <CockpitMainNav
                onNavigate={(context, label) => handleSelectContext(context, label, 'Navigation')}
                activeContext={activeContext}
                onOpenRayonsMenu={() => setShowRayonsModal(true)}
                onOpenFilters={() => setShowFiltersModal(true)}
                onOpenArtisansFilters={() => setShowArtisansFiltersModal(true)}
                onOpenParticuliersCategories={() => setShowCategoriesModal(true)}
                onOpenParticuliersFilters={() => setShowParticuliersFiltersModal(true)}
                onOpenEmploiFilters={() => setShowEmploiFiltersModal(true)}
              />
            )}
          </div>
          
          {/* Contenu de la page externe */}
          <div className="flex-1 overflow-auto cockpit-embedded-page">
            <style>{`
              .cockpit-embedded-page [data-testid="unified-header"],
              .cockpit-embedded-page header:first-of-type,
              .cockpit-embedded-page nav[data-testid="main-navigation"],
              .cockpit-embedded-page > div > header,
              .cockpit-embedded-page > div > nav {
                display: none !important;
              }
            `}</style>
            
            {/* Accueil AbracadaBati */}
            {activeContext === 'page_accueil_bati' && <AccueilBatiPremium />}

            {/* Univers */}
            {activeContext === 'page_univers' && <UniversPage embedded={true} />}
            
            {/* Artisans */}
            {activeContext === 'page_artisans' && !isMobile && <ArtisansPage embedded={true} />}
            {activeContext === 'page_artisan_detail' && <ShopDetailPage onBack={() => handleSelectContext('page_artisans', 'Artisans', 'Prestataires')} />}
            {activeContext === 'page_artisans_5_etoiles' && <Artisans5EtoilesPage embedded={true} />}
            {activeContext === 'page_artisans_carte' && <ArtisansMapView embedded={true} sidebarWidth={sidebarCollapsed ? 70 : 300} onBack={() => handleSelectContext('page_artisans', 'Artisans', 'Prestataires')} />}
            {activeContext === 'page_decrire_projet' && <DecrireProjetPage embedded={true} onBack={() => handleSelectContext('page_artisans', 'Artisans', 'Prestataires')} />}

            {/* Marketplace */}
            {activeContext === 'page_marketplace' && <MarketplaceProduitsPage embedded={true} />}
            {activeContext === 'page_produits' && <MarketplaceProduitsPage embedded={true} filter="produits" />}
            {activeContext === 'page_prestations' && <MarketplaceProduitsPage embedded={true} filter="prestations" />}
            {activeContext === 'page_ventes_flash' && <MarketplaceProduitsPage embedded={true} filter="ventes-flash" />}
            {activeContext === 'page_meilleures_ventes' && <MarketplaceProduitsPage embedded={true} filter="meilleures-ventes" />}
            {activeContext === 'page_nouveautes' && <MarketplaceProduitsPage embedded={true} filter="nouveautes" />}
            
            {/* Entre Particuliers */}
            {activeContext === 'page_entre_particuliers' && <EntreParticuliersPage embedded={true} />}
            {activeContext === 'page_annonces_recentes' && <EntreParticuliersPage embedded={true} filter="recentes" />}
            {activeContext === 'page_annonces_urgentes' && <EntreParticuliersPage embedded={true} filter="urgentes" />}
            {activeContext === 'page_deposer_annonce' && <DeposerAnnoncePage onSelectTool={handleSelectContext} />}
            
            {/* Réseau Social */}
            {activeContext === 'page_reseau' && <ActuPage embedded={true} />}
            {activeContext === 'page_reseau_cinema' && <DesktopCinemaPage embedded={true} />}
            {activeContext === 'page_reseau_galerie' && <DesktopGaleriePage embedded={true} />}
            {activeContext === 'page_reseau_salon' && <DesktopSalonPage embedded={true} />}
            {activeContext === 'page_reseau_evenements' && <DesktopEvenementsPage embedded={true} />}
            
            {/* Emploi */}
            {activeContext === 'page_emploi' && <EmploiUnifiedPage />}
            {activeContext === 'page_emploi_entreprises' && <EmploiUnifiedPage defaultTab="entreprises" />}
            {activeContext === 'page_deposer_cv' && <EmploiUnifiedPage defaultTab="cv" />}
            
            {/* Espace Pro */}
            {activeContext === 'page_espace_pro' && (
              <HubMonEspacePro
                onSelectTool={(context, label) => handleSelectContext(context, label, 'Mon Espace')}
                isMobile={isMobile}
              />
            )}

            {/* Cockpit Pro — Gestion Catalogue */}
            {activeContext === 'page_pro_catalogue' && <CockpitProDashboard />}
            {activeContext === 'page_pro_produits' && <ProduitsPage />}
            {activeContext === 'page_pro_prestations' && <PrestationsPage />}
            {activeContext === 'page_pro_tarifs' && <HourlyRatesPage />}
            {activeContext === 'page_pro_import' && <ImportCSVPage />}

            {/* Cockpit Pro — Boutique */}
            {activeContext === 'page_pro_boutique' && <BoutiqueProPage />}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PANNEAU DROIT - ARTIFACTS (Design Premium)
          Desktop: Largeur fixe 720px OU pleine largeur si outil en mode pleine page
          Mobile: Plein écran en overlay
      ══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showArtifacts && (
          <motion.aside
            data-testid="right-panel-artifacts"
            initial={{ x: isMobile ? '100%' : 0, width: isMobile ? '100%' : 0, opacity: 0 }}
            animate={{ 
              x: 0, 
              width: isMobile ? '100%' : (isDesktopToolFullScreen ? '100%' : 720), 
              opacity: 1 
            }}
            exit={{ x: isMobile ? '100%' : 0, width: isMobile ? '100%' : 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className={`h-full flex flex-col overflow-hidden ${isMobile ? 'fixed inset-x-0 top-[94px] z-50' : 'relative border-l flex-1'} ${isDarkMode ? 'border-[#334155] bg-[#0f172a]' : 'border-gray-100 bg-white'}`}
            style={{ 
              minWidth: isMobile ? '100%' : (isDesktopToolFullScreen ? 'auto' : 720), 
              maxWidth: isMobile ? '100%' : (isDesktopToolFullScreen ? '100%' : 720),
              bottom: isMobile ? '60px' : 'auto', // Espace pour la navbar du bas
            }}
          >
            {/* Header Desktop uniquement - Mobile n'a pas besoin de header ici, les pages ont le leur */}
            {!isMobile && (
              <div className={`h-14 px-4 flex items-center justify-between border-b ${isDarkMode ? 'border-[#334155] bg-[#1e293b]' : 'border-gray-100 bg-white'}`}>
                {/* Desktop Pleine Page: Bouton Hamburger + Titre de l'outil */}
                {isDesktopToolFullScreen && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowSidebar(!showSidebar)}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                      data-testid="fullscreen-hamburger"
                    >
                      <Menu className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-[#1E3A5F]'}`} />
                    </button>
                    {activeContextLabel && (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => {
                            const espaceHubs = {
                              'Mon Espace': 'hub_espace',
                              'Marketplace': 'hub_marketplace',
                              'Prestataires': 'hub_artisans',
                              'Réseau Social': 'hub_reseau',
                              'Emploi': 'hub_emploi',
                            };
                            const hubContext = espaceHubs[activeEspaceLabel] || 'hub_espace';
                            handleSelectContext(hubContext, activeEspaceLabel, activeEspaceLabel);
                          }}
                          className={`text-sm hover:underline ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-[#1E3A5F]'}`}
                        >
                          {activeEspaceLabel}
                        </button>
                        <ChevronRight size={14} className={isDarkMode ? 'text-gray-500' : 'text-gray-400'} />
                        <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-[#1E3A5F]'}`}>{activeContextLabel}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Mode classique Desktop: Titre "Résultats" */}
                {!isDesktopToolFullScreen && (
                  <h2 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-[#1E3A5F]'}`} data-testid="right-panel-title">Résultats</h2>
                )}
                
                {/* Actions globales Desktop */}
                <div className="flex items-center gap-1">
                  {/* Desktop Pleine Page: Afficher mode sombre, messages, notifications, formule */}
                  {isDesktopToolFullScreen && (
                    <>
                      {/* Dark Mode Toggle */}
                      <ThemeToggleMini />
                      
                      {/* Messages avec badge */}
                      <button 
                        onClick={() => navigate('/messages')}
                        className={`relative p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                        title="Messages"
                        data-testid="messages-btn-fullscreen"
                      >
                        <MessageCircle className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} strokeWidth={1.6} />
                        <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 bg-[#F97316] rounded-full flex items-center justify-center">
                          <span className="text-[9px] font-bold text-white">3</span>
                        </span>
                      </button>
                      
                      {/* Notifications */}
                      <button
                        onClick={() => handleSelectContext('notifications', 'Notifications', 'Communication')}
                        className={`relative p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                        title="Notifications"
                      >
                        <Bell className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full" />
                      </button>
                      
                      <div className={`w-px h-5 mx-1 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`} />
                      
                      {/* Badge Formule + AbracaCoins */}
                      <FormulaBadge isPro={userType === 'pro'} compact={false} />
                    </>
                  )}
                  
                  {/* Desktop Mode classique: Boutons artifact */}
                  {activeArtifact && !isDesktopToolFullScreen && (
                    <>
                      <button
                        onClick={() => {
                          const event = new CustomEvent('artifact-edit', { detail: activeArtifact.id });
                          window.dispatchEvent(event);
                        }}
                        className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
                        title="Modifier"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          const content = JSON.stringify(activeArtifact.data, null, 2);
                          navigator.clipboard.writeText(content);
                          toast.success('Copié dans le presse-papier');
                        }}
                        className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
                        title="Copier"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          const event = new CustomEvent('artifact-download', { detail: activeArtifact });
                          window.dispatchEvent(event);
                        }}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                        title="Télécharger PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <div className="w-px h-4 bg-gray-200 mx-1" />
                      <button
                        onClick={() => {
                          const event = new CustomEvent('artifact-send-email', { detail: activeArtifact });
                          window.dispatchEvent(event);
                        }}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                        title="Envoyer par mail"
                      >
                        <Mail className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          const event = new CustomEvent('artifact-send-message', { detail: activeArtifact });
                          window.dispatchEvent(event);
                          toast.info('Envoyer par message - Sélectionne un contact');
                        }}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                        title="Envoyer par message"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </button>
                      <div className="w-px h-4 bg-gray-200 mx-1" />
                    </>
                  )}
                  {/* Bouton fermer - Seulement en mode classique Desktop (pas pleine page) */}
                  {!isDesktopToolFullScreen && (
                    <button
                      onClick={() => setShowArtifacts(false)}
                      className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                      title="Fermer"
                    >
                      <X className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* Contenu Artifacts */}
            <div className="flex-1 overflow-y-auto scrollbar-hide" data-testid="right-panel-content">
              {artifacts.length > 0 ? (
                <div className="p-3" data-testid="artifact-container">
                  {/* Onglets si plusieurs artifacts */}
                  {artifacts.length > 1 && (
                    <div className="flex gap-1 mb-3 overflow-x-auto pb-2" data-testid="artifact-tabs">
                      {artifacts.map((artifact, idx) => (
                        <button
                          key={artifact.id}
                          data-testid={`artifact-tab-${artifact.id}`}
                          onClick={() => setActiveArtifact(artifact)}
                          className={`
                            px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors
                            ${activeArtifact?.id === artifact.id 
                              ? 'bg-gray-200 text-gray-800' 
                              : 'text-gray-500 hover:bg-gray-100'
                            }
                          `}
                        >
                          {artifact.title}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Contenu de l'artifact actif */}
                  <div data-testid={`artifact-content-${activeArtifact?.type || 'none'}`}>
                    {activeArtifact && renderArtifactContent(activeArtifact)}
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center p-6" data-testid="artifact-empty-state">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                    <FolderOpen size={32} className="text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500 mb-2">Aucun résultat</p>
                  <p className="text-xs text-gray-400">
                    Les devis, tâches et autres résultats s'afficheront ici
                  </p>
                </div>
              )}
            </div>
            
            {/* ══════════════════════════════════════════════════════════════════════
                BOUTON FLOTTANT "DEMANDER À ABRA" - Mobile uniquement
                Contextuel à la page affichée (Emails, Agenda, Notes, etc.)
            ══════════════════════════════════════════════════════════════════════ */}
            {isMobile && activeArtifact && (
              <button
                onClick={() => {
                  // Obtenir le contexte selon la page affichée
                  const contextMap = {
                    'MailsParticulierPage': { context: 'emails', label: 'Mes Emails', message: 'Tu es sur ta boîte mail. Comment puis-je t\'aider avec tes emails ?' },
                    'AgendaParticulierPage': { context: 'agenda_perso', label: 'Mon Agenda', message: 'Tu es sur ton agenda. Comment puis-je t\'aider à organiser ton planning ?' },
                    'MesNotesPage': { context: 'notes', label: 'Mes Notes', message: 'Tu es sur tes notes. Comment puis-je t\'aider à structurer tes idées ?' },
                    'NotesPage': { context: 'notes', label: 'Mes Notes', message: 'Tu es sur tes notes. Comment puis-je t\'aider à structurer tes idées ?' },
                    'BoiteAIdeesPage': { context: 'boite_idees', label: 'Boîte à idées', message: 'Tu es sur ta boîte à idées. Comment puis-je t\'aider à développer tes idées ?' },
                    'DocumentsParticulierPage': { context: 'documents', label: 'Mes Documents', message: 'Tu es sur tes documents. Comment puis-je t\'aider avec tes fichiers ?' },
                    'VisioconferencePage': { context: 'visio', label: 'Visioconférence', message: 'Tu es sur la visioconférence. Comment puis-je t\'aider à organiser tes réunions ?' },
                    'MessagerieParticulierPage': { context: 'messagerie', label: 'Mes Messages', message: 'Tu es sur ta messagerie. Comment puis-je t\'aider à rédiger tes messages ?' },
                    'TachesKanbanPage': { context: 'taches_perso', label: 'Liste de tâches', message: 'Tu es sur ta liste de tâches. Comment puis-je t\'aider à organiser ton travail ?' },
                  };
                  const pageConfig = contextMap[activeArtifact?.componentName] || { 
                    context: activeContext || 'general', 
                    label: activeContextLabel || 'Assistant', 
                    message: 'Comment puis-je t\'aider ?' 
                  };
                  handleAskAbra(pageConfig.context, pageConfig.label, pageConfig.message);
                }}
                className="fixed z-50 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-white active:scale-95 transition-all"
                style={{ 
                  bottom: '80px', // Au-dessus du navbar
                  right: '16px',
                  background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)',
                  boxShadow: '0 4px 14px rgba(232, 149, 92, 0.35)',
                  border: '1px solid rgba(255, 255, 255, 0.12)'
                }}
                data-testid="floating-ask-abra-btn"
              >
                <Sparkles size={14} className="text-white" />
                <span className="text-[12px] font-semibold">Demander à Abra</span>
              </button>
            )}
            
            {/* ══════════════════════════════════════════════════════════════════════
                NAVBAR DU BAS MOBILE - Gérée globalement par MobileNavWrapper
                La navbar standard (Actu, Boutiques, Cockpit, Acheter, Plus) est affichée via MobileNavWrapper dans App.js
            ══════════════════════════════════════════════════════════════════════ */}
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Modal Notes Vocales */}
      <AnimatePresence>
        {showVoiceNotes && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowVoiceNotes(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <VoiceNotesSuperpouvoir 
                userId={userId}
                onClose={() => setShowVoiceNotes(false)}
                onTranscription={(text) => {
                  setInputValue(text);
                  setShowVoiceNotes(false);
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal "À venir" pour Réseau Pro et Centrale d'achat */}
      <AnimatePresence>
        {comingSoonModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 md:left-[288px] bg-black/60 z-[100] flex items-center justify-center p-4"
            onClick={() => setComingSoonModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header avec icône */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] flex items-center justify-center shadow-lg">
                  {comingSoonModal.icon && <comingSoonModal.icon className="w-7 h-7 text-white" />}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{comingSoonModal.label}</h3>
                  <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                    En cours de développement
                  </span>
                </div>
              </div>
              
              {/* Description */}
              <p className="text-gray-700 font-medium mb-2">
                Arrive bientôt !
              </p>
              <p className="text-gray-500 text-sm mb-6">
                {comingSoonModal.comingSoonDescription}
              </p>
              
              {/* CTA */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-gray-600 text-center">
                  Tu veux être informé du lancement ?
                </p>
                <p className="text-xs text-gray-400 text-center mt-1">
                  En tant que membre, tu seras notifié en priorité.
                </p>
              </div>
              
              {/* Bouton fermer */}
              <button
                onClick={() => setComingSoonModal(null)}
                className="w-full py-3 bg-[#1E3A5F] text-white rounded-xl font-semibold hover:bg-[#2d5a8a] transition-colors"
                data-testid="coming-soon-close-btn-unified"
              >
                J'ai compris
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════════════════════════════════════════
          BOUTON FLOTTANT "DEMANDE À ABRACADA" - Desktop uniquement en mode pleine page
          Visible quand un outil est affiché en pleine page sur Desktop
      ══════════════════════════════════════════════════════════════════════ */}
      {!isMobile && isDesktopToolFullScreen && !showFloatingChat && (
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowFloatingChat(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full shadow-2xl text-white font-semibold transition-all"
          style={{
            background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
            boxShadow: '0 8px 32px rgba(249, 115, 22, 0.4), 0 4px 12px rgba(0,0,0,0.15)'
          }}
          data-testid="floating-chat-button"
        >
          <Sparkles size={20} />
          <span>Demande à Abracada</span>
        </motion.button>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PANNEAU CHAT COULISSANT PREMIUM - Desktop uniquement en mode pleine page
          Design contextuel avec Quick Actions et message d'accueil personnalisé
      ══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {!isMobile && isDesktopToolFullScreen && showFloatingChat && (() => {
          // Récupérer le contexte adapté à la page active
          const contextKey = resolveContextKey(activeContext);
          const assistantContext = getAssistantContext(contextKey, isPro);
          
          return (
          <motion.div
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className={`fixed top-0 right-0 h-full w-1/3 min-w-[360px] max-w-[600px] z-50 flex flex-col shadow-2xl border-l ${isDarkMode ? 'bg-[#faf9f7] border-gray-200' : 'bg-[#faf9f7] border-gray-200'}`}
            style={{ boxShadow: '-8px 0 32px rgba(0,0,0,0.08)' }}
            data-testid="floating-chat-panel"
          >
            {/* ═══ HEADER PREMIUM ═══ */}
            <div className="px-5 py-4 border-b border-gray-100 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Icône magique */}
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center shadow-lg shadow-orange-200">
                    <Sparkles size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1E3A5F] text-base">{assistantContext.title}</h3>
                    <p className="text-xs text-gray-500">{assistantContext.subtitle}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowFloatingChat(false)}
                  className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
                >
                  <X size={18} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* ═══ ZONE DE MESSAGES + INTRO ═══ */}
            <div className="flex-1 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="p-5 space-y-5">
                  {/* Bloc d'introduction contextuel */}
                  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      <ReactMarkdown>{assistantContext.intro}</ReactMarkdown>
                    </p>
                  </div>
                  
                  {/* Quick Actions - Suggestions rapides */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider px-1">
                      Actions rapides
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {assistantContext.quickActions.map((action) => (
                        <button
                          key={action.id}
                          onClick={() => {
                            setInputValue(action.label);
                            // Optionnel : envoyer directement
                            // handleSendMessage();
                          }}
                          className="group flex items-center gap-2 px-3 py-2 bg-white rounded-full border border-gray-200 hover:border-[#F97316] hover:bg-orange-50 transition-all duration-200 shadow-sm"
                        >
                          <span className="text-sm text-[#1E3A5F] group-hover:text-[#F97316] font-medium">
                            {action.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Message rassurant */}
                  <p className="text-xs text-gray-400 text-center pt-2">
                    Tu peux aussi écrire librement comme dans ChatGPT
                  </p>
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'system' ? (
                        <div className="w-full px-4 py-3 rounded-xl bg-white border border-gray-100 text-sm text-gray-600 shadow-sm">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                          {/* Quick Actions après message système */}
                          {idx === 0 && (
                            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                              {assistantContext.quickActions.slice(0, 3).map((action) => (
                                <button
                                  key={action.id}
                                  onClick={() => setInputValue(action.label)}
                                  className="text-xs px-2.5 py-1.5 rounded-full bg-gray-50 hover:bg-orange-50 text-gray-600 hover:text-[#F97316] border border-gray-200 hover:border-[#F97316] transition-all"
                                >
                                  {action.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : msg.role === 'user' ? (
                        <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tr-md bg-[#1E3A5F] text-white text-sm shadow-sm">
                          {msg.content}
                        </div>
                      ) : (
                        <div className="max-w-[85%] flex gap-2.5">
                          <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-[#F97316] to-[#EA580C] shadow-sm">
                            <Sparkles size={14} className="text-white" />
                          </div>
                          <div className="px-4 py-2.5 rounded-2xl rounded-tl-md text-sm bg-white border border-gray-100 text-gray-700 shadow-sm">
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="flex gap-2.5">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#F97316] to-[#EA580C] shadow-sm">
                          <Loader2 size={14} className="text-white animate-spin" />
                        </div>
                        <div className="px-4 py-3 rounded-2xl rounded-tl-md bg-white border border-gray-100 shadow-sm">
                          <div className="flex gap-1.5">
                            <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 bg-[#F97316] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Zone de saisie - Même barre ChatGPT que le chat principal */}
            <div className={`p-3 border-t ${isDarkMode ? 'border-[#334155] bg-[#1e293b]' : 'border-gray-100 bg-white'}`}>
              <div 
                className="flex items-center rounded-full"
                style={{ 
                  height: '52px',
                  paddingLeft: '16px',
                  paddingRight: '8px',
                  backgroundColor: isDarkMode ? '#2d3748' : '#ececec',
                  border: '2px solid #F97316'
                }}
              >
                {/* Bouton + */}
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setShowAttachMenu(!showAttachMenu)}
                    disabled={isUploading}
                    className={`flex items-center justify-center ${
                      isUploading ? 'opacity-50' : ''
                    } ${isDarkMode ? 'text-gray-300' : 'text-[#676767]'}`}
                  >
                    {isUploading ? (
                      <Loader2 size={24} className="animate-spin" />
                    ) : (
                      <Plus size={24} strokeWidth={2} />
                    )}
                  </button>
                  
                  {/* Menu dropdown pour les attachments */}
                  <AnimatePresence>
                    {showAttachMenu && (
                      <>
                        <div 
                          className="fixed inset-0 z-40" 
                          onClick={() => setShowAttachMenu(false)}
                        />
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          className="absolute bottom-full left-0 mb-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
                        >
                          <div className="p-2">
                            <div className="flex items-center justify-between px-3 py-1.5">
                              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Importer</p>
                              <button
                                onClick={() => setShowAttachMenu(false)}
                                className="w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
                              >
                                <X size={14} className="text-gray-400" />
                              </button>
                            </div>
                            <button
                              onClick={() => {
                                fileInputRef.current?.setAttribute('accept', 'image/*');
                                fileInputRef.current?.click();
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                <ImageIcon size={16} className="text-blue-600" />
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-medium text-gray-800">Photos</p>
                                <p className="text-xs text-gray-500">Images, captures</p>
                              </div>
                            </button>
                            <button
                              onClick={() => {
                                fileInputRef.current?.setAttribute('accept', '.pdf,.doc,.docx,.xls,.xlsx');
                                fileInputRef.current?.click();
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                                <FileText size={16} className="text-red-600" />
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-medium text-gray-800">Documents</p>
                                <p className="text-xs text-gray-500">PDF, Word, Excel</p>
                              </div>
                            </button>
                            <button
                              onClick={() => {
                                fileInputRef.current?.setAttribute('accept', 'video/*');
                                fileInputRef.current?.click();
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                <Film size={16} className="text-purple-600" />
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-medium text-gray-800">Vidéos</p>
                                <p className="text-xs text-gray-500">MP4, MOV</p>
                              </div>
                            </button>
                            <button
                              onClick={() => {
                                fileInputRef.current?.setAttribute('accept', '*/*');
                                fileInputRef.current?.click();
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                                <Paperclip size={16} className="text-gray-600" />
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-medium text-gray-800">Autres fichiers</p>
                                <p className="text-xs text-gray-500">Tous types</p>
                              </div>
                            </button>
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
                
                {/* Input texte avec placeholder contextuel */}
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  placeholder={assistantContext.placeholder}
                  style={{
                    flex: 1,
                    marginLeft: '4px',
                    marginRight: '8px',
                    fontSize: '16px',
                    fontWeight: '400',
                    color: isDarkMode ? '#fff' : '#1a1a1a',
                    caretColor: isDarkMode ? '#fff' : '#1a1a1a',
                    backgroundColor: 'transparent',
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none'
                  }}
                  disabled={isLoading || isUploading}
                />
                
                {/* Micro */}
                <button
                  onClick={() => setShowVoiceNotes(true)}
                  className="flex-shrink-0"
                  style={{ marginLeft: '8px' }}
                >
                  <Mic size={24} strokeWidth={1.5} className="text-[#9a9a9a]" />
                </button>
                
                {/* Bouton Envoyer orange */}
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className="flex-shrink-0 rounded-full flex items-center justify-center bg-[#E8650A] hover:bg-[#d55a09] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ width: '40px', height: '40px' }}
                >
                  <ArrowUp size={20} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Message flottant style Bitrix24 - Desktop uniquement */}
      <FloatingHelper variant={isPro ? 'pro' : 'particulier'} />
      
      {/* Modal d'inscription contextuel - rendu au niveau parent pour éviter les problèmes de z-index */}
      <RegisterPromptModal 
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        actionLabel={registerModalAction}
        showGoogleOption={registerModalShowGoogle}
      />
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          MODALES NAVIGATION HORIZONTALE
          Rayons (Marketplace), Catégories (Particuliers), Filtres
      ═══════════════════════════════════════════════════════════════════════════ */}
      
      {/* Modal Rayons (Acheter à un pro) */}
      <MarketplaceCatalogueMenu
        isOpen={showRayonsModal}
        onClose={() => setShowRayonsModal(false)}
        sidebarWidth={sidebarCollapsed ? 70 : 300}
      />

      {/* Modal Catégories (Entre particuliers) */}
      <ParticuliersCatalogueMenu
        isOpen={showCategoriesModal}
        onClose={() => setShowCategoriesModal(false)}
        sidebarWidth={sidebarCollapsed ? 70 : 300}
      />

      {/* Filtres Artisans "Trouver" (avec métiers: Jardinier, Plombier, Électricien...) */}
      <UnifiedFilterSystem
        isOpen={showArtisansFiltersModal}
        onClose={() => setShowArtisansFiltersModal(false)}
        context="artisans"
        filters={artisansFilters}
        onFiltersChange={setArtisansFilters}
        sidebarWidth={sidebarCollapsed ? 70 : 300}
        onApply={(filters) => {
          console.log('Filtres artisans appliqués:', filters);
          setShowArtisansFiltersModal(false);
        }}
      />

      {/* Filtres "Trouve en 3 clics" (Acheter à un pro - Produits boutiques Cockpit Pro) */}
      <UnifiedFilterSystem
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        context="produits_pro"
        filters={marketplaceFilters}
        onFiltersChange={setMarketplaceFilters}
        rayons={rayons}
        sousRayons={sousRayons}
        marques={[]}
        sidebarWidth={sidebarCollapsed ? 70 : 300}
        onApply={(filters) => {
          console.log('Filtres produits pro appliqués:', filters);
          setShowFiltersModal(false);
        }}
      />

      {/* Filtres "Trouve ton annonce en 3 clics" (Entre particuliers) */}
      <UnifiedFilterSystem
        isOpen={showParticuliersFiltersModal}
        onClose={() => setShowParticuliersFiltersModal(false)}
        context="marketplace"
        filters={particuliersFilters}
        onFiltersChange={setParticuliersFilters}
        rayons={rayons}
        sousRayons={sousRayons}
        marques={[]}
        sidebarWidth={sidebarCollapsed ? 70 : 300}
        onApply={(filters) => {
          console.log('Filtres particuliers appliqués:', filters);
          setShowParticuliersFiltersModal(false);
        }}
      />

      {/* Filtres "Trouve ton emploi en 3 clics" (Emploi) */}
      <UnifiedFilterSystem
        isOpen={showEmploiFiltersModal}
        onClose={() => setShowEmploiFiltersModal(false)}
        context="emploi"
        filters={emploiFilters}
        onFiltersChange={setEmploiFilters}
        sidebarWidth={sidebarCollapsed ? 70 : 300}
        onApply={(filters) => {
          console.log('Filtres emploi appliqués:', filters);
          setShowEmploiFiltersModal(false);
        }}
      />
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          NAVBAR FIXE EN BAS - COCKPIT MOBILE (toujours visible)
          Espace | Marketplace | Réseau | Service | Emploi
          Navigation DIRECTE : change l'état activeContext + réinitialise les artifacts
          z-index 9999 pour être AU-DESSUS de toutes les sous-pages fullscreen
      ═══════════════════════════════════════════════════════════════════════════ */}
      {isMobile && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-[9990] border-t border-[#2A4A6F]" 
          style={{ background: '#1E3A5F' }}
        >
          <div className="grid grid-cols-5 items-center justify-items-center" style={{ height: '60px' }}>
            {/* Espace - Dashboard principal */}
            <button 
              onClick={() => {
                // Reset complet pour afficher le hub
                setActiveArtifact(null);
                setArtifacts([]);
                setMessages([]);
                setShowArtifacts(false);
                setIsDesktopToolFullScreen(false);
                setActiveContext('hub_espace');
                setActiveContextLabel('Mon Espace');
                setActiveEspaceLabel('Mon Espace');
                setShowSidebar(false);
                navigate('/cockpit-particulier');
              }}
              className="flex flex-col items-center justify-center gap-[2px]" 
              data-testid="fixed-nav-espace"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeContext === 'hub_espace' ? '#F97316' : 'rgba(255,255,255,0.75)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              <span style={{ color: activeContext === 'hub_espace' ? '#F97316' : 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: activeContext === 'hub_espace' ? 600 : 500 }}>Espace</span>
            </button>
            {/* Marketplace - Achats Pros */}
            <button 
              onClick={() => {
                // Reset complet pour afficher le hub
                setActiveArtifact(null);
                setArtifacts([]);
                setMessages([]);
                setShowArtifacts(false);
                setIsDesktopToolFullScreen(false);
                setActiveContext('hub_marketplace');
                setActiveContextLabel('Marketplace');
                setActiveEspaceLabel('Marketplace');
                setShowSidebar(false);
                navigate('/cockpit-particulier/marketplace');
              }}
              className="flex flex-col items-center justify-center gap-[2px]" 
              data-testid="fixed-nav-marketplace"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeContext === 'hub_marketplace' ? '#F97316' : 'rgba(255,255,255,0.75)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 0 1-8 0"/>
              </svg>
              <span style={{ color: activeContext === 'hub_marketplace' ? '#F97316' : 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: activeContext === 'hub_marketplace' ? 600 : 500 }}>Marketplace</span>
            </button>
            {/* Réseau - Réseau Social */}
            <button 
              onClick={() => {
                // Reset complet pour afficher le hub
                setActiveArtifact(null);
                setArtifacts([]);
                setMessages([]);
                setShowArtifacts(false);
                setIsDesktopToolFullScreen(false);
                setActiveContext('hub_reseau');
                setActiveContextLabel('Réseau Social');
                setActiveEspaceLabel('Réseau Social');
                setShowSidebar(false);
                navigate('/cockpit-particulier/reseau');
              }}
              className="flex flex-col items-center justify-center gap-[2px]" 
              data-testid="fixed-nav-reseau"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeContext === 'hub_reseau' ? '#F97316' : 'rgba(255,255,255,0.75)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span style={{ color: activeContext === 'hub_reseau' ? '#F97316' : 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: activeContext === 'hub_reseau' ? 600 : 500 }}>Réseau</span>
            </button>
            {/* Service - Artisans/Prestataires */}
            <button 
              onClick={() => {
                // Reset complet pour afficher le hub
                setActiveArtifact(null);
                setArtifacts([]);
                setMessages([]);
                setShowArtifacts(false);
                setIsDesktopToolFullScreen(false);
                setActiveContext('hub_prestataires');
                setActiveContextLabel('Prestataires');
                setActiveEspaceLabel('Prestataires');
                setShowSidebar(false);
                navigate('/cockpit-particulier/artisans');
              }}
              className="flex flex-col items-center justify-center gap-[2px]" 
              data-testid="fixed-nav-service"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeContext === 'hub_prestataires' ? '#F97316' : 'rgba(255,255,255,0.75)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
              <span style={{ color: activeContext === 'hub_prestataires' ? '#F97316' : 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: activeContext === 'hub_prestataires' ? 600 : 500 }}>Service</span>
            </button>
            {/* Emploi */}
            <button 
              onClick={() => {
                // Reset complet pour afficher le hub
                setActiveArtifact(null);
                setArtifacts([]);
                setMessages([]);
                setShowArtifacts(false);
                setIsDesktopToolFullScreen(false);
                setActiveContext('hub_emploi');
                setActiveContextLabel('Emploi');
                setActiveEspaceLabel('Emploi');
                setShowSidebar(false);
                navigate('/cockpit-particulier/emploi');
              }}
              className="flex flex-col items-center justify-center gap-[2px]" 
              data-testid="fixed-nav-emploi"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={activeContext === 'hub_emploi' ? '#F97316' : 'rgba(255,255,255,0.75)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              </svg>
              <span style={{ color: activeContext === 'hub_emploi' ? '#F97316' : 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: activeContext === 'hub_emploi' ? 600 : 500 }}>Emploi</span>
            </button>
          </div>
          {/* Safe area padding pour iPhone */}
          <div style={{ height: 'env(safe-area-inset-bottom, 0px)', background: '#1E3A5F' }} />
        </div>
      )}
    </div>
  );
}
